import type { Translations } from './es';

export const zh: Translations = {
  gatePage: {
    title: '门岗',
    subtitle: '核对班组点名名单，并登记车辆通行。',
    people: '人员',
    vehicles: '车辆',
    codeLabel: '通行码（扫码枪或手输）',
    codeHint: '使用USB或蓝牙扫码枪时，将光标放在此处并扫描二维码。',
    search: '查询',
    camera: '使用摄像头',
    cameraOff: '关闭摄像头',
    cameraUnsupported: '无法打开摄像头。请检查浏览器权限，或使用扫码枪或输入代码。',
    invalidCode: '代码无效，或名单已过期（24小时）。',
    group: '班组',
    closedAt: '名单关闭时间',
    present: '名单中到场人数',
    countedLabel: '您清点的人数',
    entry: '进入',
    exit: '离开',
    confirm: '确认人数',
    match: '一致：人数正确。',
    mismatch: (exp: number, got: number) => `不一致：名单为${exp}人，您清点为${got}人。已记为异常。`,
    plate: '车牌',
    driver: '司机',
    company: '公司',
    occupants: '乘员',
    note: '备注',
    register: '登记通行',
    allowed: '授权车辆。',
    visitor: '车辆未登记：已记为访客。',
    authorizeTitle: '登记授权车辆',
    validUntil: '有效期至',
    save: '保存车辆',
    saved: '车辆已保存。',
    logTitle: '今日记录',
    colTime: '时间',
    colType: '类型',
    colDetail: '详情',
    colResult: '结果',
    emptyLog: '今天没有记录。',
    faceTitle: '人脸识别',
    inactiveInList: '名单中有 {n} 人已离职。请通知人事。',
    status: { ACTIVE: '在职', PRE_TERMINATION: '待离职', INACTIVE: '已离职', EXTRA: '额外' },
    cameraTitle: '摄像头',
    cameraHint: '打开一次：自动读取二维码或条形码；识别人脸请点击“识别”。',
    openCameraFirst: '请先打开摄像头以识别人脸。',
    faceHint: '打开摄像头拍照并确认身份。照片不会保存。',
    faceCamera: '打开人脸摄像头',
    identify: '识别',
    faceError: '无法识别。请靠近摄像头后重试。',
    letIn: '登记进入',
    letOut: '登记离开',
    pickCandidate: '无法确定身份，请选择此人：',
    terminatedPending: (n: string) => `${n} 已离职：已提交申请，需有权限的人授权。`,
    terminatedDenied: (n: string) => `${n} 已离职：已拒绝并记录。`,
    authReason: '授权原因',
    logAsVisitor: '登记为访客',
    authorizePass: '授权放行',
    requestAuth: '申请授权',
    logDenied: '拒绝并记录',
    resolved: '已处理',
    authorizedOk: '已授权放行。',
    saveRules: '保存规则',
    rulesTitle: '离职人员规则',
    rulesHint: '按顺序检查，第一条匹配的规则生效。严格：不放行。访客：登记为访客。需授权：等待有权限的人。',
    ruleRole: '角色',
    ruleDirection: '进出',
    ruleAction: '动作',
    anyRole: '任意角色',
    anyDirection: '进出皆可',
    moveUp: '上移',
    moveDown: '下移',
    removeRule: '删除',
    addRule: '添加规则',
    otherwise: '都不匹配时',
    rulesSaved: '规则已保存。',
    policyLabels: { STRICT: '严格', VISITOR: '访客', AUTHORIZE: '需授权' },
    terminated: '{name} 已离职。请勿放行并通知人事。',
    notActive: '非在职',
    visitorName: '访客姓名',
    logVisitor: '登记访客',
    tooSmall: '人脸太小：请靠近摄像头。',
    personLogged: (n: string) => `已登记：${n}。`,
    visitorLogged: (n: string) => `已登记访客：${n}。`,
    typePerson: '人员',
    invalidVehicle: '请检查数据：车牌为4至10位字母或数字，乘员最多200人。',
    typeList: '考勤名单',
    typeVehicle: '车辆',
    results: { MATCH: '一致', MISMATCH: '不一致', ALLOWED: '授权', VISITOR: '访客', DENIED: '已拒绝', PENDING: '待授权', AUTHORIZED: '已授权' },
  },
  attendancePage: {
    personnelTitle: '人员',
    attendanceTitle: '考勤',
    subtitlePersonnel: '人员名册、人脸登记、预离职以及生物特征数据删除。',
    subtitleAttendance: '按班组查看每日点名：出勤、缺勤及每条记录的照片。',
    addPerson: '添加人员',
    edit: '编辑',
    editTitle: '编辑人员',
    save: '保存',
    numberShort: '工号',
    fullName: '全名',
    employeeNumber: '员工编号（可选）',
    colCrew: '班组',
    noCrew: '无班组',
    colUser: '系统用户',
    noUser: '无用户',
    crewHint: '班组',
    newCrewName: '新班组（名称）',
    createCrew: '创建班组',
    deleteCrewConfirm: (n: string) => `删除班组“${n}”？以往名单仍保留其名称。`,
    add: '添加',
    importTitle: '从 Excel 导入',
    importHint: '第一个工作表需有姓名列（Nombre、Empleado 或 Trabajador）。可选：编号、班组。',
    chooseFile: '选择 .xlsx 文件',
    preview: '预览',
    confirmImport: '确认导入',
    toCreate: '待创建',
    alreadyExist: '已存在',
    importDone: '已导入人数',
    errorsFound: '请修正以下错误后重试',
    rowLabel: (n: number) => `第 ${n} 行`,
    methods: { AUTO: '自动', CONFIRMED: '已确认', MANUAL: '手动', EXTRA: '额外人员' },
    errors: {
      FEATURE_DISABLED: '您的公司未启用人脸考勤。',
      FORBIDDEN: '您没有执行此操作的权限。',
      NOT_FOUND: '未找到，可能已不存在。',
      DUPLICATE_NUMBER: '该工号已存在，请使用其他工号。',
      REASON_REQUIRED: '请填写授权原因。',
      HAS_ATTENDANCE: '无法删除：已有考勤记录。请改为办理离职。',
      INVALID_INPUT: '请检查数据后重试。',
      INVALID_BODY: '请求无效。',
      INVALID_DECISION: '决定无效。',
      INVALID_FILE: '文件无效。',
      FILE_TOO_LARGE: '文件太大。',
      INVALID_RANGE: '日期范围无效。',
      RANGE_TOO_LARGE: '日期范围过大（最多93天）。',
      CONSENT_REQUIRED: '需要先登记此人签署的同意书。',
      SESSION_CLOSED: '名单已关闭。',
      MISSING_UNDECIDED: '关闭前需要先处理部分人员的情况。',
      NO_FACE: '看不到人脸。请靠近并检查光线。',
      MULTIPLE_FACES: '出现了多人。',
      FACE_TOO_SMALL: '人脸太小，请靠近。',
      EYES_NOT_VISIBLE: '必须能看到双眼。',
      LOW_QUALITY: '照片质量不足，请重拍。',
      TOO_MANY_FACES: '照片中的人数超过允许上限。',
      GATE_CODE_INVALID: '代码无效，或名单已过期（24小时）。',
      USER_ALREADY_LINKED: '该用户已绑定到其他人员。',
      CREW_HAS_PEOPLE: '该班组仍有人员，请先重新分配。',
    },
    genericError: '操作未能完成，请重试。',
    listLabel: '名单',
    pickList: (n: number) => `选择名单（${n}）`,
    filterSearch: '按姓名或编号搜索',
    allCrews: '全部班组',
    allStatuses: '全部状态',
    allEnrollment: '全部录入情况',
    filterPending: '录入未完成',
    clearFilters: '清除筛选',
    noMatches: '没有符合筛选条件的人员。',
    validating: '检查中…',
    importing: '导入中…',
    colRow: '行',
    template: '下载模板',
    warnings: '提示',
    colName: '姓名',
    colStatus: '状态',
    colEnrollment: '登记',
    colMisses: '缺勤次数',
    colActions: '操作',
    statusACTIVE: '在职',
    statusPRE_TERMINATION: '预离职',
    statusINACTIVE: '已离职',
    enrolled: '已登记',
    noConsent: '未同意',
    photosOf: '张照片',
    keep: '保留',
    terminate: '离职',
    deleteBiometrics: '删除生物特征数据',
    deleteTitle: '删除生物特征数据：',
    deleteBody: '将删除此人已登记的人脸、登记照片和出勤照片。多人合照中仅遮挡其面部。其姓名及出勤日期和时间将保留。此操作无法撤销。',
    typeName: '输入全名以确认',
    reason: '原因（必填）',
    cancel: '取消',
    deletePerson: '删除人员',
    deletePersonTitle: '删除',
    deletePersonBody: '将删除此人的记录、人脸和注册照片。仅当其从未有考勤记录时才可删除；否则请办理离职。此操作无法撤销。',
    confirmDelete: '删除生物特征数据',
    receiptTitle: '删除回执',
    receiptFaces: '已删除人脸',
    receiptEnroll: '已删除登记照片',
    receiptAttendance: '已删除出勤照片',
    receiptId: '编号',
    close: '关闭',
    loading: '加载中…',
    empty: '暂无人员。',
    disabled: '您的公司尚未启用人脸考勤。请联系超级管理员启用。',
    date: '日期',
    lote: '地块',
    sessionStatus: '状态',
    open: '进行中',
    closed: '已结束',
    present: '出勤',
    missing: '缺勤',
    extras: '额外人员',
    noSessions: '该日期没有点名记录。',
    viewStamped: '查看带水印照片',
    viewEnrollPhotos: '查看登记照片',
    enrollPhotosTitle: '登记照片：',
    noEnrollPhotos: '没有登记照片。',
    sessionPhotos: '本次点名照片',
    allStaff: '全体人员',
    destination: '去向',
    sessionNotes: '备注',
    noSessionPhotos: '本次点名暂无照片。',
    tabDay: "每日点名",
    tabReport: "报表",
    from: "从",
    to: "至",
    allLotes: "全部地块",
    download: "下载 Excel",
    byPerson: "按人员",
    byDay: "按日期",
    totalSessions: "点名次数",
    colPresent: "出勤",
    colAbsent: "缺勤",
    colJustified: "已说明",
    colUndecided: "未决定",
    colPct: "出勤率",
    colLate: "迟到",
    colHours: "工时",
    colExpected: "应到",
    colExtras: "额外",
    colDate: "日期",
    colLote: "地块",
    reportHint: "出勤率 = 出勤天数 /（出勤 + 缺勤）。已说明的天数不计入。",
    reportEmpty: "该期间没有点名记录。",
    rangeError: "请检查日期：最多 93 天，且起始日期不能晚于结束日期。",
    extrasTotal: "额外人员",
    undecided: '未决定',
    absent: '缺席',
    justified: '已说明',
    back: '返回',
  },
  evidencePage: {
    open: '打开',
    download: '下载',
    folders: '文件夹',
    empty: '暂无证据照片。',
  },
  invalidRequest: '请求无效',
  shipmentEvidence: {
    title: '货运证据',
    createTitle: '创建链接',
    label: '链接名称',
    days: '有效天数（1 至 30）',
    stages: '包含的阶段（留空 = 全部）',
    stageNames: { LOADING: '装货', SEALING: '封存', DEPARTURE: '发车', CHECKPOINT: '途经', INSPECTION: '检验', DELIVERY: '交付', INCIDENT: '事件' },
    create: '创建链接',
    created: '链接已创建。请立即复制：令牌不会再次显示。',
    copy: '复制链接',
    shareTitle: '已共享链接',
    none: '此货运没有链接。',
    active: '有效',
    revoked: '已撤销',
    expired: '已过期',
    revoke: '撤销',
    expiresOn: '到期日',
    loading: '加载中…',
    public: '共享的证据',
    invalid: '链接无效或已过期。',
    noEvidence: '没有证据。',
    stageLabel: '阶段',
    takenAt: '拍摄于',
    open: '打开',
  },
  budgetNotice: {
    title: '预算提醒',
  },
  footer: {
    socialTitle: "社交媒体",
    networks: {
      facebook: "Facebook",
      instagram: "Instagram",
      linkedin: "LinkedIn",
      youtube: "YouTube",
      x: "X",
    },
    openBot: "打开 Agrovid 机器人",
    openQr: "在手机上打开",
    qrTitle: "在手机上打开 Agrovid",
    qrHint: "用手机相机扫描此代码。",
    qrClose: "关闭",
    legalNav: "法律信息",
    usagePolicy: "使用政策",
    terms: "条款与条件",
    privacy: "隐私声明",
    rights: (year: number) => `© ${year} Agrovid。保留所有权利。`,
  },
  legal: {
    contactLabel: "联系方式",
    back: "← 返回首页",
    updated: "最后更新：2026 年 10 月",
    draftNotice: "草稿，待法律审核。本文仅供参考，不能替代法律意见，在审核确认前不应视为最终版本。",
    docs: {
      usage: {
        title: "使用政策",
        sections: [
          {
            heading: "1. 允许的使用",
            body: "本平台用于管理贵公司的农业运营：田间记录、库存、财务和报表。仅限公司管理员授权的人员访问。",
          },
          {
            heading: "2. 禁止行为",
            body: "不得共享账号凭证、试图访问其他公司的数据、故意录入虚假信息、批量提取数据或干扰服务运行。",
          },
          {
            heading: "3. 数据责任",
            body: "各公司对其录入信息的真实性负责，包括农药施用、收获和证据资料。",
          },
          {
            heading: "4. 暂停",
            body: "对违反本政策或危及服务及其他用户安全的账号，我们可以予以暂停。",
          },
        ],
      },
      copyright: {
        title: "版权声明",
        sections: [
          {
            heading: "1. 权属",
            body: "Agrovid 的软件、设计、商标、标识和文字归其权利人所有，受适用的著作权和工业产权法律保护。",
          },
          {
            heading: "2. 许可",
            body: "每家公司在合同有效期内获得有限、非排他、不可转让的平台使用许可。不得复制、修改、反编译或再分发软件。",
          },
          {
            heading: "3. 公司数据",
            body: "各公司录入的数据仍归其所有。Agrovid 仅为提供服务而处理这些数据。",
          },
          {
            heading: "4. 侵权投诉",
            body: "如认为有内容侵犯版权，请发送邮件至联系邮箱，说明相关内容及您的权利。",
          },
        ],
      },
      terms: {
        title: "条款与条件",
        sections: [
          {
            heading: "1. 接受",
            body: "访问或使用 Agrovid 即表示您接受本条款。如代表公司使用平台，您声明有权约束该公司。",
          },
          {
            heading: "2. 服务与可用性",
            body: "网页平台和 Telegram 机器人按现状提供；我们力求高可用，但不保证服务不中断或无错误。",
          },
          {
            heading: "3. 账号与安全",
            body: "您须对访问凭证保密并对账号下的活动负责。如怀疑被未经授权使用，请立即告知我们。",
          },
          {
            heading: "4. 责任限制",
            body: "农艺建议和计算仅为决策辅助；最终决定及合规责任由用户承担。在法律允许范围内，我们不对间接损失负责。",
          },
          {
            heading: "5. 变更与适用法律",
            body: "我们可能更新本条款并在平台上通知。本条款适用墨西哥合众国法律。",
          },
        ],
      },
      privacy: {
        title: "隐私声明",
        sections: [
          {
            heading: "1. 责任方",
            body: "Agrovid 依据墨西哥《私人持有个人数据保护联邦法》对您的个人数据处理负责。联系方式：本页末尾所示的联系邮箱。",
          },
          {
            heading: "2. 处理的数据",
            body: "姓名、电话、邮箱、Telegram 标识、角色和平台活动，以及您录入的运营信息（地块、施用、库存、财务）和作为证据上传的照片或位置。",
          },
          {
            heading: "3. 目的",
            body: "提供服务、验证身份、保持可追溯性和合规、发送运营通知并改进平台。我们不出售您的个人数据。",
          },
          {
            heading: "4. 转移与服务商",
            body: "我们使用云基础设施和人工智能服务商来运行服务，并采取保密和安全措施。",
          },
          {
            heading: "5. ARCO 权利",
            body: "您可通过联系邮箱行使访问、更正、取消或反对处理数据的权利。我们将在法定期限内答复。",
          },
          {
            heading: "6. 变更",
            body: "对本声明的任何修改将发布在本页面。",
          },
        ],
      },
    },
  },
  nav: {
    myDay: "我的一天",
    groups: {
      campo: "田间",
      cosechaVenta: "收获与销售",
      recursos: "资源",
      finanzas: "财务",
      cumplimiento: "合规与报表",
      admin: "管理",
    },
    sections: {
      campoOperacion: '田间运营',
      finanzasInversion: '财务与投资',
      comercialLogistica: '商业与物流',
      riesgoCumplimiento: '风险与合规',
      admin: '系统管理',
    },
    dashboard:  '首页',
    investorOverview: '概况',
    investorModules: '区域',
    investorYield: '产量',
    investorLogistics: '物流',
    investorFunding: '投资与资金',
    investorProduction: '生产',
    tasks:      '任务',
    finance:    '财务',
    fieldLots:  '田地与地块',
    irrigation: '灌溉',
    harvest:    '收获',
    inventory:  '库存',
    inventories: '批量上传',
    machinery:  '机械',
    spray:      '施药',
    porcino:    '猪场',
    payroll:    '工资单',
    reports:    '报告',
    map:        'GIS地图',
    chat:       'AI对话',
    users:      '用户',
    settings:   '设置',
    config:     '设置',
    gettingStarted: '入门指南',
    companies:  '企业',
    ranchos:    '牧场',
    system:     '系统',
    bulkImport: '批量导入',
    merma:       '损耗',
    crop:        '作物管理',
    // Sprint B
    export:      '出口',
    transport:   '运输',
    traceability:'溯源',
    fsmaExport:  'FSMA 204 交付',
    protocol:    '投入品方案',
    permisos:    '权限',
    vocabulary:  '词汇表',
    drones:      '无人机',
    devices:     '设备',
    sales:       '销售',
    documents:   '文档',
    evidence:    '证据',
    personnel: '人员',
    attendance: '考勤',
    gate: '门岗',
    riesgos:     '风险',
    complianceRules: '合规规则',
    costos:      '成本',
    calculators: '计算器',
    expensesDashboard: '费用',
    expenseApprovals: '待审批',
    incompleteDrafts: '未完成登记',
    openMenu: '打开菜单',
    closeMenu: '关闭菜单',
    changeLanguage: '切换语言',
    changeTheme: '切换主题',
    viewProfile: '查看个人资料',
  },

  telegramHelp: {
    tooltip: '如何通过 Telegram 完成此操作',
    title: '通过 Telegram 操作',
    intro: '你也可以直接对 Telegram 上的 Agrovid 机器人说话或发文字来记录这项操作，无需使用此表单。',
    asCommand: '使用命令',
    naturalLanguage: '使用自然语言（语音或文字）',
    defaultDesc: 'AI 会理解你的指令，生成草稿，并在保存任何数据前请你确认。',
    gotIt: '知道了',
    openBot: '在 Telegram 中打开',
    botOnline: '在线',
    examples: {
      machinery: [
        { voice: "我在4号地块启动了约翰迪尔拖拉机，翻耕", desc: "机器人会在该地块以指定作业启动机器计时。" },
        { voice: "拖拉机干完了，用了40升柴油，作业6公顷", desc: "结束本次作业，并根据油耗和工时计算每公顷成本。" },
      ],
      sales: [
        { voice: "他们为发票123存入了2万美元", desc: "机器人会生成收款草稿，确认后冲减余额。" },
        { voice: "我们从客户Frutas del Norte收到15万比索", desc: "支持比索或美元；保存前总会请你确认。" },
      ],
      cropPhenology: [
        { voice: "4号地块的苦瓜已开花，播种后第35天", desc: "机器人会记录作物当前的物候期。" },
        { voice: "2号地块已进入结果期，第60天", desc: "更新地块的生育期并重新计算距离采收的天数。" },
        { voice: "7号地块的四季豆刚出苗，第8天", desc: "适合跟踪刚播种的作物。" },
      ],
      cropPest: [
        { voice: "在4号地块发现粉虱，中等压力，约25%的植株", desc: "机器人会记录病虫害并计算压力等级。" },
        { voice: "2号地块有蓟马，压力很高，几乎半块地", desc: "高压力可能会向农艺师触发警报。" },
        { voice: "7号地块发现霜霉病斑，只有几株", desc: "病害与虫害一样记录发生率。" },
      ],
      finance: [
        { voice: "将4号地块2026-A周期的预算设为每公顷40,000比索", desc: "机器人可以记录该周期的基础预算，以及预计产量和价格。" },
        { voice: "7号地块2026-B周期预算：55,000比索，预计12吨，每公斤18比索", desc: "包含预计产量和价格，用于预测利润率。" },
        { voice: "4号地块的预算目前已花了多少？", desc: "也可以查询预算执行进度。" },
      ],
      harvest: [
        { voice: "我们从4号地块包装了40箱A级四季豆和5箱B级，中等规格", desc: "机器人会记录包装及各等级明细，并计算包装率%。" },
        { voice: "2号地块采收：120箱A级，30箱B级，10箱次级", desc: "可以在一条消息里说出多个等级。" },
        { voice: "今天在7号地块采了300公斤苦瓜", desc: "没有箱数时，机器人可以记录采收重量。" },
      ],
      inventory: [
        { voice: "20袋Novatec肥料入库，发票15,000比索", desc: "机器人会自动记录出入库，并对照目录校验产品。" },
        { voice: "从仓库领出5升阿维菌素用于4号地块", desc: "出库会扣减库存并关联到地块。" },
        { voice: "仓库里还剩多少Novatec？", desc: "无需打开系统也可以查询库存。" },
      ],
      irrigation: [
        { voice: "用机动背负式设备给北区地块灌溉了4小时，加入2升氮肥", desc: "机器人会记录灌溉（人工、水表或简化）及施肥灌溉（如有）。" },
        { voice: "4号地块滴灌，早上6点到10点", desc: "根据开始和结束时间计算灌溉时长。" },
        { voice: "7号地块水表读数：1,250立方米", desc: "对于水表灌溉，机器人会与上次读数对比计算用水量。" },
      ],
      merma: [
        { voice: "4号地块因腐烂损失了50公斤四季豆", desc: "机器人会记录损耗原因并计算经济影响。" },
        { voice: "2号地块有12箱苦瓜因热害损失", desc: "可以用箱数代替公斤数。" },
        { voice: "包装时因机械损伤丢弃了30公斤产品", desc: "采收后的损耗也可以记录。" },
      ],
      payroll: [
        { voice: "胡安·佩雷斯在4号地块工作了3个工日，每天250比索", desc: "机器人会把付款（日工、计件或合同）记录到当前开放的工资期。" },
        { voice: "玛丽亚·洛佩斯在2号地块采了80箱四季豆，每箱6比索", desc: "计件请说明数量和单价。" },
        { voice: "佩德罗·拉米雷斯，清理7号地块水渠的合同，3,000比索", desc: "合同按固定金额记录。" },
      ],
      porcinoEntry: [
        { voice: "80头猪进入3号猪圈，平均体重25公斤，供应商Granja del Norte", desc: "机器人会记录育肥批次并将猪圈标记为使用中。" },
        { voice: "120头仔猪到达1号猪圈，平均18公斤", desc: "每次进栏都会开启一个新的育肥批次。" },
        { voice: "我们从供应商Los Pinos接收了60头猪到5号猪圈，平均30公斤", desc: "附上供应商以便追溯。" },
      ],
      porcinoEvent: [
        { voice: "我们给3号猪圈的猪称重了，现在平均55公斤", desc: "机器人会判断是称重、饲料、死亡还是出售，并记录相应事件。" },
        { voice: "今天3号猪圈死了2头猪", desc: "记录死亡并从批次中扣除头数。" },
        { voice: "我们给3号猪圈喂了400公斤饲料", desc: "饲料用于计算料肉比。" },
        { voice: "我们从3号猪圈卖了50头猪，共6,000公斤，每公斤32比索", desc: "出售会扣减头数并计算收入。" },
      ],
      protocol: [
        { voice: "这是苦瓜的施肥方案", desc: "可以连同方案的照片或PDF一起发送；AI不会编造用量，只提取文档中的内容。" },
        { voice: "这是四季豆2026-A周期的病虫害防治计划", desc: "适用于植保和营养方案。" },
        { voice: "发给你供应商的农药价格表", desc: "也会从文档中提取价格目录。" },
      ],
      spray: [
        { voice: "我在4号地块用无人机喷施阿维菌素，2桶，7点进、8点出", desc: "保存任何植保施药前，机器人会自动校验MRL/PHI合规性。" },
        { voice: "我用背负式喷雾器给2号地块喷了3桶多杀菌素，操作员Luis", desc: "记录设备、桶数和操作员。" },
        { voice: "我在7号地块施了铜制剂，每公顷4升，美国市场", desc: "如果产品不符合目标市场的MRL，机器人会在保存前提醒你。" },
      ],
      tasks: [
        { voice: "明天要在4号地块施肥，通知Everardo", desc: "机器人会为指定工人安排任务，并在早上7点通知。" },
        { voice: "周五要修剪2号地块，分配给Manuel", desc: "可以在同一句话里说明日期和负责人。" },
        { voice: "提醒整个灌溉团队周一检查水管", desc: "任务可以发给整个小组。" },
      ],
      transport: [
        { voice: "4号地块发出一批货到麦卡伦，200箱A级四季豆，司机Manuel，车牌ABC-123", desc: "机器人会记录运单的目的地、箱数和运输信息。" },
        { voice: "我们给瓜达拉哈拉批发市场发了150箱苦瓜", desc: "有目的地和箱数就够了，其余可以稍后补充。" },
        { voice: "发往麦卡伦的货已到达，交付了198箱", desc: "可以通报到达情况和交付箱数。" },
      ],
    },
    cropPhenologyVoice: '4 号地块的苦瓜已经开花了，现在是播种后第 35 天',
    cropPhenologyDesc: '机器人会记录作物当前的物候期。',
    cropPestVoice: '我在 4 号地块发现了白粉虱，中等程度，大约 25% 的植株受害',
    cropPestDesc: '机器人会记录病虫害并计算危害程度。',
    financeVoice: '把 4 号地块 2026-A 周期的预算定为每公顷 4 万比索',
    financeDesc: '机器人可以为该周期记录基础预算，包括预计产量和价格。',
    harvestVoice: '我们从 4 号地块包装了 40 箱 A 级四季豆和 5 箱 B 级，中等规格',
    harvestDesc: '机器人会按等级明细记录包装，并计算出货率（packout %）。',
    inventoryVoice: '20 袋 Novatec 肥料入库了，发票金额 15,000 比索',
    inventoryDesc: '机器人会自动记录仓库的入库或出库，并对照产品目录校验产品。',
    irrigationVoice: '我们用机动背负式喷雾器给北区地块灌溉了 4 小时，加了 2 升氮肥',
    irrigationDesc: '机器人会记录灌溉（手动、计量或简化方式），如有施肥也会一并记录。',
    mermaVoice: '4 号地块有 50 公斤四季豆因腐烂而损耗',
    mermaDesc: '机器人会记录损耗及其原因，并计算经济影响。',
    payrollVoice: '胡安·佩雷斯在 4 号地块干了 3 个工日，每天 250 比索',
    payrollDesc: '机器人会把这笔付款（日工、计件或合同）记录到当前开放的工资周期中。',
    porcinoEntryVoice: '80 头猪进了 3 号圈，平均体重 25 公斤，供应商 Granja del Norte',
    porcinoEntryDesc: '机器人会记录育肥批次，并将该圈标记为使用中。',
    porcinoEventVoice: '我们给 3 号圈的猪称重了，现在平均 55 公斤',
    porcinoEventDesc: '机器人会识别这是称重、饲喂、死亡还是出售，并记录相应事件。',
    protocolCommandDesc: '农艺师发送投入品方案的照片或文件，AI 会自动提取施用安排、剂量和价格目录。',
    protocolVoice: '这是苦瓜的施肥方案',
    protocolDesc: '可以连同方案的照片或 PDF 一起发送；AI 不会编造剂量，只提取文件中已有的内容。',
    sprayVoice: '我在 4 号地块用无人机施了阿维菌素，2 个药箱，7 点进去，8 点出来',
    sprayDesc: '机器人会在保存任何植保施药记录前，自动校验 MRL/PHI 合规性。',
    tasksVoice: '明天要在 4 号地块施肥，通知 Everardo',
    tasksDesc: '机器人会为指定的工人安排任务，并在早上 7 点通知他。',
    transportVoice: '一批货从 4 号地块发往 McAllen，200 箱 A 级四季豆，司机 Manuel，车牌 ABC-123',
    transportDesc: '机器人会记录该批货运的目的地、箱数和运输信息。',
  },

  inventoryUpload: {
    dragDrop: '将 Excel 文件拖放到此处',
    orBrowse: '或点击选择文件',
    selected: '已选择：',
    upload: '上传',
    historyTitle: '上传历史',
    colFile: '文件',
    colStatus: '状态',
    colRecords: '记录数',
    colErrors: '错误数',
    colDate: '日期',
  },

  common: {
    colorLabel: '地图颜色',
    colorCustom: '自定义颜色',
    colorClear: '清除颜色',
    loading:    '加载中...',
    save:       '保存',
    cancel:     '取消',
    edit:       '编辑',
    delete:     '删除',
    search:     '搜索',
    noData:     '暂无数据。',
    yes:        '是',
    no:         '否',
    of:         '共',
    actions:    '操作',
    status:     '状态',
    date:       '日期',
    total:      '合计',
    name:       '名称',
    type:       '类型',
    method:     '方法',
    notes:      '备注',
    reloadPage: '重新加载',
    errorUnexpected: '应用程序发生意外错误。',
    somethingWrong:  '出错了',
    searchNoResults: '无结果',
    createNamed: '创建“{name}”',
    close: '关闭',
    zoneCycleClosed: '周期已结束',
    searchRanchoPlaceholder: '搜索牧场...',
    searchLotePlaceholder: '搜索地块...',
    searchBatchPlaceholder: '搜索周期...',
    validation: {
      fieldRequired: '此字段为必填项',
      nameRequired:       '名称为必填项',
      ranchoRequired:     '牧场为必填项',
    },
    valueLabel: '值',
    rangeLabel: '范围',
    configSaveError: '无法保存配置。',
  },

  phoneField: {
    countryLabel: '国家/地区',
    phoneLabel: '电话',
    placeholder: '4421234567',
    favorites: '常用',
    allCountries: '所有国家/地区',
    invalidTen: '请输入10位手机号码，不含国家区号和开头的0。例如 4421234567',
    invalidGeneric: '所选国家/地区的号码无效（总共8至15位数字）。',
    hint: '只需填写号码，不含国家区号。',
  },
  passwordField: {
    show: '显示密码',
    hide: '隐藏密码',
    confirmLabel: '确认密码',
    mismatch: '两次输入的密码不一致',
    rulesTitle: '密码必须包含：',
    ruleLength: '至少{n}个字符',
    ruleUpper: '一个大写字母',
    ruleLower: '一个小写字母',
    ruleNumber: '一个数字',
    ruleSpecial: '一个特殊字符（如 ! @ # $）',
    rulePass: '已满足',
    ruleFail: '未满足',
  },
  auth: {
    title:        'Agrovid',
    subtitle:     '智能农场管理系统',
    loginGoogle:  '使用Google账号登录',
    googleBtn:    '使用Google登录',
    demoBtn:      '演示模式（无需账号）',
    otpSent:      '已将验证码发送至您的Telegram',
    otpPlaceholder: '000000',
    verify:       '验证',
    back:         '← 返回',
    errorGoogle:  'Google认证失败',
    errorOtp:     '验证码错误',
    errorDemo:    '演示模式启动失败',
    logOut:       '退出',
    hasPasswordLink: '已有密码？在此登录',
    emailPasswordTitle: '或使用邮箱和密码登录',
    emailLoginPlaceholder: 'youremail@example.com',
    passwordSubtitle: '使用手机号和密码登录',
    passwordPlaceholder: '密码',
    phonePlaceholder: '4421234567',
    enter:        '登录',
    errorPassword: '凭据无效',
    showPassword: '显示密码',
    hidePassword: '隐藏密码',
    forgotPassword: '忘记密码？',
    passwordResetSubtitle: '恢复您的密码',
    resetViaTelegram: '通过 Telegram',
    resetViaEmail: '通过电子邮件',
    resetEmailPlaceholder: 'you@example.com',
    phoneCountryHint: '请输入带国家区号的号码，例如 +52…',
    phoneInvalid: '电话无效。请输入10位号码，例如 4421234567',
    emailInvalid: '电子邮件无效',
    resetCodeSentHint: '如果信息已注册且已绑定该方式，您将收到验证码。',
    resetNotArrived: '没收到：尝试其他方式',
    resendCode: '重新发送验证码',
    resendIn: '{n} 秒后可重新发送',
    resetLocked: '验证码发送次数已用完。请联系管理员解锁密码找回。',
    otpWait: '请等待 {n} 秒后再获取验证码。',
    otpLocked: '验证码发送次数已用完。请联系管理员解锁。',
    googleEmailNotRegistered: '电子邮件 {email} 未注册。',
    googleUseOtherAccount: '使用其他账号',
    sendCode: '发送代码',
    enterResetCode: '输入代码和新密码',
    newPasswordPlaceholder: '新密码',
    resetPassword: '设置密码',
    tenantNotFound: '未找到该企业',
    tenantNotFoundHint: '请检查网址或联系管理员。',
    inviteBanner: '您收到了一份邀请，加入身份为',
    // F0.10 — 邀请接受页面
    acceptInviteTitle: '完成注册',
    acceptInviteSubtitle: '邀请您加入的身份为',
    acceptInviteName: '姓名',
    acceptInvitePhone: '电话',
    acceptInvitePhoneOptional: "手机号（可选）",
    acceptInviteIdentityRequired: "请填写手机号或邮箱，或使用 Google 登录。",
    acceptInvitePasswordNeedsEmail: "使用密码时请同时填写邮箱。",
    acceptInviteEmail: '邮箱（用于 Google 登录）',
    acceptInvitePassword: '密码（可选）',
    acceptInvitePasswordHint: '留空则使用 Google 或 Telegram 登录。',
    acceptInviteSubmit: '创建账户',
    acceptInviteLoading: '正在验证邀请...',
    acceptInviteInvalid: '此邀请无效或已过期。请向管理员索取新的邀请。',
    acceptInviteUsed: '此邀请已被使用。如果您已有账户，请直接登录。',
    acceptInvitePhoneTaken: '该电话号码已存在账户。请直接登录，无需注册。',
    acceptInviteLinkTelegram: '绑定我的 Telegram',
    acceptInviteError: '无法创建账户。请检查信息后重试。',
    acceptInviteNameRequired: '请输入姓名',
    acceptInvitePhoneRequired: '请输入有效电话（墨西哥和美国：10位数字）',
    acceptInviteEmailInvalid: '邮箱无效',
    acceptInvitePasswordShort: '密码至少需要 8 个字符',
    acceptInviteGoToLogin: '前往登录',
    // F0.10 — 需要短信验证码但账户未绑定 Telegram
    telegramNotLinked: '您的账户未绑定 Telegram，因此无法向您发送验证码。',
    telegramNotLinkedHint: '请向管理员索取 Telegram 绑定链接。',
    telegramNotLinkedPassword: '您也可以使用密码登录。',
    roleSelectionTitle: '选择您的角色',
    roleSelectionSubtitle: '选择您想用来访问的角色',
    selectRoleError: '选择角色出错。请重试。',
    noRoleAssigned: '您的账户尚未分配角色，请联系管理员。',
    noRoleSignOut: '退出',
    roles: {
      fieldWorker: '田间工作人员',
      supervisor: '主管',
      admin: '管理员',
      agronomist: '农学家',
      superAdmin: '超级管理员',
      auditor: '审计员',
      warehouseManager: '仓库管理员',
      investor: '投资人',
    },
  },

  demo: {
    fieldWorker:      '操作权限和活动记录登记权限',
    supervisor:       '操作和任务管理权限',
    warehouseManager: '库存管理、库存跟踪和FEFO控制权限',
    agronomist:       '作物方案、物候学和病虫害监测权限',
    investor:         '财务 KPI、报告与投资总览（只读）',
    admin:            '完整管理员权限，包括用户和财务',
  },

  dashboard: {
    title:          '仪表板',
    kpiCostToday:   '今日成本',
    kpiActivities:  '活动数',
    kpiActivitiesSub: '过去24小时',
    kpiPendingTasks: '待处理任务',
    kpiOverdueTasks: '逾期任务',
    kpiActiveLots:   '活跃地块',
    kanbanTitle:    '看板 — 今日任务',
    activityTitle:  '近期活动',
    loadingKpis:    '加载KPI中...',
    moveTo:         '移至',
  },

  onboarding: {
    title:          '开始使用',
    celebration:    '恭喜！你已准备就绪 🎉',
    stepPotreros:   '录入你的地块',
    stepCultivos:   '设置你的作物',
    stepInsumos:    '录入初始库存',
    go:             '前往',
    bulkImportHint: '要一次性建立整个农场吗？用一个 Excel 文件批量上传农场、地块、用户和目录。',
    bulkImportCta:  '前往批量导入',
    required:       '必填',
    optional:       '可选',
    optionalTitle:  '可选项',
    markReviewed:   '标记为已查看',
    blocks: { structure: '结构', people: '人员', operation: '运营', finance: '财务', system: '系统' },
    items: {
      rancho: { label: '创建农场', desc: '至少添加一个农场。' },
      lotes_geocerca: { label: '带地理围栏的地块', desc: '在地图上绘制地块轮廓。' },
      cultivo: { label: '作物与播种', desc: '指定作物和播种日期。' },
      usuarios_accesos: { label: '用户与权限', desc: '邀请用户并设置其农场和地块。' },
      telegram: { label: '已绑定 Telegram', desc: '绑定 Telegram 以使用田间机器人。' },
      grupo_telegram: { label: 'Telegram 群组', desc: '连接农场的 Telegram 群组。' },
      productos_reglas: { label: '产品与规则', desc: '录入产品及 PHI/REI/MRL 规则。' },
      bodega_stock: { label: '仓库与库存', desc: '录入仓库的初始库存。' },
      maquinaria: { label: '农机', desc: '登记拖拉机和设备。' },
      nomina: { label: '工资', desc: '为员工设置工资。' },
      presupuesto: { label: '预算', desc: '为一个周期设定预算。' },
      locale: { label: '语言与时区', desc: '查看语言、时区和货币。' },
      proveedores_ia: { label: 'AI 提供商', desc: '检查 AI 提供商是否正常响应。' },
      reportes: { label: '定时报告', desc: '设置报告的定时发送。' },
    },
  },

  tour: {
    next:     '下一步',
    prev:     '上一步',
    done:     '完成',
    progress: '第 {{current}} 步，共 {{total}} 步', // driver.js 自身的模板语法 — 不是我们的 i18n 插值
    dashboard: {
      title: '首页',
      description: '在这里查看你的运营概况：关键指标、最近的活动记录和配置进度。',
    },
    camposLotes: {
      title: '地块管理',
      description: '录入你的地块，在地图上绘制边界，并为每个地块分配作物或用途。',
    },
    tareas: {
      title: '任务',
      description: '安排和分配田间作业——播种、喷药、施肥——设置截止日期和负责人。',
    },
    insumos: {
      title: '投入品',
      description: '管理农药、肥料等投入品库存，库存不足时自动提醒。',
    },
    finanzas: {
      title: '财务',
      description: '记录支出和收入，查看每公顷成本和现金流报告。',
    },
  },

  adoption: {
    countSingular: '本周已录入 {count} 条数据',
    countPlural:   '本周已录入 {count} 条数据',
    teamRankingTitle: '本周团队采用情况',
  },

  tasks: {
    title:             '任务',
    loading:           '加载任务中...',
    taskUnit:          '任务',
    taskUnitPlural:    '任务',
    overdueLabel:      '已逾期',
    viewKanban:        '看板视图',
    viewList:          '列表视图',
    searchPlaceholder: '搜索任务...',
    filterAllLots:     '所有地块',
    filterPriority:    '优先级',
    filterType:        '类型',
    clearFilters:      '清除筛选',
    emptyColumn:       '暂无任务',
    emptyFilter:       '没有符合条件的任务。',
    overdueBadge:      '已逾期',
    tableTitle:        '任务',
    tableLot:          '地块',
    tableType:         '类型',
    tablePriority:     '优先级',
    tableDate:         '日期',
    tableStatus:       '状态',
    newTask:           '+ 新建任务',
    newTaskTitle:      '新建任务',
    editTaskTitle:     '编辑任务',
    deleteTaskTitle:   '删除任务',
    deleteTaskConfirm: '删除此任务？此操作无法撤销。',
    fieldLote:         '地块 *',
    fieldZone:         '区域（可选）',
    zoneAllLote:       '整个地块',
    fieldTaskTitle:    '标题 *',
    fieldTaskTitlePlaceholder: '在V9施肥...',
    fieldDescription:  '描述',
    fieldActivityType: '活动类型 *',
    fieldScheduledDate: '计划日期 *',
    fieldDueDate:      '截止日期',
    fieldPriority:     '优先级',
    fieldAssignedTo:   '负责人（电话）',
    loteRequired:      '请选择地块',
    noLoteOption: '无地块（通用任务）',
    generalLabel: '通用',
    audTitle: '发送给',
    audIndividual: '个人（无受众）',
    audAll: '所有人（有农场权限）',
    audRoles: '按角色',
    audLotes: '按地块',
    audCrops: '按作物',
    audPickRoles: '角色',
    audPickLotes: '地块',
    audPickCrops: '作物',
    audReachCount: '将通知 {n} 人',
    audReachOne: '将通知 1 人',
    audNoRecipients: '该受众没有接收人',
    audTooMany: '超过 {max} 人上限：请减少人员',
    audRemove: '移除',
    audAdd: '添加人员',
    audAddPlaceholder: '搜索人员…',
    audNoTelegram: '未绑定 Telegram',
    audLoading: '正在计算接收人…',
    audPreviewError: '无法计算接收人列表',
    audSnapshotHint: '此列表以固定快照保存：权限或作物变化不会修改它，除非点击“重新计算接收人”。',
    audRecompute: '重新计算接收人',
    audRecomputed: '已新增 {n} 位接收人',
    audRecomputeNone: '没有新的接收人',
    audRecomputeError: '无法重新计算接收人',
    audNoRecipientsError: '请至少添加一位接收人',
    audRoleNames: {
      SUPER_ADMIN: '超级管理员', ADMIN: '管理员', AGRONOMIST: '农艺师', SUPERVISOR: '主管',
      WAREHOUSE_MANAGER: '仓库管理员', FIELD_WORKER: '田间工人', AUDITOR: '审计员', HR: '人力资源', GUARD: '保安', INVESTOR: '投资人',
    },
    ranchoRequired: '请为通用任务选择牧场',
    fieldRancho: '牧场 *',
    titleRequired:     '标题为必填项',
    dateRequired:      '计划日期为必填项',
    createTaskError:   '创建任务时出错',
    updateTaskError:   '更新任务时出错',
    deleteTaskError:   '删除任务时出错',
    assignedToPlaceholder: '选择人员...',
    fieldNotifyAt: '定时通知（可选）',
    notifyAtHint: '留空 = 保存时通过 Telegram 通知。设置日期和时间后，将在该时间发送通知。',
      notifyScheduledLabel: '定时通知', notifyCancelButton: '取消通知', notifyKeepButton: '保留通知', notifyCancelledHint: '保存时将取消定时通知。',
    noAssigneesHint:   '目前没有在Telegram注册的用户。',
    telegramHelpTooltip: '如何在Telegram上为某人注册',
    telegramHelpTitle:  '在Telegram上注册用户',
    telegramHelpIntro:  '要让某人出现在此列表中，需要先在Telegram上通过Agrovid机器人注册。有两种方式：',
    telegramHelpCommandLabel: '使用命令',
    telegramHelpCommandDesc:  '该用户打开与机器人的对话并输入 /start。机器人会要求分享电话号码以进行身份验证。',
    telegramHelpVoiceLabel:   '使用自然语言（语音或文字）',
    telegramHelpVoiceExample: '你好，我想注册',
    telegramHelpVoiceDesc:    'AI会识别意图并引导完成相同的身份验证流程，无需记住确切的命令。',
    confirmDialog:            '明白了',
    linkedShipment:           '关联到发货',
    linkedPayroll:            '关联到工资',
    groupPending:             '待处理',
    groupCompleted:           '已完成',
    markComplete:             '标记为已完成',
    alreadyCompleted:         '已完成',
  },

  taskStatus: {
    PENDING:     '待处理',
    IN_PROGRESS: '进行中',
    COMPLETED:   '已完成',
    CANCELLED:   '已取消',
  },

  taskPriority: {
    HIGH:   '高',
    MEDIUM: '中',
    LOW:    '低',
  },

  // ── 未完成登记 — 网页向导（第二阶段） ────────────────────────────────────────
  incompleteDrafts: {
    pageTitle:            '未完成登记',
    pageDescription:      '完成因缺少数据而暂时无法提交的登记。',
    emptyState:           '暂无待完成的登记。🎉',
    reasonNotSpecified:   '你没有指定地块。',
    reasonNotFound:       '你提到的地块目前不可用。',
    reasonRestricted:     '你提到的地块目前不可用。',
    reasonAmbiguous:      '有多个同名地块 — 请选择正确的一个。',
    fieldLote:            '地块',
    fieldQuantity:        '数量 / 费用',
    fieldDate:            '日期',
    fieldNotes:           '备注',
    completeButton:       '完成',
    saveButton:           '保存更改',
    cancelButton:         '取消登记',
    cancelConfirmTitle:   '取消这条登记？',
    cancelConfirmBody:    '此操作无法撤销，口述的记录将会丢失。',
    cancelConfirmConfirm: '是，取消',
    cancelConfirmDismiss: '返回',
    completeSuccess:      '登记已完成！你现在可以在 Telegram 中确认它。',
    stillIncomplete:      '已保存，但仍有其他数据需要补全。',
    cancelSuccess:        '登记已取消。',
    myTab:                '我的登记',
    teamTab:               '团队',
    teamEmptyState:       '你的团队暂无待处理的登记。🎉',
    ownerLabel:           '{name} 的登记',
  },

  field: {
    title:          '田地与地块',
    searchPlaceholder: '搜索地块或作物...',
    lots:           '地块',
    lotsPlural:     '地块',
    loadingLots:    '加载地块中...',
    hectares:       '公顷',
    lotStatus: {
      ACTIVE:   '活跃',
      FALLOW:   '休耕',
      INACTIVE: '非活跃',
    },
    detail: {
      accumulatedCost: '累计成本',
      costByType:      '按类型成本',
      recentActivity:  '近期活动',
      noActivities:    '暂无近期活动。',
      loadingCosts:    '加载成本中...',
    },
    // Form
    editTitle:              '编辑 — {name}',
    newLoteTitle:           '新建田地/地块',
    lotNameLabel:           '地块名称 *',
    ranchoIdLabel:          '牧场 *',
    ranchoIdHint:           '此地块所属牧场的ID',
    searchRanchoPlaceholder: '搜索牧场...',
    cropTypeLabel:          '当前作物',
    hectaresLabel:          '公顷',
    usoLabel:               '地块用途',
    usoCampo:               '田间',
    usoAdministrativo:      '行政（办公室，不计入田间）',
    latitudeLabel:          '纬度',
    goToMapAfterCreate:     '保存后前往地图绘制地块边界',
    addGeofence:            '边界',
    geofenceCaptured: '已采集围栏',
    geofenceMissing:  '无围栏',
    editGeofence:     '编辑围栏',
    addModules:             '区域',
    pickOnMapBtn:           '在地图上选择',
    locationDialogTitle:    '{name} 的位置',
    locationDialogTitleGeneric: '地块位置',
    longitudeLabel:         '经度',
    noResultsSearch:        '未找到符合搜索条件的结果。',
    noLotsRegistered:       '尚无注册地块。',
    createFirst:            '创建首个地块',
    editLot:                '编辑地块',
    newLot:                 '新建地块',
    deleteTitle:            '删除地块',
    deleteMessagePrefix:    '删除 ',
    deleteMessageSuffix:    '？此操作无法撤销。仅当地块未注册任何活动时才可执行。',
    saveChanges:            '保存更改',
    createLote:             '创建地块',
  },

  irrigation: {
    lotesLoadError: '无法加载地块。',
    title:         '灌溉与施肥',
    selectLot:     '选择地块',
    zoneFilter:    '地块或区域',
    allLotes:      '所有地块',
    tableLote:     '地块',
    kpiVolume:     '总用水量 (升)',
    kpiVolumeSub:  '次灌溉',
    kpiEfficiency: '平均升/公斤',
    kpiEfficiencySub: '水分利用率',
    kpiLastIrrigation: '上次灌溉',
    chartVolume:   '每次灌溉水量（最近30次）',
    chartEfficiency: '水分利用率 (升/公斤)',
    tableTitle:    '灌溉记录',
    tableDate:     '日期',
    tableMethod:   '方法',
    tableVolume:   '水量 (升)',
    tableDuration: '时长 (分钟)',
    tableDepthMm:  '灌溉水深 (mm)',
    fieldDepthMm:  '灌溉水深 (mm，可选)',
    tableLPerKg:   '升/公斤',
    tableFert:     '施肥',
    tableFertCost: '施肥成本',
    tableTimestamps: '时间',
    tableRecordedBy: '记录人',
    sourceTelegram: 'Telegram',
    sourceWeb:      '网页',
    chartByLote:    '各地块用水量 (L)',
    flowUnit:       '升/小时',
    noRecords:     '该地块暂无记录。',
    methods: {
      MANUAL:     '手动',
      METER:      '水表',
      SIMPLIFIED: '简化',
    },
    timestamps: {
      title:               '灌溉时间',
      inicioHidratacion:   '水化开始',
      inicioFertilizacion: '施肥开始',
      finFertilizacion:    '施肥结束',
      finLavadoCintilla:   '冲洗结束',
      duracionFert:        '施肥时长',
    },
    newRecord:          '+ 登记灌溉',
    newRecordTitle:     '登记灌溉',
    fieldBatch:         '周期 (batchId) *',
    fieldMethod:        '方式 *',
    methodManual:       '手动',
    methodMedidor:      '水表',
    methodSimplificado: '简化',
    fieldDurationMin:   '时长（分钟）',
    fieldFlowRate:      '预计流量 (L/h)',
    fieldMeterStart:    '起始读数',
    fieldMeterEnd:      '结束读数',
    fieldDescription:   '描述',
    fieldNotes:         '备注',
    batchRequired:      '周期 (batchId) 为必填项',
    createError:        '登记灌溉时出错',
    create:             '登记',
    kpiLoadError: '无法加载灌溉 KPI。',
    tsHydrationStart: '补水开始',
    tsFertStart: '施肥开始',
    tsFertEnd: '施肥结束',
    tsWashEnd: '冲洗结束',
    stockDeductWarnTitle: '库存扣减未完成',
    stockDeductFailed: '灌溉记录已保存，但投入品的自动扣减未能完成。请在库存中检查并调整库存。',
    stockDeductPartial: '灌溉记录已保存，但投入品的自动扣减仅部分完成（有投入品未扣减）。请在库存中检查库存。',
  },

  spray: {
    title:            '农药施用',
    selectLot:        '选择地块',
    zoneFilter:    '地块或区域',
    kpiTotal:         '施药总次数',
    kpiLastApp:       '上次施药',
    tableTitle:       '施药记录',
    tableDate:        '日期',
    tableLote:        '地块',
    tableProduct:     '产品',
    tableDosis:       '剂量',
    tableOperator:    '操作员',
    tableEquipo:      '设备',
    tableCargas:      '罐数',
    tableVelocidad:   '速度 (km/h)',
    tableLitrosAgua:  '用水量 (升)',
    tableHoraEntrada: '进地时间',
    tableHoraSalida:  '离地时间',
    noRecords:        '暂无施药记录。',
    newApplicationBtn:  '+ 登记施药',
    newApplicationTitle: '登记农药施用',
    fieldOccurredAt:    '施药日期/时间 *',
    fieldProductName:   '产品（名称）*',
    fieldQty:           '数量 *',
    fieldUnit:          '单位 *',
    fieldProductId:     '产品ID（目录，用于合规）*',
    searchProductPlaceholder: '搜索产品...',
    fieldCrop:          '作物（合规）*',
    fieldMarket:        '目标市场 *',
    fieldPlannedHarvest: '计划收获日期',
    fieldBatchId:       '周期 (batchId)',
    sprayDataTitle:     '设备施用数据（可选）',
    fieldEquipo:        '设备（拖拉机、无人机、背负式）',
    fieldCargas:        '罐数',
    fieldVelocidad:     '速度 (km/h)',
    fieldLitrosAgua:    '用水量（升）',
    fieldHoraEntrada:   '进地时间 (HH:MM)',
    fieldHoraSalida:    '离地时间 (HH:MM)',
    fieldOperador:      '操作员',
    fieldNotes:         '备注',
    dateRequired:       '日期为必填项',
    productRequired:    '产品名称为必填项',
    productIdRequired:  '需要产品ID以验证合规性',
    cropRequired:       '需要作物信息以验证合规性',
    complianceBlockedTitle: '被MRL/PHI合规规则阻止',
    createError:        '登记施药时出错',
    create:             '登记',
  },

  finance: {
    title:            '财务',
    totalCost:        '累计总成本',
    costByLot:        '各地块成本',
    weeklyTrend:      '周趋势',
    byLotTable:       '各地块明细',
    tableLot:         '地块',
    tableActivities:  '活动数',
    tableCost:        '成本',
    tableTotal:       '合计',
    loadingSummary:   '加载财务摘要中...',
    costLabel:        '成本',
    protocolsTitle:   '已加载的投入协议',
    protocolsDesc:    '通过Telegram发送。成本/公顷用作应用到地块时的基础预算。',
    protocolsLoading: '加载协议中...',
    protocolsEmpty:   '暂无已注册协议。',
    protocolCropCycle: '作物 / 周期',
    protocolType:     '类型',
    protocolChemHa:   '化学品/公顷',
    protocolFertHa:   '化肥/公顷',
    protocolTotalHa:  '总计/公顷',
    protocolOrigin:   '来源',
    protocolTypeQuimico:     '化学品',
    protocolTypeFertilizante: '化肥',
    protocolTypeAmbos:       '两者',
    protocolSourceDoc:   'DOCX',
    protocolSourcePhoto: '照片',
    protocolSourceText:  '文本',
    tabResumen:          '摘要',
    tabPL:               '盈亏',
    tabCashflow:         '现金流',
    tabPresupuesto:      '预算',
    plTitle:             '损益表 (P&L)',
    plRevenue:           '预计收入',
    plRevenueSub:        '基于活跃预算中的产量 × 价格',
    plCosts:             '实际成本',
    plMargin:            '毛利润',
    plMarginPct:         '利润率 %',
    plCostByCategory:    '分类成本',
    plLoading:           '加载盈亏数据...',
    cashflowTitle:       '现金流',
    cashflowInflow:      '流入',
    cashflowOutflow:     '流出',
    cashflowBalance:     '余额',
    cashflowWeek:        '每周',
    cashflowMonth:       '每月',
    cashflowLoading:     '加载现金流...',
    budgetTitle:         '预算 vs 实际',
    budgetCategory:      '类别',
    budgetPlanned:       '预算',
    budgetExecuted:      '实际',
    budgetVariance:      '差异',
    budgetPct:           '%',
    budgetCommitted:     '已承诺',
    budgetTotalPlanned:  '计划总额',
    budgetTotalExecuted: '已执行总额',
    budgetTotalCommitted: '已承诺总额',
    editCommittedTitle:  '编辑承诺金额',
    editCommittedLabel:  '承诺金额 (MXN)',
    editCommittedSave:   '保存',
    noActiveBudget:      '该地块没有生效中的预算',
    budgetLoading:       '加载预算...',
    budgetAllLots:       '所有地块',
    newBudgetBtn:        '+ 新建预算',
    newBudgetTitle:      '新建预算',
    fieldRanchoId:       '牧场 *',
    fieldLoteId:         '地块 *',
    searchRanchoPlaceholder: '搜索牧场...',
    searchLotePlaceholder: '搜索地块...',
    fieldCiclo:          '周期 *',
    fieldCurrency:       '预算货币',
    fieldTotalBudget:    '总预算 ($) *',
    fieldEstimatedYield: '预计产量',
    fieldEstimatedPrice: '预计价格 ($/单位)',
    fieldNotes:          '备注',
    ranchoRequired:      '牧场为必填项',
    loteRequired:        '地块为必填项',
    cicloRequired:       '周期为必填项',
    totalBudgetRequired: '总预算必须大于0',
    createBudgetError:   '创建预算时出错',
    create:              '创建',
    categories: {
      PREPARACION: '整地',
      SIEMBRA: '播种',
      RIEGO: '灌溉',
      FERTILIZACION: '施肥',
      FITOSANITARIO: '植保',
      COSECHA: '收获',
      MAQUINARIA: '机械',
      LABORES: '农事作业',
      OTRO: '其他',
    },
  },

  topbar: {
    project: '项目',
    allProjects: '全部项目',
    rancho: '农场',
    allRanchos: '所有农场',
  },

  terrain: {
    cropAssignedInCultivo: '作物和播种日期请在“作物”页面设置。',
    module: '区域',
    modules: '区域',
    valve: '阀门',
    areaHa: '面积（公顷）',
    crop: '作物',
    noModules: '该农场尚未登记任何区域。',
    legend: '区域状态',
    lastClosedCycle: '上一个已关闭周期',
    status: {
      PREPARACION: '准备中',
      NORMAL: '正常',
      ATENCION: '需关注',
      CRITICO: '紧急',
      COSECHANDO: '采收中',
    },
    createModuleBtn: '创建区域',
    editLoteBtn: '编辑地块',
    newModuleTitle: '新建区域',
    editModuleTitle: '编辑区域',
    editModuleBtn: '编辑',
    saveModuleBtn: '保存',
    updateModuleError: '无法保存区域',
    fieldModuleCode: '编号',
    fieldModuleCodePlaceholder: '例如 M-01',
    fieldModuleCodeReadonlyHint: '区域创建后代码不可修改',
    fieldParentModule: '上级区域',
    attendanceZoneLabel: '可用于点名的地点',
    attendanceZoneHint: '可在此点名（入口、办公室、田间等）。未标记任何区域时使用地块。',
    fieldLoteReadonly: '地块',
    parentModuleNone: '无（顶层）',
    fieldModuleName: '名称',
    fieldCropName: '作物',
    fieldValves: '阀门',
    fieldPlantingDate: '种植日期',
    createModuleSuccess: '区域已创建',
    createModuleError: '无法创建区域',
    duplicateModuleCode: '该编号的区域已存在',
    selectLote: '选择地块',
    noProjectSelectedHint: '创建区域前请先选择活动项目/季节',
    farmModulesDisabledTooltip: '该农场未启用按区域细分',
    noPermissionHint: '您没有权限修改区域',
    deleteModuleBoundaryBtn: '删除地界',
    deleteModuleBtn: '删除区域',
    deleteModuleConfirm: '删除区域',
    deleteModuleHasChildren: '该区域有子区域，请先删除或移动子区域。',
    deleteModuleError: '无法删除区域。',
    newCropBtn: '+ 新作物',
    newCropPlaceholder: '作物名称',
  },

  map: {
    title:      '地块地图',
    loading:    '加载地块...',
    lotesCount: '地块',
    badgeGPS:   'GPS',
    badgePoly:  '地理围栏',
    badgeNoPoly: '无围栏',
    searchPlaceholder: '搜索地块、分区、编码、状态或作物',
    ranchoUnknown: '未命名牧场',
    collapseLote: '收起地块',
    expandLote: '展开地块',
    collapseSidebar: '收起地块面板',
    expandSidebar: '展开地块面板',
    drawBtn:    '绘制围栏',
    deleteBtn:  '删除围栏',
    saveBtn:    '保存围栏',
    saving:     '保存中...',
    cancelBtn:  '取消',
    saveError:  '保存围栏时出错',
    areaTooLargeError: '多边形面积过大（{area} 公顷）— 请检查绘制的点',
    inviteAfterGeofenceHint: '地块已设置好。要邀请人来负责这个农场吗？',
    inviteAfterGeofenceCta:  '邀请用户',
    drawHint:   '点击地图添加点',
    drawPoints: '个点',
    drawReady:  '可以保存',
    satellite:  '卫星图',
    fieldLatitude:   '纬度',
    fieldLongitude:  '经度',
    useMapPointBtn:  '在地图上选点',
    saveLocationBtn: '保存位置',
    discardTitle:   '放弃更改？',
    discardBody:    '绘制的图形尚未保存，现在离开将会丢失。',
    discardConfirm: '放弃',
    discardCancel:  '继续编辑',
    dateSliderLabel: '日期',
    ndviLayerToggle: 'NDVI图层',
    ndviDisabled: '此牧场未启用NDVI；请联系管理员启用。',
    ndviSceneDate: '最近的卫星影像',
    fillThemeLabel: '填充',
    fillThemeStatus: '状态',
    fillThemeNdvi: 'NDVI',
    fillThemeStage: '物候期',
    fillThemeCustom: '自选颜色',
    stageNoData: '未记录物候期',
    labelsToggle: '标签',
    labelFieldName: '名称',
    labelFieldCrop: '作物',
    labelFieldHectares: '公顷',
    labelFieldCode: '编号',
    ndviHigh: 'NDVI 高（≥ 0.5）',
    ndviMid: 'NDVI 中（0.3–0.5）',
    ndviLow: 'NDVI 低（< 0.3）',
    ndviNoData: '无数据',
    ndviScale: 'NDVI 色阶',
    ndviScaleFixed: '固定',
    ndviScaleDynamic: '动态',
    ndviRangeMin: 'NDVI 最小 {min}',
    ndviRangeMax: 'NDVI 最大 {max}',
  },

  chat: {
    title:            'Agrovid 助手',
    subtitle:         '只读 · 查询您的农场数据',
    clearBtn:         '清除对话',
    welcomeTitle:     '您好，我是 Agrovid 助手',
    welcomeBody:      '请向我询问您的地块、活动、任务和成本。',
    noRanchoNotice:   '当前没有选择牧场：回答可能涵盖您所有的牧场。建议在牧场选择器中选择一个。',
    inputPlaceholder: '输入您的问题...（Enter发送）',
    sendBtn:          '发送',
    errorGeneric:     '联系助手时出错，请重试。',
    fallbackWebHint:  '这个功能在：',
    fallbackWebGo:    '前往 {label}',
    feedbackUp:       '回答有帮助',
    feedbackDown:     '回答没有帮助',
    feedbackSaved:    '已保存反馈',
    fallbackBotHint:  '这个通过 Telegram 登记 — 在机器人里试试 {command}。',
    sidebar: {
      newChat:       '新对话',
      empty:         '没有保存的对话',
      rename:        '重命名',
      delete:        '删除',
      deleteConfirmTitle: '删除此对话？',
      deleteConfirmBody:  '此操作无法撤销。',
      cancel:        '取消',
      confirmDelete: '删除',
      toggle:        '对话列表',
    },
    suggestions: {
      s1: '我有多少个活跃的地块？',
      s2: '最近一次记录的作业是什么？',
      s3: '今天有哪些待办任务？',
      s4: '今天的累计成本是多少？',
    },
    fallbackLabels: {
      reports: '报表',
      sales: '销售与价格',
      logistics: '物流',
      traceability: '溯源',
      devices: '设备',
      permissions: '权限矩阵',
      inventory: '库存',
      tasks: '任务',
    },
  },
  notifications: {
    bell:        '通知',
    panelTitle:  '最近活动',
    markAllRead: '全部标为已读',
    empty:       '过去24小时内无活动',
  },

  // ── TopBar ──────────────────────────────────────────────────────────────────
  topBar: {
    selectTenant:     '切换组织',
    tenants:          '组织',
    loading:          '加载中...',
    noTenants:        '未分配组织',
    switchTenantError: '切换组织出错',
    roleLabels: {
      SUPER_ADMIN:       '超级管理员',
      ADMIN:             '管理员',
      SUPERVISOR:        '田间主管',
      AGRONOMIST:        '农艺师',
      FIELD_WORKER:      '田间工人',
      BODEGUERO:         '仓库管理员',
      WAREHOUSE_MANAGER: '仓库管理员',
      AUDITOR:           '审计员',
      HR:           '人力资源',
      GUARD:           '保安',
      INVESTOR:          '投资人',
    },
    timeJustNow: '刚刚',
    timeMinutesAgo: '{n} 分钟前',
    timeHoursAgo: '{n} 小时前',
    timeDaysAgo: '{n} 天前',
    startTourTooltip: '查看教程',
    restartTourLabel: '再次查看教程',
    dayCloseTimeLabel: 'Telegram 日结提醒',
    assignedAccessLabel: '已分配权限',
    assignedRanchos: '农场',
    assignedLotes: '地块',
    assignedAll: '全部',
    dayCloseTimeHint: '到这个时间点，如果你今天还没有记录任何内容，我们会通过 Telegram 询问你是否要记录一些内容',
    dayCloseTimeDisable: '关闭',
    telegramLink: {
      title:          'Telegram',
      linked:         '已绑定 Telegram',
      notLinked:      '未绑定 Telegram',
      link:           '绑定 Telegram',
      openTelegram:   '在 Telegram 中打开',
      waiting:        '请在 Telegram 中打开链接并确认绑定。等待确认中...',
      expired:        '链接已过期且未确认。请重新生成链接再试一次。',
      unavailable:    '暂时无法绑定 Telegram，请稍后再试。',
      unlink:         '解除绑定',
      unlinkConfirm:  '确定要解除你账户的 Telegram 绑定吗？',
      unlinkConfirmBtn: '是，解除绑定',
      cancel:         '取消',
    },
  },

  reports: {
    title:               'PDF报告',
    subtitle:            '生成并下载PDF格式的系统报告。文件可用24小时。',
    loading:             '加载报告类型...',
    generateBtn:         '生成PDF',
    generating:          '生成中...',
    generateError:       '生成报告时出错',
    readyTitle:          '报告已就绪',
    downloadBtn:         '下载PDF',
    expiresNote:         '可用24小时',
    selectLote:          '选择地块',
    selectLotePlaceholder: '— 选择地块 —',
    badgeTenant:         '全农场',
    badgeLote:           '按地块',
    recentTitle:         '最近报告',
    noRecent:            '尚未生成任何报告。',
    colName:             '名称',
    colType:             '类型',
    colDate:             '日期',
    scheduledTitle:      '定期报告',
    noScheduled:         '暂无定期报告。',
    frequencyWEEKLY:     '每周',
    frequencyMONTHLY:    '每月',
    nextRun:             '下次运行',
    lastRun:             '上次运行',
    colFrequency:        '频率',
    createSchedule:      '安排报告',
    deleteSchedule:      '删除',
    scheduleTypeLabel:   '报告类型',
    scheduleFrequencyLabel: '频率',
    scheduleCreateBtn:   '创建',
    pushToTelegramLabel: '通过 Telegram 发送给管理员',
    pushToTelegramHint:  '农场的管理员、主管和农艺师每周都会通过 Telegram 收到该 PDF。',
    scheduleCreateError: '无法创建定期报告。',
    scheduleDeleteError: '无法删除定期报告。',
  },

  sales: {
    title:          '销售与价格',
    newOrder:       '新订单',
    newOrderNoPermission: '您没有创建销售订单的权限',
    newCustomer: '+ 新建客户',
    newCustomerNoPermission: '您没有创建客户的权限',
    newCustomerTitle: '新建客户',
    customerName: '名称',
    customerCountry: '国家',
    customerContact: '联系人',
    customerEmail: '邮箱',
    customerPhone: '电话',
    customerTermsDays: '账期（天）',
    customerCreateError: '无法创建客户，请检查字段。',
    customerInvalidEmail: '邮箱无效',
    noCustomers: '暂无客户，请点击“+ 新建客户”创建。',
    customer:       '客户',
    crop:           '作物',
    grade:          '等级',
    qty:            '数量',
    targetPrice:    '目标价格',
    lote:           '地块',
    loteOptional:   '不指定地块',
    stockNotice:    (over: number, lote: string) => `提醒：本次销售超出地块 ${lote} 可用库存 ${over} 公斤。销售已保存。`,
    unitPrice:      '实际价格',
    variance:       '差异',
    status:         '状态',
    total:          '总计',
    statusDraft:     '草稿',
    statusConfirmed: '已确认',
    statusFulfilled: '已完成',
    statusCancelled: '已取消',
    createError:    '无法创建订单，请检查字段。',
    kpiRevenue: '总销售额',
    kpiOrders: '订单数',
    kpiAvgPrice: '加权平均价格',
    kpiAvgVariance: '平均差异',
    kpiTopCustomer: '主要客户',
    kpiTopCrop: '主要作物',
    priceHistoryTitle: '按作物划分的历史价格',
    priceHistorySubtitle: '根据已登记订单得出的加权月平均值。',
    priceRangeTitle: '预计价格区间',
    priceRange: '观察到的区间',
    performanceTitle: '按客户划分的销售业绩',
  },

  harvest: {
    title:           '收获',
    selectLot:       '选择地块',
    kpiTotalKg:      '总收获量 (公斤)',
    kpiPackout:      '平均出货率',
    kpiPackoutSub:   'A级 / 总量',
    kpiCycles:       '记录周期数',
    kpiCostPerKg:    '成本/公斤',
    kpiCostPerKgSub: '墨西哥比索/公斤',
    chartByLot:      '各地块公斤数',
    chartGradeBreakdown: '等级分布（最近20次）',
    chartGradeA:     'A级',
    chartGradeB:     'B级',
    chartGradeC:     'C级',
    tableTitle:      '收获历史',
    tableLot:        '地块',
    tableCycle:      '周期',
    tableDate:       '日期',
    tableKg:         '总公斤',
    tableGradeA:     'A级 (公斤)',
    tablePackout:    '出货率 %',
    tableCostPerKg:  '成本/公斤',
    tableRecordedBy: '记录人',
    noHarvests:      '暂无收获记录。',
    loadingHarvests: '加载收获数据中...',
    // 损耗 / 废弃
    mermaTitle:           '损耗 / 废品',
    mermaKpiRejectedKg:   '总拒绝量（公斤）',
    mermaKpiImpact:       '经济影响',
    mermaKpiRecords:      '记录事件数',
    mermaTableDate:       '日期',
    mermaTableReason:     '原因',
    mermaTableKg:         '拒绝公斤',
    mermaTableBoxes:      '箱数',
    mermaTableLabel:      '标签',
    mermaTableImpact:     '影响（$）',
    mermaTableStatus:     '状态',
    mermaNoRecords:       '暂无损耗记录。',
    mermaLoading:         '加载损耗数据中...',
    mermaReasonCLIMA:           '气候损害',
    mermaReasonPLAGA:           '病虫害损害',
    mermaReasonMECANICO:        '机械损害',
    mermaReasonRECHAZO_CALIDAD: '品质/规格拒收',
    mermaReasonPUDRICION:       '腐烂变质',
    mermaReasonOTRO:            '其他原因',
    mermaChartTitle:      '各原因损耗（公斤）',
    newHarvestBtn:      '+ 登记收获',
    newHarvestTitle:    '登记收获/包装',
    fieldLoteId:        '地块 *',
    fieldLoteZone:      '地块或区域',
    fieldBatchId:       '周期 (batchId) *',
    fieldContainerCode: '集装箱编号',
    fieldBoxesTitle:    '箱子',
    fieldCalibre:       '规格',
    fieldBoxCount:      '箱数',
    fieldWeightKg:      '重量 (kg)',
    fieldGrade:         '等级',
    addBoxRow:          '+ 添加行',
    removeBoxRow:       '移除',
    fieldNotes:         '备注',
    loteRequired:       '地块为必填项',
    batchRequired:      '周期 (batchId) 为必填项',
    boxesRequired:      '请至少添加一行箱子信息',
    createError:        '登记收获时出错',
    create:             '登记',
  },

  inventory: {
    title:          '库存',
    tabStock:       '库存',
    tabMovements:   '出入库记录',
    selectWarehouse: '选择仓库',
    warehouseShared: '共享（全公司）',
    lowStockAlert:  '低库存商品',
    tableProduct:   '产品',
    tableCategory:  '类别',
    tableQuantity:  '数量',
    tableUnit:      '单位',
    tableExpiry:    '到期日',
    tableCost:      '成本',
    tableDate:      '日期',
    tableType:      '类型',
    tableReason:    '原因',
    tableRef:       '参考',
    tableLote: '地块',
    tableEquipment: '设备',
    tableRecipient: '领用人',
    tableUser: '用户',
    productDetailTitle: '产品详情',
    productDetailTabSummary: '概览',
    productDetailTabMovements: '出入库记录',
    productDetailClose: '关闭',
    productDetailOpen: '查看详情',
    productDetailNoMovements: '该产品暂无出入库记录。',
    productDetailLoading: '正在加载出入库记录...',
    productDetailError: '无法加载出入库记录。',
    productDetailCurrentStock: '当前库存',
    productDetailMovementCount: '已记录的出入库',
    productDetailLastMovement: '最近一次出入库',
    noStock:        '库存中无产品。',
    noMovements:    '暂无出入库记录。',
    loadingStock:   '加载库存中...',
    loadingMovements: '加载记录中...',
    movementTypes: {
      ENTRY:      '入库',
      EXIT:       '出库',
      RETURN:     '退货',
      ADJUSTMENT: '调整',
      EXPIRY:     '过期',
    },
    reasonLabels: {
      PURCHASE:   '采购',
      FIELD_USE:  '田间使用',
      RETURN:     '退货',
      ADJUSTMENT: '调整',
      EXPIRY:     '过期',
    },
    stockLow:     '库存不足',
    stockExpiring: '即将过期',
    stockOk:      'OK',
    kpiTotalValue:    '仓库总价值',
    kpiLowStock:      '低于最低库存的产品',
    kpiExpiringSoon:  '即将过期（30天）',
    // ── 目录（产品/物资标签页）──────────────────────────────────
    tabCatalog:         '目录',
    catalogTitle:       '产品/物资目录',
    productSingular:    '产品',
    productPlural:      '产品',
    allCategories:      '所有类别',
    loadingCatalog:     '加载目录中...',
    noProductsPrefix:   '暂无已注册产品',
    noProductsFilterSuffix: '（此类别）',
    noProductsSuffix:   '。',
    addFirstProduct:    '添加第一个产品',
    newProduct:         '新产品',
    editProductTitlePrefix: '编辑 — ',
    newProductDialogTitle: '新产品/物资',
    createError:        '创建产品出错',
    updateError:        '更新产品出错',
    nameRequired:       '名称为必填项',
    invalidCost:        '单位成本无效',
    fieldName:          '名称 *',
    fieldNamePlaceholder: '阿维菌素、诺瓦特克...',
    fieldCategory:      '类别 *',
    fieldUnit:          '单位 *',
    fieldUnitCost:      '单位成本 ($) *',
    fieldUnitCostPlaceholder: '0.00',
    fieldMinStock:      '最低库存',
    fieldMinStockPlaceholder: '10',
    fieldActiveIngredient: '有效成分',
    fieldActiveIngredientPlaceholder: '阿维菌素 18g/L...',
    fieldSenasica:      'SENASICA 注册号',
    fieldSenasicaPlaceholder: 'SENASICA-12345',
    fieldDescription:   '描述',
    fieldDescriptionPlaceholder: '附加备注...',
    fieldNotifyUsers:    '通知（用户）',
    fieldNotifyUsersHint: '库存低于最低值时，这些用户会收到 Telegram 消息。',
    fieldNotifyUsersPlaceholder: '选择用户',
    fieldNotifyRoles:    '通知（角色）',
    fieldNotifyRolesPlaceholder: '选择角色',
    cancel:             '取消',
    saveChanges:        '保存更改',
    createProduct:      '创建产品',
    deleteProductTitle: '删除产品',
    deleteProductConfirmPrefix: '删除 ',
    deleteProductConfirmSuffix: '？此操作无法撤销。如果仓库中有活跃库存，可能会影响报告。',
    delete:             '删除',
    colName:            '名称',
    colCategory:        '类别',
    colUnit:            '单位',
    colUnitCost:        '单位成本',
    colMinStock:        '最低库存',
    colActiveIngredient: '有效成分',
    colImage:           '图片',
    productImage:       '产品图片',
    uploadImage:        '上传图片',
    imageError:         '无法上传图片，请重试。',
    colSenasica:        'SENASICA',
    colActions:         '操作',
    catAgroquimico:     '🧪 化学品',
    catFertilizante:    '🌱 肥料',
    catSemilla:         '🌾 种子',
    catCombustible:     '⛽ 燃料',
    catOtro:            '📦 其他',
    unitLitros:         '升',
    unitKg:             '千克',
    unitPiezas:         '件',
    unitToneladas:      '吨',
    unitCajas:          '箱',
    newMovement:            '新增出入库',
    newMovementDialogTitle: '登记入库 / 出库',
    fieldMovementType:      '类型 *',
    movementTypeIn:         '入库',
    movementTypeOut:        '出库',
    fieldProduct:           '产品 *',
    selectProduct:          '选择产品',
    fieldQty:               '数量 *',
    fieldQtyPlaceholder:    '0',
    fieldReason:            '原因 *',
    fieldReasonPlaceholder: '供应商采购、第4号地块施用...',
    fieldExpiryDate:        '到期日期',
    fieldLoteNumber:        '生产批号',
    fieldLoteNumberPlaceholder: 'L-2026-001',
    productRequired:        '请选择产品',
    qtyRequired:            '数量必须大于0',
    reasonRequired:         '原因为必填项',
    createMovementError:    '登记出入库时出错',
    registerMovement:       '登记',
    transferButton:         '调拨',
    transferDialogTitle:    '仓库间调拨',
    fieldFromWarehouse:     '源仓库',
    fieldToWarehouse:       '目标仓库',
    fieldTransferNote:      '备注',
    fieldTransferNotePlaceholder: '调拨原因或参考',
    transferWarehouseRequired: '请选择仓库',
    transferSameWarehouse:  '源仓库和目标仓库必须不同',
    transferNeedTwoWarehouses: '至少需要两个仓库才能调拨',
    transferSubmit:         '调拨',
    transferError:          '登记调拨时出错',
    // ── 负库存警报队列 ──────────────────────────────────────────────────────
    tabAlerts:              '警报',
    tabTransfers: "调拨",
    transfersColDate: "日期",
    transfersColOrigin: "来源",
    transfersColDestination: "目的地",
    transfersColProduct: "产品",
    transfersColQty: "数量",
    transfersColBy: "操作人",
    transfersBadgeReversed: "已撤销",
    transfersBadgeReverse: "撤销单",
    transfersRefresh: "刷新",
    transfersLoadMore: "加载更多",
    transfersEmpty: "暂无调拨记录。",
    transfersLoading: "正在加载调拨...",
    transfersLoadError: "无法加载调拨记录。",
    noAlerts:               '没有负库存警报。',
    alertSince:             '发生于',
    resolveAlert:           '解决',
    ignoreAlert:            '忽略',
    ignoreAllAlerts:        '全部忽略',
    ignoreDialogTitle:      '忽略警报',
    ignoreAllDialogTitle:   '忽略所有警报',
    ignoreReasonLabel:      '原因',
    ignoreReasonPlaceholder: '例如：原因已知，已核实实际库存...',
    ignoreAllConfirmWarehouse: '忽略此仓库的所有警报吗？',
    ignoreAllConfirmTenant: '忽略整个公司的所有警报吗？',
    confirmIgnore:          '忽略',
    // ── 产品诊断面板 ────────────────────────────────────────────────────────
    diagnosticsTitle:       '库存诊断',
    deleteMovement:         '删除',
    editMovement:           '编辑',
    editMovementDialogTitle: '编辑出入库记录',
    confirmDeleteMovement:  '删除此出入库记录？库存将自动重新计算。',
    systemMovementLocked:   '自动生成',
    systemMovementLockedTooltip: '此出入库记录由田间作业自动生成，无法编辑或删除。',
  },

  settings: {
    title:        '设置',
    usersTitle:   '设置 — 用户',
    noPermission: '您没有访问此部分的权限。',
    loadingUsers: '加载用户中...',
    languageTitle: '系统语言',
    languageLabel: '语言',
    languageEs:   'Español',
    languageEn:   'English',
    languageZh:   '中文',
    ranchoTitle:      '农场配置',
    ranchoName:       '农场名称',
    ranchoLocation:   '位置',
    ranchoSave:       '保存更改',
    ranchoSaved:      '更改已保存',
    behaviorTitle:    '系统行为',
    warehouseMode:    '养殖仓库模式',
    warehouseModeDesc: '配置养殖批次使用共享、独立还是两种仓库。',
    warehouseShared:  '共享',
    warehouseSeparate: '独立',
    warehouseBoth:    '两者',
    reportMode:       '养殖报告模式',
    reportModeDesc:   '定义每周养殖报告是整合到农场报告还是单独发送。',
    reportIntegrated: '整合',
    reportSeparate:   '独立',
    budgetBlock:      '预算锁定',
    budgetBlockDesc:  '达到100%时软锁定 — 需要管理员授权。',
    budgetBlockSoft:  '软锁（请求授权）',
    budgetBlockHard:  '硬锁（始终锁定）',
    // ── AI 提供商 ──────────────────────────────────────────────────────────────
    aiProvidersTitle:    'AI 提供商（田间机器人）',
    aiProvidersDesc:     '当某个 AI 提供商失败时的备用顺序。已禁用的提供商将被完全跳过。',
    aiPresetLabel:       '优先级',
    aiPresetCost:        '成本优先（免费优先）',
    aiPresetSpeed:       '速度优先（质量优先）',
    aiPresetCustom:      '自定义',
    aiProviderEnabled:   '已启用',
    aiMoveUp:            '上移',
    aiMoveDown:          '下移',
    aiProvidersSave:     '保存顺序',
    aiProvidersSaved:    '顺序已保存',
    // ── 管理配置 — 动态标签页 (阶段 3.2-3.4) ────────────────────────────────
    adminConfig: {
      tabProviders: 'AI 提供商', tabScouting: '田间侦察', tabBudget: '预算',
      tabMachinery: '机械', tabHarvest: '收获', tabLocalization: '本地化',
      tabBotBehavior: '机器人行为', tabSchedules: '日程', tabCompliance: '合规',
      tabFeatureFlags: '功能开关', tabScope: '按牧场的范围', tabInventory: '库存与仓库', tabCashflow: '现金流与异常', tabAgromonitoring: 'Agromonitoring (NDVI)', tabInvoicing: '电子发票（CFDI）', tabSystem: '系统', tabAgroModules: '系统模块',
      tabSelfRegister: '自助注册',
      tabRoutines: '例程',
      tabGateRules: '门岗规则',
      routinesTitle: '自动例程',
      routinesDesc: '系统在贵公司固定时间自动执行的任务。在您启用之前全部处于关闭状态。',
      routinesEmpty: '暂无可用例程。',
      routinesColRoutine: '例程', routinesColSchedule: '时间', routinesColEnabled: '启用', routinesColLastRun: '最近运行',
      routinesNever: '从未',
      routinesScheduleDaily: '每天 {hour}:00',
      routinesScheduleWeekly: '{day} {hour}:00',
      routinesDay1: '星期一', routinesDay2: '星期二', routinesDay3: '星期三', routinesDay4: '星期四',
      routinesDay5: '星期五', routinesDay6: '星期六', routinesDay7: '星期日',
      routinesLogTitle: '运行记录',
      routinesLogEmpty: '尚无运行记录。',
      routinesColDate: '日期', routinesColCompany: '公司', routinesColResult: '结果', routinesColError: '错误',
      routinesStatusRUNNING: '运行中', routinesStatusOK: '成功', routinesStatusERROR: '出错',
      routinesStatusSKIPPED: '无变化', routinesStatusAWAITING_CONFIRMATION: '等待确认',
      routinesStatusCONFIRMED: '已确认', routinesStatusCANCELLED: '已取消', routinesStatusEXPIRED: '已过期',
      routinesToggleError: '无法更改例程，请重试。',
      groupScope: '牧场与范围', groupInventory: '库存与仓库', groupFinance: '财务', groupIntegrations: '集成',
      groupField: '田间作业与成本', groupBot: 'AI 与机器人', groupSystem: '系统与访问', groupsLabel: '配置分组',
      pageTitle: '设置',
      systemInfoTitle: '系统信息',
      backendLabel: '后端', frontendLabel: '前端',
      botBehaviorDesc: '响应、超时、重试和日志的配置。',
      schedulesDesc: '警报、预测快照、会话过期及其他定时任务。',
      complianceDesc: 'PHI/REI/MRL 规则、限制窗口和收获安全警报。',
      featureFlagsDesc: '启用/禁用系统模块和功能。',
      saveButton: '保存', savedBadge: '已保存',
      scoutingTitle: '虫害严重程度阈值',
      scoutingLowLabel: '低严重度 (%)', scoutingLowDesc: '标记为“低”所需的最小损害百分比',
      scoutingMediumLabel: '中严重度 (%)', scoutingMediumDesc: '标记为“中”所需的最小损害百分比',
      scoutingHighLabel: '高严重度 (%)', scoutingHighDesc: '标记为“高”所需的最小损害百分比',
      budgetTitle: '预算阈值',
      budgetWarnLabel: '警告 (%)', budgetWarnDesc: '触发黄色警报的消耗百分比',
      budgetBlockLabel: '阻止 (%)', budgetBlockDesc: '触发严重警报的消耗百分比',
      budgetModeLabel: '模式', budgetModeDesc: '超出预算时的处理方式',
      machineryTitle: '机械警报',
      machineryYellowLabel: '黄色警报 (%)', machineryYellowDesc: '触发警报的预算百分比',
      machineryOrangeLabel: '橙色警报 (%)', machineryOrangeDesc: '触发警报的预算百分比',
      machineryRedLabel: '红色警报 (%)', machineryRedDesc: '触发警报的预算百分比',
      harvestTitle: '收获阈值',
      harvestGoodLabel: '良好包装率 (%)', harvestGoodDesc: '视为良好的包装率下限',
      harvestWarnLabel: '警告包装率 (%)', harvestWarnDesc: '低于此值标记为警告',
      harvestBadLabel: '严重包装率 (%)', harvestBadDesc: '低于此值标记为严重',
      harvestGradesLabel: '等级标签', harvestGradesDesc: '以逗号分隔（例如 A,B,C）',
      localizationTitle: '区域设置',
      currencyLabel: '货币', currencyDesc: '成本和价格所使用的货币',
      timezoneLabel: '时区', timezoneDesc: '定义提醒中“今天”的含义',
      defaultLangLabel: '默认语言', defaultLangDesc: '供未设置个人语言的用户使用的公司语言',
      phonePrefixLabel: '电话前缀', phonePrefixDesc: '例如墨西哥为 +52',
      workdayHoursLabel: '工作日小时数', workdayHoursDesc: '完整工作日的小时数',
      dateFormatLabel: '日期格式', dateFormatDesc: '显示日期的格式（dd/MM/yyyy、MM/dd/yyyy 或 yyyy-MM-dd）。',
      decimalSeparatorLabel: '小数分隔符', decimalSeparatorDesc: '数字中小数位的字符（点或逗号）。',
      speedUnitLabel: '速度单位', speedUnitDesc: '机械和运输中的速度单位（km/h 或 mi/h）。',
      botBehaviorTitle: '机器人行为',
      scheduledNoticeEnabledLabel: '定时任务通知', scheduledNoticeEnabledDesc: '关闭后，定时通知将等待，重新开启后发送',
      scheduledNoticeIntervalLabel: '定时通知检查间隔（分钟）', scheduledNoticeIntervalDesc: '检查频率，单位分钟（5 的倍数）',
      reminderHourLabel: '每日提醒时间', reminderHourDesc: '发送次日任务提醒的当地时间',
      schedulesTitle: '预测日程',
      spread7Label: '7天浮动范围 (±%)', spread7Desc: '7天预测中保守与理想情景的浮动范围',
      spread15Label: '15天浮动范围 (±%)', spread15Desc: '15天预测中保守与理想情景的浮动范围',
      spread60Label: '60天浮动范围 (±%)', spread60Desc: '60天预测中保守与理想情景的浮动范围',
      confidence7Label: '7天置信度 (%)', confidence7Desc: '7天预测所报告的置信度',
      confidence15Label: '15天置信度 (%)', confidence15Desc: '15天预测所报告的置信度',
      confidence60Label: '60天置信度 (%)', confidence60Desc: '60天预测所报告的置信度',
      complianceTitle: '冷链',
      coldMinLabel: '最低温度 (°C)', coldMinDesc: '运输途中允许的最低温度，低于此值记为偏离',
      coldMaxLabel: '最高温度 (°C)', coldMaxDesc: '运输途中允许的最高温度，高于此值记为偏离',
      featureFlagsTitle: '功能开关',
      trackingEnabledLabel: '货运跟踪', trackingEnabledDesc: '启用基于遥测的跟踪（路线与温度）',
      autoDeductLabel: '自动仓库扣减', autoDeductDesc: '确认作业后自动从库存中扣减',
      negativeStockIgnoreScopeLabel: '"全部忽略"范围（负库存）', negativeStockIgnoreScopeDesc: '在库存警报队列中忽略所有负库存警报时的范围：仅当前仓库，还是整个公司。',
      inventoryScopeByRanchoLabel: '按当前牧场显示库存', inventoryScopeByRanchoDesc: '按所选牧场筛选仓库和产品。关闭：显示整个公司。未分配牧场的记录始终可见。',
      machineryScopeByRanchoLabel: '按当前牧场显示机械', machineryScopeByRanchoDesc: '按所选牧场筛选机械。关闭：显示整个公司。未分配牧场的设备始终可见。',
      invoicingEnabledLabel: '电子发票（CFDI）', invoicingEnabledDesc: '允许机器人通过Facty开具CFDI 4.0发票。默认关闭。与Facty的连接需另行配置。',
      invoicingRfcLabel: '开票方RFC', invoicingRfcDesc: '开具发票的公司RFC（12或13位）。为空则阻止开票。',
      invoicingRegimenLabel: '开票方税务制度', invoicingRegimenDesc: 'SAT代码，3位数字（如601）。为空则阻止开票。',
      invoicingModeLabel: '开票模式', invoicingModeDesc: '沙盒用于测试；仅在与Facty连接验证后使用生产模式。',
      factyCredentialsTitle: 'Facty连接', factyCredentialsConfigured: '连接已配置', factyCredentialsMissing: '未连接：开票已被阻止', factyOrgLabel: 'Facty组织（org）', factyApiKeyLabel: 'Facty API密钥', factyCredentialsSave: '保存连接', factyCredentialsRemove: '移除连接', factyCredentialsError: '保存失败，请检查后重试。',
      ndviEnabledLabel: 'NDVI卫星影像', ndviEnabledDesc: '启用NDVI模块（Agromonitoring卫星影像，消耗套餐配额）。默认关闭。每个牧场可覆盖此设置。',
      ndviRanchosTitle: '按牧场设置NDVI', ndviRanchosDesc: '覆盖公司设置。勾选 = 该牧场启用NDVI。',
      mrlMissingDoseLabel: '无剂量记录时采收（MRL）', mrlMissingDoseDesc: '当农药施用记录没有剂量时，MRL无法评估。阻止：在登记剂量之前停止采收。警告（默认）：仅显示警告。PHI和REI始终阻止。',
      mrlMissingDoseBlock: '阻止采收', mrlMissingDoseWarn: '仅警告',
      mrlMissingDoseRanchosTitle: '按牧场设置采收剂量规则', mrlMissingDoseRanchosDesc: '覆盖公司设置。阻止是按牧场的决定：除非该牧场需要，否则保持"仅警告"。',
      anomalyAutoLabel: '自动异常检测', anomalyAutoDesc: '检测到计划偏差时自动生成异常记录',
      cashflowBudgetLabel: '现金流中的预算', cashflowBudgetDesc: '将周期预算作为支出计入现金流预测',
      cashflowForecastLabel: '现金流中的预测', cashflowForecastDesc: '将预测收获作为未来收入计入',
      useFarmModulesLabel: '使用区域细分', useFarmModulesDesc: '如果禁用，农场将仅在地块级别进行管理',
      agroModulesTab: '系统模块',
      agroModulesHelp: '为此企业启用或禁用机器人的功能模块。',
      agroModulesHelpWithCompany: '为 {company} 启用或禁用机器人的功能模块。',
      notifyDisabledSwitch: '不使用AI通知已禁用的模块',
      saveAgroModules: '保存模块',
      selfRegisterTitle: '自助注册链接',
      selfRegisterDesc: '生成带唯一代码的链接，让农艺师或企业负责人无需单独邀请即可创建自己的账户。',
      generateSelfRegisterLink: '生成自助注册链接',
      regenerateSelfRegisterLink: '重新生成链接',
      selfRegisterLinkHint: '分享此链接 —— 一旦生成新代码，旧代码将立即失效：',
      selfRegisterCopyLink: '复制链接',
      selfRegisterCopied: '✓ 已复制',
      selfRegisterError: '无法生成自助注册链接',
    },
    roles: {
      SUPER_ADMIN:       '超级管理员',
      ADMIN:             '管理员',
      AGRONOMIST:        '农学家',
      SUPERVISOR:        '主管',
      WAREHOUSE_MANAGER: '仓库管理员',
      FIELD_WORKER:      '田间工人',
      AUDITOR:           '审计员',
      HR:           '人力资源',
      GUARD:           '保安',
      INVESTOR:          '投资人',
    },
    systemTab:        '系统',
    systemStatus:     '系统状态',
    checking:         '检查中...',
    refresh:          '刷新',
    checkingProviders: '正在检查供应商...',
    aiProviders:      'AI 供应商',
    telegramBots:     'Telegram 机器人',
    rotateTokenTitle: '轮换 {name}',
    rotateTokenHint: '粘贴新值。它会保存在服务器上（之后不再显示），所有实例生效可能需要几分钟。现场机器人会向 Telegram 验证并重新注册 webhook。',
    rotateTokenLabel: '新值',
    rotateTokenSubmit: '轮换',
    rotateTokenOk: '密钥已轮换。',
    rotateTokenError: '无法轮换密钥。',
    rotateTokenBtn: '轮换',
    rotateTokenOkBot: '令牌已轮换。机器人 {bot} 已重新注册 webhook。',
    zoneCoordsTitle: '区域位置',
    zoneCoordsHint: '为有边界但没有坐标的地块和区域计算纬度和经度（边界中心）。用于精确定位和天气。不会更改已有坐标。',
    zoneCoordsBtn: '计算缺失的坐标',
    zoneCoordsRunning: '计算中…',
    zoneCoordsDone: '完成：已更新 {lotes} 个地块和 {modules} 个区域（{noBoundary} 个没有边界）。',
    zoneCoordsNothing: '无需计算：所有有边界的区域都已有坐标（{noBoundary} 个没有边界）。',
    zoneCoordsError: '无法计算坐标。',
    secretsTitle: '密钥与令牌',
    secretsHint: 'AI 密钥、现场机器人和 GitHub。不会显示具体值。',
    secretConfigured: '已配置',
    secretNotConfigured: '未配置',
    frontendSection:  '前端（仪表板）',
    miniappSection:   '相机迷你应用',
    miniappLabel:     '版本',
    backendSection:   '后端（Lambda API）',
    checkingVersion:  '正在查询版本...',
    versionError:     '无法获取后端版本。',
    version:          '版本',
    cached:           '（缓存）',
    fieldBotLabel:    'API · 田间机器人',
    statusOk:            '正常',
    statusRateLimited:   '速率限制',
    statusNoCredits:     '无额度',
    statusNoWebhook:     '无 Webhook',
    statusPrivacyOn:     '隐私模式已开启',
    statusError:         '错误',
    statusUnconfigured:  '未配置',
    statusDisabled:      '已停用',
    statusTimeout:       '超时',
    aiUsageTitle:        '按模型统计用量',
    aiUsageDays7:        '最近 7 天',
    aiUsageDays30:       '最近 30 天',
    aiUsageShareTitle:   '各模型调用占比',
    aiUsageFailuresTitle: '按原因统计失败',
    aiUsageNoData:       '该时间段暂无用量记录。',
    aiUsageCalls:        '次调用',
    aiUsageOk:           '成功',
    aiUsageFailed:       '失败',
    aiUsageAvgLatency:   '平均延迟',
    aiUsageTokens:       '令牌（输入 / 输出）',
    aiUsageLoadError:    '无法加载 AI 用量。',
    chatFeedbackTitle:   '聊天评价（👍 / 👎）',
    chatFeedbackUp:      '有帮助（👍）',
    chatFeedbackDown:    '没有帮助（👎）',
    chatFeedbackFrom:    '开始',
    chatFeedbackTo:      '结束',
    chatFeedbackLoadError: '无法加载评价统计。',
    aiCause_timeout:     '超时',
    aiCause_rate_limited: '请求频率受限',
    aiCause_no_credits:  '额度/配额不足',
    aiCause_auth:        '认证失败',
    aiCause_server_error: '服务商错误',
    aiCause_other:       '其他',
    webhookActive:       'webhook 已激活',
    privacyOnHint:       '隐私模式已开启 — 请前往 BotFather 禁用',
    noWebhookRegistered: '未注册 webhook',
    pendingUpdates:      '待处理更新',
  },

  config: {
    title:                '系统配置',
    tabRancho:            '牧场',
    tabComportamiento:    '行为',
    tabProveedores:       'AI 提供商',
  },

  // ── 账户停用 ────────────────────────────────────────────────────────────────────
  accountInactive: {
    title: '账户已停用',
    message: '您的账户已被停用。请联系管理员了解更多信息。',
    backBtn: '返回登录',
  },

  // ── 系统页面 ────────────────────────────────────────────────────────────────────
  systemPage: {
    title: '系统配置',
    botBehavior: '机器人行为',
    botBehaviorDesc: '配置AI模型、响应风格、默认语言。',
    schedules: '日程和通知',
    schedulesDesc: '提醒时间、警报频率、活动窗口。',
    compliance: '合规性',
    complianceDesc: 'PHI/REI/MRL 防护等级、收获规则。',
    versioning: '版本管理',
    versioningDesc: '应用版本、构建信息、最近更改。',
    featureFlags: '功能标志',
    featureFlagsDesc: '实验性功能、测试版功能。',
    underConstruction: '⚠️ 页面正在构建中 — 敬请期待',
  },

  // ── 系统指南 ────────────────────────────────────────────────────────────────────
  a11yLabels: {
    telegram: 'Telegram',
    ndviImage: 'NDVI 图像',
    logoPreview: '徽标预览',
    close: '关闭',
  },
  sharedUi: {
    noAlerts: '暂无提醒',
  },

  systemGuide: {
    title: 'Agrovid 系统指南与帮助中心',
    subtitle: '了解如何使用 Telegram 对话机器人与 Web 控制台。',
    searchPlaceholder: '搜索模块、语音命令、植保规则或术语...',
    helpTooltip: '系统指南 / 帮助',
    openGuideBtn: '查看系统指南',
    tabs: {
      architecture: '系统如何工作',
      roles: '角色指南',
      modules: '模块说明',
      voiceCommands: 'Telegram 语音命令',
      faq: '常见问题与术语',
    },
    dualEntry: {
      telegramTitle: '📱 对话机器人 (田间)',
      telegramDesc: '田间工人可以通过 Telegram 发送自然语言语音、照片和文本，并经过 Draft Gate 确认后保存。',
      webTitle: '💻 Web 控制台',
      webDesc: '完整的管理控制台，按角色分配权限，用于目录管理、库存审批、双周薪资和 P&L 报表。',
      commonDbTitle: '⚙️ 所有信息集中一处',
      commonDbDesc: '两个渠道汇聚到同一个系统，数据始终安全、同步。',
    },
    copyBtn: '复制命令',
    content: {
      archTitle: '两种工作方式：田间（Telegram）+ 办公室（网页仪表板）',
      archIntro: 'Agrovid 提供两条独立的途径来记录和查询农场信息。两个渠道汇聚到同一处，因此信息始终保持一致且及时更新。',
      draftGateBefore: '包含',
      draftGateTerm: 'Draft Gate（草稿确认）',
      draftGateAsk: '机器人会询问',
      draftGateQuestion: '您确认登记吗？',
      draftGateAfter: '后再保存。',
      webHint: '📊 按牧场/地块的高级筛选、PDF/Excel 报表和交互式图表。',
      glossaryHeading: '农业术语与业务规则词汇表',
      glossaryQuestions: {
        PHI: '🧪 PHI（收获前间隔期）',
        REI: '🚫 REI（限制进入间隔期）',
        FEFO: '📦 FEFO（先到期先出）',
        Packout: '📊 Packout %（包装率）',
        DraftGate: '🛑 Draft Gate（机器人确认制动）',
      },
      phiBlockNote: '若 PHI 尚未到期，系统会自动阻止该地块的收获。',
      botLabel: '机器人：',
      mainActions: '主要操作：',
      cheatsheetIntroPre: '向 Telegram 机器人发送语音或自然语言文字。点击',
      cheatsheetIntroBtn: '复制',
      cheatsheetIntroPost: '以在机器人中试用这些句子：',
      voiceExamples: [
        { title: '🌾 植保施药', phrase: '在 3 号地块用拖拉机 01 施用了 25 升 Kumulus DF', note: '机器人会识别投入品、剂量和地块，并要求您确认。', role: '田间 / 农艺师' },
        { title: '💧 按小时灌溉', phrase: '在 1 号地块使用深井泵灌溉了 5 小时', note: '自动计算已用的毫米水深和立方米。', role: '田间' },
        { title: '📦 收获与包装率', phrase: '在 2 号地块包装了 80 箱 4kg 40 号 Hass 牛油果', note: '生成包装追溯单号并更新产量。', role: '田间 / 主管' },
        { title: '🚜 机械与工时表', phrase: '在 4 号地块用约翰迪尔拖拉机开始深松，工时表 2150', note: '开启机械会话，用于按公顷核算成本。', role: '田间 / 拖拉机手' },
        { title: '🐛 病虫害监测', phrase: '在 5 号地块检测到中等水平的蓟马，检查了 4 个点', note: '更新地块的植保状态并提醒农艺师。', role: '农艺师' },
        { title: '👥 考勤与计件', phrase: '1 号地块考勤 6 名工人，计件 50 筐', note: '记录进度，用于半月工资计算。', role: '主管' },
      ],
      modules: {
        'rancho-lote': { name: '牧场与地块管理', description: '农业牧场和地块的分级管理。面积与作物划定。', example: '登记 Slp 牧场地块 4' },
        operations: { name: '植保作业与农事', description: '语音/文字录入农事作业、施用投入品、剂量及 PHI/REI 校验。', example: '在 4 号地块用拖拉机 2 施用了 20 升 Kumulus' },
        irrigation: { name: '灌溉与水肥一体化', description: '按小时、井水立方米或水库毫米记录灌溉，计算水分效率和 N-P-K。', example: '在 1 号地块用 15kg 硝酸盐灌溉了 4 小时' },
        harvest: { name: '收获、包装与包装率', description: '按规格/等级记录收获箱数，关闭追溯单号，并记录产量 t/ha。', example: '在 3 号地块包装了 120 箱 4kg 40 号牛油果' },
        'crop-management': { name: '农艺监测与病虫害', description: '病虫害/疾病侦察、物候期、营养平衡及安全收获日期计算。', example: '在 2 号地块检测到轻度红蜘蛛，检查了 5 个采样点' },
        inventory: { name: '库存与仓库（FEFO）', description: '农药和肥料目录。发票入库、按 FEFO 出库派送和补货提醒。', example: '中央仓库入库 50 袋尿素' },
        machinery: { name: '机械与工时表', description: '机械作业计时、初始/结束工时表、柴油消耗及每公顷作业成本。', example: '在 2 号地块用拖拉机 01 开始耙地，工时表 1450' },
        payroll: { name: '工资与劳动力', description: '按日薪、计件（筐/箱）和合同方式记录考勤并进行半月结算。', example: '1 号地块登记 8 名计件工人 40 筐' },
        finance: { name: '财务与地块损益', description: '汇总直接成本（化学品、水、柴油、工资）与收获收入，计算每公顷利润率。', example: '查询 3 号地块的累计成本' },
        protocol: { name: '农艺方案上传', description: '上传并解析农艺文件/处方，用于规划本季植保施用。', example: '上传 2026 年牛油果植保方案' },
        merma: { name: '损耗与废果控制', description: '记录田间/包装环节被拒或损失的果实，并标注原因（风、病虫害、操作）。', example: '4 号地块因冰雹掉落 120kg 损耗果实' },
        export: { name: '出口物流', description: '国际运输管理、FDA/植检认证、封条及温度监控。', example: '用封条 44810 关闭集装箱 EXP-9901' },
        transport: { name: '国内运输', description: '创建出库运单、分配司机/车牌以及从田间到包装厂的运输。', example: '04 号车载 15 吨前往中央包装厂出发' },
        traceability: { name: '田间至包装追溯', description: '从地块、农药施用直至最终消费者的完整单号追踪。', example: '追溯单号 TR-2026-0044' },
        porcino: { name: '生猪育肥', description: '育肥猪场管理：仔猪入栏、称重、饲料、料肉比（FCR）及销售。', example: '登记 2 号猪舍育肥饲料消耗 500kg' },
        resources: { name: '资源与作业队', description: '机械、DJI Agras 无人机、采摘队和承包商的目录。', example: '安排 3 号作业队到 1 号地块收获' },
        drone: { name: '无人机施药作业', description: 'DJI Agras T40 无人机飞行记录：升/公顷、覆盖公顷数和飞行时间。', example: '5 号地块无人机 T40 飞行施用 100 升，覆盖 4 公顷' },
        calendar: { name: '日历与农事任务', description: '按地块和工人安排作业，并通过 Telegram 自动提醒。', example: '明天 8 点为 2 号地块创建灌溉任务，分配给 Juan' },
        weather: { name: '天气与 Open-Meteo 预报', description: '按地块地理位置查询气温、累计降水量和 7 天预报。', example: '本周 1 号地块的降雨预报如何？' },
        users: { name: '用户与预登记', description: '按手机号码录入员工、分配角色并绑定 Telegram。', example: '预登记用户 8112345678，角色 AGRONOMIST' },
        vocabulary: { name: '词汇与语音别名', description: '按公司的别名目录，用于修正语音转写错误（例如 "Kumulo" -> "Kumulus DF"）。', example: '为 Lote-04 添加别名 "lote cuatro"' },
        sales: { name: '商业周期与销售', description: '按收获记录销售订单、开票及客户收款。', example: '向 Frutas del Norte 客户销售 500 箱牛油果' },
        support: { name: '支持与机器人工单', description: '当 Telegram 机器人检测到错误或混淆时，自动生成技术支持工单。', example: '/soporte 机器人无法识别投入品 X' },
        developer: { name: '技术诊断', description: '用于查看系统整体状态的内部支持和监控工具。' },
      },
    },
    copied: '已复制！',
    filterRole: '按角色筛选：',
    allRoles: '所有角色',
    rolesList: {
      FIELD_WORKER: '田间工人',
      AGRONOMIST: '农艺师',
      SUPERVISOR: '田间主管',
      ADMIN: '管理员 / 经理',
      INVESTOR: '投资者 (只读)',
    },
    rolesDesc: {
      FIELD_WORKER: '通过 Telegram 语音记录施药、灌溉、采收批次和考勤。',
      AGRONOMIST: '监测病虫害、开具植保处方、验证安全采收期 (PHI) 并加载方案。',
      SUPERVISOR: '启动机械计时器、管理班组、申请库存并审计包装率。',
      ADMIN: '管理农场/地块、审批库存出库、审批双周薪资并审计财务。',
      INVESTOR: '管理层只读访问 P&L、生产进度、公顷产量 (t/ha) 和可追溯性。',
    },
    glossaryTitle: '农业术语表',
    glossary: {
      PHI: 'PHI (安全采收间隔): 施用农药后到安全采收所需的天数。',
      REI: 'REI (限制进入间隔): 工人重新进入施药地块前需等待的小时数。',
      FEFO: 'FEFO (先到期先出库): 优先出库临近过期物料的库存规则。',
      Packout: 'Packout %: 符合出口/包装质量标准的采收水果百分比。',
      DraftGate: 'Draft Gate: 机器人在保存记录之前询问”确认记录 X？”的确认步骤。',
    },
    cards: {
      architecture: {
        title: '🚀 Agrovid 如何运作',
        desc: '了解两种记录信息的方式：通过 Telegram 机器人语音录入，或在网页仪表板中操作。',
      },
      modules: {
        title: '📦 模块使用手册',
        desc: '灌溉、收获、植保、FEFO 库存、机械、工资和财务的分步操作手册。',
      },
      voice: {
        title: '🎙️ Telegram 语音指令',
        desc: '可复制的语音消息示例，发送给 Telegram 机器人（施药、灌溉、收获）。',
      },
      roles: {
        title: '👤 角色指南',
        desc: '了解工人、农艺师、主管和投资人的职责与关键页面。',
      },
      faq: {
        title: '❓ 术语表与业务规则',
        desc: '了解 PHI、REI、FEFO、Packout %、Draft Gate 的含义以及常见问题解答。',
      },
    },
  },

  // ── 全局组件 ────────────────────────────────────────────────────────────────────
  errorBoundary: {
    title: '出错了',
    message: '应用程序出现了意外错误。',
    reloadBtn: '重新加载页面',
  },

  protectedRoute: {
    title: '访问被拒绝',
    message: '您没有权限查看此部分。如果您认为这是一个错误，请联系管理员。',
    dashboardBtn: '返回仪表板',
  },

  demoGate: {
    loading: '加载演示中…',
    error: '无法启动演示会话。请验证服务器是否活跃。',
  },

  // ── 权限模块 ────────────────────────────────────────────────────────────────────
  permissionModules: {
    ASISTENCIA: '考勤',
    GESTIÓN: '管理',
    OPERACIONES: '运营',
    CAMPO: '田间',
    COSECHA: '收获',
    ANÁLISIS: '分析',
    SISTEMA: '系统',
  },

  permissionsMatrix: {
    roleEditHint: '点击单元格可为贵公司的角色授予或移除权限。每个人重新登录后生效。无法修改超级管理员。',
    saveRoles: '保存角色更改',
    discardRoles: '放弃',
    resetRoles: '恢复默认',
    rolesSaved: '角色权限已保存',
    rolesSaveError: '无法保存角色权限',
    rolesDirty: '有未保存的更改',
    title:        '权限矩阵',
    subtitle:     '按角色分配的权限。权限根据当前会话的激活角色生效。',
    noAccess:     '您没有权限访问此部分。',
    permissionCol: '权限',
    tabMatrix:    '按角色查看矩阵',
    tabOverrides: '按用户查看附加权限',
    allowed:      '已授权',
    denied:       '无权限',
    searchPlaceholder: '按姓名或电话搜索',
    permissions: {
      'users:read': '查看用户',
      'users:write': '编辑用户',
      'admin:ranchos': '管理农场',
      'admin:rbac': '管理权限',
      'admin:super': '超级管理员',
      'inventory:read': '查看库存',
      'inventory:write': '库存出入',
      'warehouse:transfer': '仓库间调拨',
      'payroll:read': '查看工资',
      'payroll:write': '编辑工资',
      'machinery:read': '查看机械',
      'lotes:read': '查看地块',
      'lotes:write': '编辑地块',
      'lotes:approve': '批准或删除地块记录',
      'tasks:write': '编辑任务',
      'tasks:manage': '管理任务（创建/编辑/删除）',
      'crop:write': '记录作物',
      'irrigation:write': '记录灌溉',
      'harvest:read': '查看采收',
      'harvest:write': '记录采收',
      'merma:read': '查看损耗',
      'finance:read': '查看财务',
      'finance:write': '编辑财务',
      'reports:read': '查看报表',
      'reports:configure': '配置报表',
      'audit:read': '查看审计日志',
      'vocabulary:write': '管理词汇',
      'config:write': '编辑配置',
      'attendance:read': '查看考勤',
      'attendance:take': '记录考勤',
      'attendance:enroll': '录入人脸',
      'attendance:policy': '配置考勤策略',
      'attendance:prebaja_decide': '处理预离职决定',
      'attendance:delete_biometrics': '删除生物识别数据',
      'attendance:gate': '使用门禁（通行证）',
      'attendance:gate_authorize': '授权已离职人员离场',
    },
    userNotFound: '未找到用户',
    saved: '权限已成功保存',
    saveError: '保存权限失败',
    userLabel: '用户',
    roleLabel: '角色',
    phoneLabel: '电话',
    rolePermsTitle: '角色权限',
    noBasePerms: '无基础权限',
    extraPermsTitle: '附加权限（可选）',
    saveChanges: '保存更改',
    searchPrompt: '搜索用户以分配附加权限',
  },

  porcino: {
    title:          '🐷 养猪场 — 育肥',
    corralTitle:    '猪舍',
    corralLoading:  '加载猪舍中...',
    corralEmpty:    '暂无猪舍。请使用“+ 新建猪舍”创建。',
    corralCapLabel: '容量',
    corralCapUnit:  '头',
    kpisLoading:    '加载KPI中...',
    noActiveLot:    '此猪舍无活跃批次。请使用“+ 登记入栏”登记猪的入栏。',
    eventsTitle:    '事件历史',
    eventsLoading:  '加载事件中...',
    eventsEmpty:    '无已记录事件',
    colDate:        '日期',
    colType:        '类型',
    colDetail:      '详情',
    colRecordedBy:  '记录人',
    detailCabezas:  '头',
    detailKgCab:    '千克/头',
    detailKgFood:   '千克饲料',
    detailCausa:    '原因',
    kpiHeads:       '当前头数',
    kpiDaysUnit:    '在栏天数',
    kpiEntryPrefix: '入栏',
    kpiWeight:      '平均体重',
    kpiGdp:         '日增重',
    kpiGdpSub:      '平均日增重',
    kpiFcr:         '饲料转化率',
    kpiFcrUnit:     '千克消耗',
    kpiMortality:   '死亡率',
    kpiMortalityUnit: '已记录损失',
    kpiCostKg:      '成本/活重千克',
    kpiCostKgSub:   '总成本 / 当前活重千克',
    statusInUse:    '使用中',
    statusActive:   '活跃',
    statusEmpty:    '空栏',
    statusInactive: '非活跃',
    eventoEntrada:  '🐷 入栏',
    eventoPesaje:   '⚖️ 称重',
    eventoAlimento: '🌽 喂食',
    eventoMortalidad: '💀 死亡',
    eventoMedicacion: '💊 用药',
    eventoVenta:    '💰 出售',
    newCorralBtn:      '+ 新建圈舍',
    newCorralTitle:    '新建圈舍',
    fieldRanchoId:     '牧场 *',
    searchRanchoPlaceholder: '搜索牧场...',
    fieldCorralName:   '名称 *',
    fieldCapacity:     '容量（头数）*',
    newEntradaBtn:     '+ 登记入栏',
    newEntradaTitle:   '登记批次入栏',
    fieldCabezas:      '头数 *',
    fieldPesoPromedio: '平均体重 (kg) *',
    fieldCostoPorCabeza: '每头成本 ($，可选)',
    fieldProveedor:    '供应商',
    newEventBtn:       '+ 登记事件',
    newEventTitle:     '登记事件',
    fieldEventType:    '事件类型 *',
    fieldKgAlimento:   '饲料 (kg) *',
    fieldTipoAlimento: '饲料类型',
    fieldCausa:        '原因',
    fieldPesoTotal:    '出售总重 (kg) *',
    fieldPrecioKg:     '每kg价格 ($) *',
    fieldDestino:      '去向',
    fieldNotes:        '备注',
    ranchoRequired:    '牧场为必填项',
    nameRequired:      '名称为必填项',
    capacityRequired:  '容量必须大于0',
    cabezasRequired:   '头数必须大于0',
    pesoRequired:      '平均体重必须大于0',
    kgAlimentoRequired: '饲料公斤数必须大于0',
    pesoTotalRequired: '总重量为必填项',
    precioKgRequired: '每公斤价格为必填项',
    createError:       '登记时出错',
    create:            '登记',
    gdpUnit: '克/天',
  },

  payroll: {
    title:            '工资单',
    loading:          '加载工资单中...',
    openPeriod:       '开放期',
    noActivePeriod:   '无活跃周期',
    kpiTotal:         '累计总额',
    kpiWorkers:       '工人数',
    kpiWorkersSub:    '在活跃周期中',
    kpiJornal:        '日薪',
    kpiJornalSub:     '每工作日',
    kpiDestajo:       '计件',
    kpiDestajoCub:    '每件产品',
    kpiContrato:      '合同',
    kpiContratoSub:   '固定金额',
    tabActive:        '活跃周期',
    tabByLot:         '按地块',
    tabHistory:       '历史记录',
    recordsLabel:     '记录',
    noActivePeriodOpen: '无开放周期',
    noEntries:        '活跃周期中无记录。',
    noEntriesSub:     '请使用添加按钮为此期间登记工人。',
    colWorker:        '工人',
    colLot:           '地块',
    colActivity:      '活动',
    colType:          '类型',
    colDetail:        '详情',
    colTotal:         '总计',
    jornalDays:       '天',
    contratoLabel:    '固定合同',
    chartTitle:       '各地块劳动成本 (MXN)',
    chartLegend:      '劳动总计',
    noData:           '暂无数据',
    distTitle:        '支付类型分布',
    distColType:      '类型',
    distColAmount:    '金额',
    distTotal:        '总计',
    historyTitle:     '周期历史',
    historyLoading:   '加载历史中...',
    historyEmpty:     '无已注册周期。',
    histColPeriod:    '周期',
    histColDates:     '日期',
    histColStatus:    '状态',
    histColCostBasis: '成本基准',
    histColTotal:     '总计',
    statusOpen:       '开放',
    statusClosed:     '已关闭',
    costBasisTotal:   '总产量',
    costBasisSellable: '可销售',
    openPeriodBtn:    '+ 开启周期',
    closePeriodBtn:   '关闭周期',
    closePeriodConfirm: '关闭此薪资周期？将计算最终总额，之后无法再添加记录。',
    addEntryBtn:      '+ 登记支付',
    openPeriodTitle:  '开启薪资周期',
    fieldLabel:       '标签 *',
    fieldLabelPlaceholder: '2026年7月上半月',
    fieldStartDate:   '开始日期 *',
    fieldEndDate:     '结束日期 *',
    fieldCostBasis:   '成本基准',
    addEntryTitle:    '登记支付',
    fieldWorkerName:  '工人姓名 *',
    unknownLote: '地块不可用',
    workerPlaceholder: '搜索或输入姓名',
    workerFreeText: '将使用输入的姓名',
    lotePlaceholder: '搜索地块',
    fieldWorkerPhone: '电话（可选）',
    fieldLoteId:      '地块 *',
    payTypeJORNAL:  '日薪',
    payTypeDESTAJO: '计件',
    payTypeCONTRATO: '合同',
    fieldPayType:     '支付类型 *',
    fieldActivity:    '活动 *',
    fieldDays:        '工作天数',
    fieldUnits:       '生产单位数',
    fieldUnitLabel:   '单位（kg、箱...）',
    fieldRate:        '费率 ($) *',
    fieldNotes:       '备注',
    labelRequired:    '标签为必填项',
    datesRequired:    '开始和结束日期为必填项',
    workerRequired:   '工人姓名为必填项',
    loteRequired:     '请选择地块',
    activityRequired: '活动为必填项',
    rateRequired:     '费率必须大于0',
    openPeriodError:  '开启周期时出错',
    closePeriodError: '关闭周期时出错',
    addEntryError:    '登记支付时出错',
    create:           '创建',
  },

  activityTypes: {
    PREPARACION:   '土地准备',
    SIEMBRA:       '播种',
    RIEGO:         '灌溉',
    FERTILIZACION: '施肥',
    FITOSANITARIO: '病虫害防治',
    COSECHA:       '收获',
    MAQUINARIA:    '机械',
    LABORES:       '劳作',
    OTRO:          '其他',
    NOVEDAD:       '田间记事',
    CLIMA:         '天气',
  },

  activityFeed: {
    loading: '加载活动中...',
    empty:   '暂无最近活动。',
    unknownPerson: '有人',
    verb: {
      PREPARACION:   '整地',
      SIEMBRA:       '播种',
      RIEGO:         '浇水',
      FERTILIZACION: '施肥',
      FITOSANITARIO: '喷洒了农药',
      COSECHA:       '收获',
      MAQUINARIA:    '使用了机械',
      LABORES:       '进行了田间作业',
      OTRO:          '记录了一项作业',
      NOVEDAD:       '报告了一条田间记事',
      CLIMA:         '记录了一次天气事件',
    },
    allTab:           '全部',
    searchPlaceholder: '搜索备注和物资...',
    noResults:        '没有找到相关结果。',
    loadMore:         '加载更多',
    editNotes:        '编辑备注',
    sowingDensity:    '播种密度',
    sowingRowSpacing: '行距',
    weatherRain: '降雨',
    weatherFrost: '霜冻',
    sowingPlantSpacing: '株距',
    sowingGenetics:   '品种',
    notesLabel:       '备注',
    notesPlaceholder: '写一条备注...',
    saveNotes:        '保存',
    cancelEdit:       '取消',
    saveError:        '备注保存失败，请重试。',
    newBadge:         '{count} 条新数据',
    delete:           '删除',
    deleteConfirmTitle: '删除此记录？',
    deleteConfirmBody: '将从历史中删除“{name}”的此记录，并撤销相关的库存扣减。',
    deleteError:      '无法删除该记录。可能有其他数据依赖于它。',
    stockUsed:        '已扣减库存：{product} {qty} {unit}',
    mineTab:          '我的数据',
    materialsLabel:   '物资',
  },

  // ── 投资人门户 — 当日概况 ──────────────────────────────────────────────────────
  investor: {
    title:               '当日概况',
    subtitle:             '项目总览 — 仅供查看',
    loading:              '加载概况中...',
    kpiLotes:             '在产地块',
    kpiActivities:        '今日活动',
    kpiActivitiesSub:     '当日已登记',
    kpiCostToday:         '当日成本',
    kpiPendingTasks:      '待办任务',
    kpiOverdueTasks:      '逾期任务',
    financeTitle:         '投资与成本',
    kpiTotalCost:         '累计总成本',
    kpiLotesConCosto:     '已登记成本的地块',
    logisticsTitle:       '物流与出口',
    kpiShipments:         '货运总数',
    kpiInTransit:         '运输中',
    kpiHeld:              '滞留',
    kpiDelivered:         '已送达',
    kpiTempAnomalies:     '温度异常',
    lotesTitle:           '地块地图',
    modulesTitle:         '区域地图',
    noCrop:               '未分配作物',
    activityTitle:        '近期活动记录',
    // F6 — 地块区域详情（传感器与无人机）
    plantingDate:         '播种日期',
    area:                 '面积',
    phenology:            '物候期',
    phenologyStub:        '计划与实际对比图表（F2）',
    soilSensors:          '土壤传感器',
    sensorDataStub:       '土壤含水量、EC、pH曲线（F5）',
    droneVideo:           '无人机视频',
    videoPlaceholder:     '视频缩略图',
    openAnomalies:        '未解决的异常',
  },

  // ── 投资人门户 — 产量与收获 ────────────────────────────────────────────────────
  investorYield: {
    title:       '产量与收获',
    subtitle:    '项目产量、品质与损耗 — 仅供查看',
    harvestTitle: '收获与包装',
    mermaTitle:   '损耗与损失',
    tabResumen:  '摘要',
    tabAnalisis: '分析',
    analysisTitle: '按区域划分的预测情景',
    analysisSubtitle: '7天、15天和60天的保守/基准/理想对比 — 与"生产"页面相同的确定性预测。',
    analysisNoProject: '请在顶部栏选择一个项目以查看分析。',
    analysisNoModules: '当前项目没有带预测的区域。',
    analysisForecastTitle: '预计产量（未来15天）',
    harvestMapTitle:    '按收获状态划分的区域地图',
    harvestMapSubtitle: '每个区域的运营状态（F1）——在尚无按区域统计的收获进度百分比之前，作为替代指标。',
    lossReasonsTitle:   '损失原因',
    analysisColModule: '区域',
    analysisColCrop:   '作物',
    analysisScenarioLegend: '每个单元格：保守 / 基准 / 理想。',
  },

  // ── 投资人门户 — 物流与回款 ────────────────────────────────────────────────────
  investorLogistics: {
    title:              '物流与回款',
    subtitle:            '货运、温度与出口状态 — 仅供查看',
    kpiTempAnomalies:    '温度异常',
    kpiDelays:           '延误货运',
    shipmentsTitle:      '近期货运',
    collectionsTitle:    '回款',
    agingCurrent:        '未到期',
    agingBucketPrefix:   '逾期',
    agingTotal:          '应收账款总额',
    tableCustomer:       '客户',
    telemetryTitle:      '货运遥测',
    routeTitle:          '路线',
    temperatureTitle:    '运输途中温度',
    inRangePct:          '处于范围内的时间占比',
    noTelemetry:         '该货运未分配遥测设备。',
    telemetryError:      '无法加载该货运的遥测数据，请稍后重试。',
    selectShipment:      '选择一个货运以查看其遥测数据。',
    currentPosition:     '当前位置',
    tempRange:           '允许范围',
    lastReading:         '最后读数',
  },

  // ── 投资人门户 — 地块区域（F1，暂无地图）────────────────────────────────────────
  investorModules: {
    title:              '地块区域',
    subtitle:            '项目区域总览 — 仅供查看',
    loading:             '加载区域中...',
    noModules:           '尚未登记任何区域。',
    noCrop:              '未分配作物',
    allLotes: '所有地块',
    loteFilter: '按地块筛选',
    kmlInvalid: 'KML中没有有效的多边形',
    kmlOutside: 'KML边界超出上级区域或地块',
    kmlSaved: '已从KML更新边界',
    kmlError: '无法保存边界',
    viewOnMap:           '在地图上查看',
    daysSincePlanting:   '种植天数',
    statusPREPARACION:   '整地中',
    statusNORMAL:        '正常',
    statusATENCION:      '需关注',
    statusCRITICO:       '严重',
    statusCOSECHANDO:    '收获中',
    loadingVideo:        '视频加载中…',
    videoLoadError:      '视频加载失败',
    notFound:            '未找到区域',
    back:                '返回',
    plantingDate:        '种植日期',
    area:                '面积',
    fenologyTitle:       '物候期',
    soilSensorsTitle:    '土壤传感器',
    videoTitle:          '无人机视频',
    anomaliesTitle:      '未结异常',
    telemetryTemp:         '温度（°C）',
    telemetryHumidity:     '环境湿度（%）',
    telemetrySoilMoisture: '土壤湿度（%）',
    telemetryError:        '无法加载该区域的遥测数据。',
    noTelemetry:           '该区域暂无传感器读数。',
    historyTitle:          '历史记录',
    historyFrom:           '从',
    historyTo:             '至',
    historyActivity:       '活动',
    historyStatus:         '状态',
    historyAll:            '全部',
    historyCurrent:        '当前',
    historyClosed:         '已关闭',
    historyReasonCreate:   '区域创建',
    historyReasonUpdate:   '作物变更',
    historyReasonBackfill: '迁移',
    historyEmpty:          '没有符合这些筛选条件的历史记录。',
    historySince:          '自',
    historyUntil:          '至',
    childrenTitle:         '该地块内的区域',
    childrenEmpty:         '没有子区域。',
  },

  // ── 投资人门户 — 生产（F2）──────────────────────────────────────────────────
  investorProduction: {
    title:                '生产',
    subtitle:              '区域的计划与实际、预测和异常 — 仅供查看',
    noProjectSelected:     '请在顶部栏选择一个项目以查看其生产情况。',
    tabResumen:  '摘要',
    tabProgreso: '进度',
    progressTitle:    '按地块划分的进度',
    progressSubtitle: '当前项目的所有地块 — 完成百分比和平均偏差。',
    progressColLote:         '地块',
    progressColCrop:         '作物',
    progressColCurrentStage: '当前阶段',
    progressColPct:          '完成百分比',
    progressColDeviation:    '偏差（天）',
    progressCropTitle:       '按作物划分的计划',
    progressCropLoteCount:   '地块',
    progressGanttTitle:      '地块矩阵 — 计划与实际对比',
    progressGanttSubtitle:   '当前项目所有地块的所有阶段，按地块排序。',
    progressForecastTitle:    '按区域划分的生产预测',
    progressForecastSubtitle: '当前项目各区域未来15天的基准情景。',
    loadingModules:        '加载区域中...',
    noModules:             '该项目尚未登记任何区域。',
    noModulesHint:         '生产视图基于区域（F1）跟踪。要查看该项目的计划、预测和异常，请先在 GIS 地图中登记其区域。',
    moduleSelector:        '区域',
    loadingPlan:           '加载生产计划中...',
    noPlan:                '该区域尚无生产计划。',
    ganttTitle:            '各阶段计划与实际对比',
    ganttSubtitle:         '灰色条 = 计划。彩色条 = 实际（绿色按时，红色延迟，蓝色提前）。',
    ganttPlanned:          '计划',
    ganttActual:           '实际',
    ganttOnTime:           '按时',
    ganttLate:             '延迟',
    ganttEarly:            '提前',
    funnelTitle:           '累计产量曲线',
    funnelSubtitle:        '每个阶段结束时累计的周期总产量百分比',
    forecastTitle:         '收获预测',
    forecastDisclaimer:    '每个情景的公斤数是未来N天窗口内的预期增量，并非周期累计总量。预测期越长，置信度越低。',
    loadingForecast:       '加载预测中...',
    noForecast:            '该时间范围尚无预测数据。',
    horizon7:              '未来7天',
    horizon15:             '未来15天',
    horizon60:             '未来60天',
    confidence:            '置信度',
    scenarioConservador:   '保守情景',
    scenarioBase:          '基准情景',
    scenarioIdeal:         '理想情景',
    anomaliesTitle:        '未解决的异常',
    loadingAnomalies:      '加载异常中...',
    noAnomalies:           '没有未解决的异常。',
    responsible:           '负责人',
    severityBAJA:          '低',
    severityMEDIA:         '中',
    severityALTA:          '高',
    severityCRITICA:       '严重',
    anomalyTypeDESVIACION_FENOLOGICA: '物候偏差',
    anomalyTypePLAGA:      '病虫害',
    anomalyTypeRIEGO:      '灌溉',
    anomalyTypeMAQUINARIA: '机械设备',
    anomalyTypePERSONAL:   '人员',
    anomalyTypeCLIMA:      '天气',
    anomalyTypeCALIDAD:    '质量',
    anomalyTypeSENSOR:     '田间传感器',
    anomalyTypeOTRO:       '其他',
  },

  investorFunding: {
    title:              '投资与资金',
    subtitle:            '资本、出资与用款申请 — 仅供查看',
    loading:             '加载中...',
    noData:              '暂无数据。',
    months:              '个月',
    kpiCommitted:        '承诺资本',
    kpiDrawn:            '已出资资本',
    kpiPending:          '待出资',
    kpiCollected:        '截至目前已回款',
    kpiRoi:              '投资回报率',
    kpiIrr:              '年化内部收益率',
    kpiPayback:          '回本周期',
    byCurrencyTitle:    '按币种的指标',
    sourcesTitle:        '资金来源',
    drawdownsTitle:      '出资记录',
    fundRequestsTitle:   '用款申请',
    fundCategories: { INSUMOS: '农资投入品', MAQUINARIA: '机械', MANO_DE_OBRA: '人工', INFRAESTRUCTURA: '基础设施', OTRO: '其他' } as Record<string, string>,
    tableName:           '名称',
    tableType:           '类型',
    tableCommitted:      '承诺金额',
    tableDrawn:          '已出资',
    tablePending:        '待出资',
    tableDate:           '日期',
    tableAmount:         '金额',
    tableStatus:         '状态',
    sourceTypeINVESTOR: '投资人',
    sourceTypeBANK: '银行',
    sourceTypeFIRA: 'FIRA',
    sourceTypeOWN: '自有',
    tableCategory:       '类别',
    drawdownPLANNED:     '计划中',
    drawdownRECEIVED:    '已到账',
    drawdownCANCELLED:   '已取消',
    fundRequestPENDING:  '待审批',
    fundRequestAPPROVED: '已批准',
    fundRequestREJECTED: '已拒绝',
    fundRequestCANCELLED:'已取消',
  },

  filterPanel: {
    selectAll:       '全选',
    clear:           '清除',
    clearAll:        '清除筛选',
    apply:           '应用',
    applyFilterFor:  '应用{label}筛选',
    dateFrom:        '从',
    dateTo:          '至',
  },
  loteDetail: {
    loading:     '加载地块中...',
    notFound:    '未找到地块。',
    crop:        '作物',
    hectares:    '公顷',
    accCost:     '累计费用',
    recentActs:  '最近活动',
    costsByType: '分类费用',
    noCosts:     '暂无费用记录。',
    dataTitle:       '地块数据',
    dataFilterDate:  '日期',
    dataFilterType:  '类型',
    dataFilterUser:  '用户',
    dataEmpty:       '没有符合筛选条件的数据。',
    dataOriginActivity: '活动',
    dataOriginMovement: '库存变动',
    budgetTitle:      '基础投入预算',
    budgetEmpty:      '无基础预算。请使用“+ 新建预算”或将协议应用于此地块。',
    budgetProtocol:   '协议',
    budgetCycle:      '周期',
    budgetChemicals:  '化学品/农药',
    budgetFert:       '化肥',
    budgetCostHaChem: '化学品成本/公顷',
    budgetCostHaFert: '化肥成本/公顷',
    budgetTotal:      '预算总计',

    // NDVI
    ndviTitle:        '植被指数 (NDVI)',
    ndviMean:         '平均值',
    ndviMin:          '最小值',
    ndviMax:          '最大值',
    ndviCloud:        '云量覆盖',
    ndviDate:         '影像日期',
    ndviTimeline:     'NDVI趋势（90天）',
    ndviNoBoundary:   '未定义地理围栏。在地图中设置地块多边形以启用NDVI。',
    ndviNoApiKey:     'Agromonitoring集成未配置。',
    ndviDisabled:     '此牧场未启用NDVI；请联系管理员启用。',
    ndviNoData:       '此时段无可用卫星图像。',
    ndviLoading:      '正在获取卫星图像...',
    ndviHealthGood:   '植被健康',
    ndviHealthMid:    '植被中等',
    ndviHealthLow:    '植被稀疏/胁迫',

    // C 阶段 B — 地图图层、价格历史、风险面板
    mapLayersTitle:   '地图图层',
    layerNdvi:        'NDVI',
    systemTitle: '系统变更',
    systemEmpty: '暂无变更记录。',
    systemEvents: { LOTE_UPDATED: '地块已编辑', LOTE_BOUNDARY_CHANGED: '地块边界', MODULE_CREATED: '区域已创建', MODULE_UPDATED: '区域已编辑', MODULE_BOUNDARY_CHANGED: '区域边界', MODULE_DELETED: '区域已删除' },
    layerOrtho:       '正射影像',
    orthoPlaceholder: '尚无正射影像管道 — 待该地块拥有真实的、带地理参照的无人机/卫星影像来源后即可接入此地图。',
    priceTitle:       '价格历史',
    priceSubtitle:    '企业同一作物销售订单的单价 — 仅为近似值，当前数据模型尚未将销售与该具体地块关联。',
    priceSubtitleWithCompany: '{company} 同一作物销售订单的单价 — 仅为近似值，当前数据模型尚未将销售与该具体地块关联。',
    priceNoData:      '该作物暂无销售订单。',
    priceLatest:      '最新价格',
    riskTitle:        '该地块的风险',
    riskNoData:       '该地块没有未结的异常。',

    // 详情页签（概览 / 地图 / 活动 / 财务）
    tabSummary:       '概览',
    tabMap:           '地图',
    tabActivities:    '活动',
    tabFinance:       '财务',
    financeEmpty:     '该地块暂无财务数据。',
  },

  machinery: {
    labor: {
      title:           '劳动力',
      lote:            '地块',
      selectLote:      '选择地块',
      register:        '登记工时',
      loading:         '正在加载劳动力...',
      empty:           '该地块暂无工时记录。',
      date:            '日期',
      laborType:       '类型',
      activity:        '作业',
      workers:         '工人数',
      hoursPerWorker:  '每人工时',
      dailyRate:       '每人日薪 (MXN)',
      notes:           '备注',
      totalCost:       '费用',
      totalLabel:      '合计',
      save:            '保存',
      cancel:          '取消',
      saveError:       '无法登记工时，请检查数据后重试。',
      types: { JORNAL: '日薪', DESTAJO: '计件', CONTRATO: '合同' },
    },
    title:           '机械',
    loading:         '加载机械中...',
    machinesUnit:    '台机械',
    inUseUnit:       '使用中',
    rentedUnit:      '租用中',
    kpiTotal:        '机械总数',
    kpiTotalSub:     '正在使用',
    kpiRentals:      '租用设备',
    kpiRentalsSub:   '第三方',
    kpiDiesel:       '柴油（当前价格）',
    kpiDieselFrom:   '自',
    kpiDieselNone:   '未配置',
    kpiSessions:     '近期作业',
    kpiSessionsSub:  '最近',
    fleet:           '车队',
    recentSessions:  '近期作业记录',
    catalog:         '田间作业目录',
    rental:          '租用',
    noSessions:      '暂无作业记录。',
    noCatalog:       '暂无目录。请运行 seed 脚本填充活动。',
    tableDevice:     '机械',
    tableActivity:   '作业',
    tableStart:      '开始时间',
    tableHours:      '工时',
    tableDiesel:     '柴油 (升)',
    tableTotalCost:  '总费用',
    tableCostPerHa:  '费用/公顷',
    tableStatus:     '状态',
    sessionClosed:   '已完成',
    sessionActive:   '进行中',
    sessionOverrun:  '超时',
    overrunTooltip:  '工时超出未说明原因',
    timerStart: '开始',
    timerStop: '停止',
    timerStartTitle: '开始作业',
    timerStopTitle: '停止作业',
    timerLote: '地块',
    timerActivity: '作业内容',
    timerFuelLiters: '燃油（升）',
    timerFuelPrice: '每升价格',
    timerHourlyRate: '时薪（可选）',
    timerRunning: '进行中',
    timerErrStart: '无法开始作业',
    timerErrStop: '无法停止作业',
    timerHectares: '完成公顷数（可选）',
    timerBudgetCap: '每公顷预算上限（可选）',
    timerRentalCost: '当日租金（可选）',
    timerResultTitle: '作业已停止',
    timerCostTotal: '总成本',
    timerCostPerHa: '每公顷成本',
    timerCostPerHaNA: '未计算每公顷成本：停止时请填写完成的公顷数。',
    timerAlertLabel: '预算警报',
    timerAlertNONE: '在预算内',
    timerAlertYELLOW: '黄色：接近预算',
    timerAlertORANGE: '橙色：偏高',
    timerAlertRED: '红色：严重',
    timerAlertEXCEEDED: '已超出预算',
    timerNotPersisted: '仅供参考：该费用未记入会计账（机器人通过草稿确认）。',
    timerClose: '关闭',
    timerRegister: '记入会计账',
    timerRegistered: '已记入会计账。',
    timerErrRegister: '无法将费用记入会计账。',
    timerSelectLote: '请选择地块',
    timerActivityRequired: '请填写作业内容',
    timerDuration: '时长',
    catalogActivity: '作业',
    catalogHrsHa:    '小时/公顷',
    catalogDieselHa: '柴油/公顷',
    catalogAlertPct: '预警 %',
    types: {
      TRACTOR: '拖拉机',
      FUMIGADORA: '喷药机',
      DRON: '无人机',
      CAMION: '卡车',
      OTRO: '其他',
    },
    fuels: {
      DIESEL: '柴油',
      GASOLINE: '汽油',
      ELECTRIC: '电动',
    },
    newMachine: '新增机械',
    emptyFleet: '暂无已登记的机械。',
    addFirst: '添加第一台机械',
    rentedDefault: '租用',
    perDay: '{amount}/天',
    plateLabel: '车牌：{plate}',
    form: {
      editTitle: '编辑机械',
      name: '名称 *',
      type: '类型 *',
      fuel: '燃料',
      model: '型号',
      year: '年份',
      plate: '车牌',
      status: '状态',
      isRental: '这是租用的机械',
      rentalOwner: '所有者 / 出租方',
      rentalCost: '每日费用（MXN）',
      droneSection: '🚁 无人机信息',
      droneModel: '品牌和型号 *',
      serial: '序列号 *',
      tank: '药箱容量（升）*',
      coverage: '单次作业面积（公顷）*',
      pilotSection: '👨‍✈️ 持证飞手',
      pilotName: '姓名 *',
      pilotPhone: '电话 *',
      license: 'AFAC/SICT 执照 *',
      licenseExpiry: '执照有效期 *',
      registration: '航空器登记号',
      optional: '选填',
      saveChanges: '保存更改',
      create: '创建机械',
      errCreate: '创建失败',
      errUpdate: '更新失败',
      droneModelRequired: '请填写无人机品牌/型号',
      serialRequired: '请填写序列号',
      tankRequired: '请填写药箱容量',
      pilotNameRequired: '请填写飞手姓名',
      pilotPhoneRequired: '请填写飞手电话',
      licenseRequired: '请填写执照编号',
      licenseExpiryRequired: '请填写执照有效期',
      deleteTitle: '删除机械',
      deleteConfirm: '确定删除 {name} 吗？此操作无法撤销。',
    },
  },

  machineryStatus: {
    AVAILABLE:      '可用',
    IN_USE:         '使用中',
    MAINTENANCE:    '维护中',
    OUT_OF_SERVICE: '停用',
  },

  merma: {
    title:              '损耗 / 废品',
    loading:            '加载损耗数据中...',
    kpiRejectedKg:      '总拒绝量（公斤）',
    kpiImpact:          '经济影响',
    kpiRecords:         '记录事件数',
    tableTitle:         '损耗历史',
    tableDate:          '日期',
    tableLote:          '地块',
    tableBatch:         '周期',
    tableReason:        '原因',
    tableKg:            '拒绝公斤',
    tableBoxes:         '箱数',
    tableImpact:        '影响（$）',
    tableStatus:        '状态',
    noRecords:          '暂无损耗记录。',
    registerBtn:        '+ 登记损耗',
    closeBtn:           '关闭报告',
    closeConfirm:       '关闭该周期的损耗报告？所有记录将标记为已关闭。',
    registerTitle:      '登记损耗',
    fieldLote:          '地块',
    fieldLoteZone:      '地块或区域',
    fieldBatch:         '周期 (batchId)',
    fieldReason:        '原因',
    fieldKg:            '拒绝公斤',
    fieldBoxes:         '拒绝箱数（可选）',
    fieldCostKg:        '成本/公斤（$，可选）',
    fieldNotes:         '备注',
    filterAllLotes:     '所有地块',
    filterAllBatches:   '所有周期',
    chartTitle:         '按原因分类损耗（公斤）',
    successCreate:      '损耗已记录。',
    successClose:       '报告已关闭。',
    errorCreate:        '登记损耗时出错。',
    errorClose:         '关闭报告时出错。',
    reasonCLIMA:           '气候损害',
    reasonPLAGA:           '病虫害损害',
    reasonMECANICO:        '机械损害',
    reasonRECHAZO_CALIDAD: '品质/规格拒收',
    reasonPUDRICION:       '腐烂变质',
    reasonOTRO:            '其他原因',
    statusREGISTRADA:   '已记录',
    statusVERIFICADA:   '已核实',
    statusCERRADA:      '已关闭',
  },

  crop: {
    title:              '作物管理',
    loading:            '加载作物数据中...',
    tabPhenology:       '物候期',
    tabScouting:        '病虫害监测',
    loteLabel:          '地块',
    cicloLabel:         '周期',
    cicloPlaceholder:   '如：2026-A',
    cicloSearch:        '查询',
    cicloUse:          '使用“{value}”',
    noCiclo:            '请输入周期以查看历史记录。',
    phenoTitle:         '物候期历史',
    phenoAddBtn:        '+ 记录阶段',
    phenoNoRecords:     '该周期暂无记录。',
    phenoTableDate:     '日期',
    phenoTableStage:    '阶段',
    phenoTableCrop:     '作物',
    phenoTableDap:      'DAP',
    phenoTableBy:       '记录人',
    phenoDialogTitle:   '记录物候阶段',
    assignTitle: '作物与播种',
    assignHintZones: '此地块有分区：请在每个分区设置作物和播种日期。',
    assignHintLote: '此地块没有分区：作物和播种将设置在整个地块上。',
    assignCropLabel: '作物',
    assignDateLabel: '播种日期',
    assignZoneCol: '分区',
    assignNoCatalog: '目录中还没有作物。请在“目录”选项卡中添加。',
    assignGoCatalog: '前往目录',
    needAssignment: '该区域或地块在所选周期中没有作物。请在“分配”标签页中开启周期。',
    startCycle: '开启周期',
    closeCycle: '关闭周期',
    closeCycleTitle: '关闭周期',
    closeCycleConfirm: '要关闭 {zone} 的周期 {ciclo} 吗？在开启新周期之前，地图会以淡色显示该作物为“上一个已关闭周期”；历史记录会保留。',
    cycleOf: '{zone} 的周期',
    historyTitle: '周期历史',
    historyEmpty: '该地块还没有周期。',
    historyZone: '区域',
    historyStatus: '状态',
    historyClosedAt: '关闭日期',
    historyClosedBy: '关闭人',
    cycleOpen: '进行中',
    cycleClosed: '已关闭',
    needAssignmentGo: '前往分配',
    assignCropType: '农艺类型（可选）',
    cropTypeLeaves: '叶菜', cropTypeFruit: '水果', cropTypeOther: '其他', cropTypeAdd: '添加 {text}',
    assignError: '无法保存',
    removeCropError: '无法移除作物',
    assignCatalog: '作物目录',
    assignDeleteCrop: '删除作物',
    assignDeleteInUse: '无法删除“{name}”：正在使用中（{usage}）。',
    assignDeleteError: '无法删除该作物',
    tabAssignment: '分配',
    tabCatalog: '目录',
    allLotesTitle: '所有地块',
    loteAll: '<全部>',
    allLotesHint: '选择一个地块以分配作物并查看其周期。',
    colLote: '地块',
    colHa: '公顷',
    colCrop: '作物',
    colCycle: '周期',
    cycleNone: '无周期',
    catalogTitle: '作物目录',
    catalogNew: '+ 新作物',
    catalogColName: '名称',
    catalogColColor: '颜色',
    catalogEmpty: '目录中没有作物。',
    catalogEdit: '编辑',
    catalogCreateTitle: '新作物',
    catalogEditTitle: '编辑作物',
    fsmaCovered: 'FSMA 204：已覆盖',
    fsmaNotCovered: 'FSMA 204：未覆盖',
    fsmaUnknown: 'FSMA 204：未定义',
    catalogSaveError: '无法保存作物',
    catalogDeleteConfirm: '从目录中删除“{name}”？仅在未被使用时可删除。',
    phenoTableZone: '区域',
    zoneFilterLabel: '区域',
    zoneAll: '所有区域',
    usageLotes: '{n} 个地块',
    usageModules: '{n} 个区域',
    usageHistory: '{n} 条区域历史记录',
    usagePhenology: '{n} 条物候记录',
    phenoFieldCultivo:  '作物类型',
    phenoFieldStage:    '阶段',
    phenoFieldDap:      'DAP（播种后天数）',
    phenoFieldNotes:    '备注',
    phenoSuccess:       '阶段已记录。',
    phenoError:         '记录阶段时出错。',
    scoutTitle:         '病虫害监测',
    scoutAddBtn:        '+ 记录检测',
    scoutNoRecords:     '该周期暂无检测记录。',
    scoutTableDate:     '日期',
    scoutTableName:     '病虫害',
    scoutTableType:     '类型',
    scoutTablePressure: '压力',
    scoutTablePct:      '受影响面积%',
    scoutTableAction:   '采取措施',
    scoutDialogTitle:   '记录病虫害',
    scoutFieldPestType: '类型',
    scoutFieldName:     '病虫害名称',
    scoutFieldPressure: '压力等级',
    scoutFieldPct:      '受影响面积%（0–100）',
    scoutFieldAction:   '采取措施',
    scoutFieldNotes:    '备注',
    scoutSuccess:       '检测已记录。',
    scoutError:         '记录检测时出错。',
    alertsTitle:        '活跃警报',
    alertsNone:         '暂无活跃警报。',
    stagePREPARACION_SUELO: '土地准备',
    stageSIEMBRA:           '播种',
    stageGERMINACION:       '发芽',
    stageEMERGENCIA:        '出苗',
    stageESTABLECIMIENTO:   '定苗',
    stageVEGETATIVO:        '营养生长',
    stageFLORACION:         '开花',
    stageFRUCTIFICACION:    '结果',
    stageMADURACION:        '成熟',
    stageCOSECHA:           '收获',
    stagePOST_COSECHA:      '采后',
    pressureBAJO:   '低',
    pressureMEDIO:  '中',
    pressureALTO:   '高',
    presureCRITICO: '严重',
    pestPLAGA:       '害虫',
    pestENFERMEDAD:  '病害',
    pestMALEZA:      '杂草',
    scoutEvidenceTitle: '照片证据',
    scoutPhotoAlt: '巡查证据',
    scoutPhotoAltZoom: '巡查证据（放大）',
  },

  export: {
    kdeTitle: '追溯关键数据',
    kdeRecipientLocation: '收货方地点',
    kdeOriginLocation: '起运地点',
    kdeShipDate: '发货日期',
    kdeRefDoc: '参考单据',
    kdeTlcs: '批次代码',
    title:            '出口',
    loading:          '加载货运中...',
    kpiTotal:         '总货运',
    kpiInTransit:     '在途',
    kpiHeld:          '滞留',
    kpiDelivered:     '已交付',
    kpiKg:            '发货公斤',
    kpiTransitDays:   '平均运输天数',
    tableContainer:   '集装箱',
    tableProduct:     '产品',
    tableDestination: '目的地',
    tableKg:          '公斤',
    tableBoxes:       '箱数',
    tableStatus:      '状态',
    tableDeparted:    '出发',
    tableArrived:     '到达',
    noData:           '暂无出口货运记录。',
    detailTitle:      '货运详情',
    detailEventsTitle:'事件链',
    docDownload:      '下载',
    statusDRAFT:                '草稿',
    statusPACKING:              '装箱中',
    statusSEALED:               '已密封',
    statusDEPARTED:             '已出发',
    statusCUSTOMS_SUBMITTED:    '海关申报中',
    statusCUSTOMS_CLEARED:      '海关放行',
    statusCUSTOMS_HOLD:         '海关扣押',
    statusFDA_SUBMITTED:        'FDA申报中',
    statusFDA_CLEARED:          'FDA放行',
    statusFDA_HOLD:             'FDA扣押',
    statusARRIVED:              '已到达',
    statusDELIVERED:            '已交付',
    statusCANCELLED:            '已取消',
    daysUnit: '天',
  },

  transport: {
    title:            '运输',
    loading:          '加载发货中...',
    addBtn:           '+ 登记发货',
    noData:           '暂无发货记录。',
    filterPeriod:     '期间',
    filterLote:       '地块',
    filterAllLote:    '所有地块',
    tableDate:        '日期',
    tableDestination: '目的地',
    tableDriver:      '司机',
    tablePlate:       '车牌',
    tableKg:          '总公斤',
    tableBoxes:       '箱数',
    tableStatus:      '状态',
    tableGradeA:      'A级',
    tableGradeB:      'B级',
    tableGradeC:      'C级',
    statusPENDING:    '待处理',
    statusEN_RUTA:    '途中',
    statusENTREGADO:  '已交付',
    statusCANCELADO:  '已取消',
    markEnRuta:       '标记途中',
    markEntregado:    '标记已交付',
    markCancelado:    '取消',
    confirmAction:    '确认状态变更？',
    newShipmentTitle: '登记运输',
    fieldLoteId:      '地块 *',
    fieldLoteIdPlaceholder: 'lote_simiente_04',
    fieldDestination: '目的地 *',
    fieldDestinationPlaceholder: '中央包装厂, 麦卡伦 TX...',
    fieldDriver:      '司机',
    fieldTruckPlate:  '车牌',
    fieldTransportCompany: '运输公司',
    fieldScheduledAt: '计划日期',
    fieldBoxesTitle:  '箱子',
    fieldCalibre:     '规格',
    fieldBoxCount:    '箱数',
    fieldWeightKg:    '重量 (kg)',
    fieldGrade:       '等级',
    addBoxRow:        '+ 添加行',
    removeBoxRow:     '移除',
    loteRequired:     '地块为必填项',
    destinationRequired: '目的地为必填项',
    boxesRequired:    '请至少添加一行箱子信息',
    createShipmentError: '登记运输时出错',
    create:           '登记',
  },

  fsmaExport: {
    title:           'FSMA 204 交付',
    subtitle:        '按追溯批号（TLC）整理的关键数据表，用于 24 小时内交付、追踪和召回演练。',
    disabled:        '本公司未启用 FSMA 204 追溯功能。',
    exportTitle:     '关键数据表',
    rangeFrom:       '开始日期',
    rangeTo:         '结束日期',
    tlcOptional:     'TLC（可选）',
    exportXlsx:      '下载 .xlsx',
    exportCsv:       '下载 .csv',
    exportError:     '无法生成数据表',
    exportTruncated: '数据表中的包装记录超过导出上限，因此导出不完整。',
    traceTitle:      '按 TLC 追溯',
    traceTlc:        '追溯批号（TLC）',
    traceForward:    '向前追溯（流向何处）',
    traceBackward:   '向后追溯（来自何处）',
    traceError:      '无法执行追溯',
    lotesLabel:      '来源批次',
    foliosLabel:     '追溯单',
    unmatchedLabel:  '未平衡公斤数',
    noShipments:     '该 TLC 没有发运记录。',
    none:            '—',
    drillTitle:      '召回演练',
    drillStart:      '演练开始',
    drillFinish:     '演练结束',
    drillMerma:      '损耗（公斤，可选）',
    drillNotes:      '备注（可选）',
    drillSubmit:     '登记演练',
    drillError:      '无法登记演练',
    drillMinutes:    '耗时',
    drillPassed:     '达标',
    drillFailed:     '未达标',
    drillNoThreshold: '无结论：尚未配置物料平衡阈值',
    drillsHistory:   '已登记的演练',
  },

  traceabilityPlan: {
    title:              '溯源计划',
    subtitle:           '21 CFR 1.1315 溯源规则（FSMA 204）的计划。按公司保存，可导出。',
    recordsLocation:    '记录保存位置',
    recordsFormat:      '记录格式',
    foodIdentification: '如何识别食品追溯清单中的食品',
    tlcProcedure:       '如何分配溯源批次代码（TLC）',
    contactName:        '联系人：姓名',
    contactPhone:       '联系人：电话',
    contactEmail:       '联系人：邮箱（可选）',
    version:            '版本',
    save:               '保存计划',
    saving:             '保存中...',
    saved:              '计划已保存',
    saveError:          '无法保存计划',
    exportXlsx:         '导出 .xlsx（计划和农场地图）',
    exportGeoJson:      '导出 GeoJSON（地块边界）',
    exportError:        '导出失败',
    noPlan:             '尚未保存计划。',
    readOnly:           '只读：编辑需要溯源写入权限。',
  },
  traceability: {
    destinationLabel: '目的地：',
    buyerLabel: '买方：',
    eventCOOLING: '预冷',
    kdeComplete: '关键数据完整',
    kdeMissingPrefix: '缺少',
    kdeFields: {
      coolingLocation: '预冷地点',
      coolingDate: '预冷日期',
      product: '产品',
      quantityKg: '数量（公斤）',
      recipientLocation: '收货方地点',
      referenceDocType: '参考单据类型',
      referenceDocNumber: '参考单据编号',
      packingLocation: '初次包装地点',
      harvesterName: '采收方',
      harvesterPhone: '采收方电话',
      tlc: '追溯批次代码',
      harvestedAt: '采收日期',
      originLocation: '发货起点',
      shipDate: '发货日期',
    },
    foliosTitle: '追溯单 — {lote}',
    title:              '溯源',
    loading:            '加载溯源单中...',
    addFolioBtn:        '+ 新建溯源单',
    noData:             '暂无溯源单。请选择地块。',
    loteLabel:          '地块',
    tableFolioId:       '溯源单',
    tableProduct:       '产品',
    tableVariety:       '品种',
    tableEstKg:         '预估公斤',
    tableStatus:        '状态',
    tableCreated:       '创建时间',
    detailTitle:        '监管链',
    detailNoEvents:     '该溯源单暂无事件。',
    eventType:          '事件类型',
    eventDesc:          '描述',
    eventTemp:          '温度',
    eventKg:            '公斤',
    eventBy:            '操作员',
    addEventBtn:        '+ 添加事件',
    statusOPEN:         '开放',
    statusIN_TRANSIT:   '运输中',
    statusDELIVERED:    '已交付',
    statusCLOSED:       '已关闭',
    eventFIELD_OP:      '田间作业',
    eventHARVEST:       '收获',
    eventPACKING:       '包装',
    eventCONTAINER:     '集装箱',
    eventCUSTOMS:       '海关',
    eventEXPORT:        '出口',
    formProduct:        '产品',
    formVariety:        '品种',
    formEstKg:          '预估公斤',
    formDestination:    '目的地',
    formBuyer:          '买方',
    folioCreated:       '溯源单已创建。',
    eventAdded:         '事件已添加。',
    errorCreate:        '创建溯源单时出错。',
    errorEvent:         '添加事件时出错。',
  },

  protocol: {
    title:              '投入品方案',
    loading:            '加载方案中...',
    noData:             '暂无方案。请使用“+ 新建方案”创建。',
    filterCrop:         '作物',
    filterRancho:       '农场',
    filterAll:          '全部',
    tableId:            'ID',
    tableCrop:          '作物',
    tableCiclo:         '周期',
    tableType:          '类型',
    tableCostHa:        '成本/公顷',
    tableCreated:       '创建时间',
    tableSource:        '来源',
    detailTitle:        '方案详情',
    detailApps:         '施用日历',
    detailDay:          '天数',
    detailProducts:     '产品',
    detailCostHa:       '成本/公顷',
    detailCatalog:      '价格目录',
    detailName:         '产品',
    detailPresentation: '规格',
    detailPrice:        '单价',
    detailCategory:     '类别',
    typeQUIMICO:        '化学品',
    typeFERTILIZANTE:   '肥料',
    typeAMBOS:          '两者',
    sourceTELEGRAM_DOC:   '文件',
    sourceTELEGRAM_PHOTO: '照片',
    sourceTELEGRAM_TEXT:  '文本',
    newProtocol:        '+ 新建协议',
    newProtocolTitle:   '新建协议',
    editProtocolTitle:  '编辑协议',
    deleteBtn:          '删除',
    deleteTitle:        '删除协议',
    deleteConfirm:      '删除此协议？此操作无法撤销。',
    applyBtn:           '应用到地块',
    applyTitle:         '将协议应用为预算',
    applyDesc:          '协议成本将累加到指定地块和周期的预算中。',
    applyModeLabel: '要创建什么？',
    applyModeBudget: '仅预算',
    applyModeTasks: '预算及任务',
    applyModeTasksHint: '除预算外，还会创建生产计划以及每次施用对应的任务，日期从播种日起算。',
    fieldLoteOrZone: '地块或分区 *',
    loteOrZoneRequired: '请选择地块或分区',
    applySuccessTasks: '协议已应用：已创建预算和任务。',
    fieldZone:          '分区 *',
    fieldProject:       '项目 *',
    fieldPlantingDate:  '播种日期 *',
    zoneRequired:       '请选择分区',
    projectRequired:    '请选择项目',
    plantingDateRequired: '播种日期为必填项',
    fieldCrop:          '作物',
    fieldRanchoId:      '牧场 ID',
    fieldCiclo:         '周期',
    fieldType:          '类型 *',
    fieldCostoQuimico:  '化学品成本 ($/ha)',
    fieldCostoFert:     '肥料成本 ($/ha)',
    fieldCostoTotal:    '总成本 ($/ha) *',
    applicationsTitle:  '施用计划',
    fieldDias:          '天数（例如 9-10）',
    fieldProductName:   '产品',
    fieldDose:          '剂量（例如 800ml）',
    fieldCostoHaApp:    '成本/ha',
    addProduct:         '+ 产品',
    removeProduct:      '移除',
    addApplication:     '+ 添加施用记录',
    removeApplication:  '移除施用记录',
    catalogTitle:       '价格目录',
    fieldPresentacion:  '包装规格',
    fieldPrecioUnitario: '单价',
    fieldCategory:      '类别',
    addCatalogItem:     '+ 添加目录产品',
    removeCatalogItem:  '移除',
    fieldLoteId:        '地块 *',
    fieldHectareas:     '公顷数 *',
    typeRequired:       '请选择类型',
    costoTotalRequired: '总成本为必填项',
    applicationsRequired: '请至少添加一条施用记录',
    loteRequired:       '地块为必填项',
    hectareasRequired:  '公顷数必须大于0',
    cicloRequired:      '周期为必填项',
    createError:        '保存协议时出错',
    applyError:         '应用协议时出错',
    applySuccess:       '协议已应用到预算。',
    create:             '保存',
    typeMARCO_FERTILIZACION: '施肥方案',
    typeMARCO_PULVERIZACION: '喷药方案',
    sourceWEB: '网页录入',
    sourceIMPORT_XLSX: 'Excel',
    newMarco: '+ 新建方案',
    newMarcoTitle: '新建方案',
    editMarcoTitle: '编辑方案',
    tableName: '名称',
    marcoName: '方案名称 *',
    marcoNamePlaceholder: '例如：YUCHOY 90',
    marcoKind: '方案类型',
    marcoColumns: '产品（列）',
    marcoColumnName: '产品',
    marcoColumnUnit: '单位',
    marcoAddColumn: '+ 产品',
    marcoRemoveColumn: '删除列',
    marcoRows: '施肥次数',
    marcoAddRow: '+ 添加次数',
    marcoDuplicateRow: '复制行',
    marcoRemoveRow: '删除行',
    marcoTiempo: '次数',
    marcoDias: '播种后天数',
    marcoTipoFert: '肥料类型',
    marcoObs: '备注',
    marcoEtapa: '物候期',
    marcoFertHint: '每公顷用量，单位按各列设置。基肥请填 -1。',
    marcoPulvHint: '每次施药的产品和用量（例如 BELEAF 300g）。pH 或灌溉请写在备注。',
    marcoNameRequired: '方案名称为必填',
    marcoColumnsRequired: '至少添加一个有名称的产品',
    marcoRowsRequired: '至少添加一次',
    marcoDiasInvalid: '每一行都需要播种后天数（数字）',
    marcoCantidadInvalid: '用量必须是大于或等于 0 的数字',
    marcoSaveError: '保存方案时出错',
    detailMarcoFert: '施肥矩阵',
    marcoNoApply: '方案不含成本，不能作为预算应用。',
    duplicateBtn: '复制',
    duplicateTitle: '复制方案',
    duplicateDesc: '将创建一个可编辑的副本。如有需要，请修改名称、作物或周期。',
    duplicateConfirm: '创建副本',
    duplicateError: '复制时出错',
    duplicateCopySuffix: '副本',
    importBtn: '导入 Excel',
    importTitle: '从 Excel 导入方案',
    importIntro: '上传包含施肥或喷药方案的 .xlsx 文件。系统会识别每个工作表的类型，保存前可先预览。',
    importChoose: '选择文件',
    importValidating: '正在验证...',
    importReadError: '无法读取该文件。请确认它是符合方案格式的 .xlsx。',
    importError: '无法导入方案',
    importErrorsTitle: '请检查文件',
    importColSheet: '工作表',
    importColMessage: '问题',
    importColDetail: '内容',
    importColStatus: '状态',
    importDetected: '个方案已识别',
    importProductos: '个产品',
    importTiempos: '次',
    importAplicaciones: '次施药',
    importStatusNew: '新建',
    importReplace: '替换',
    importExistsHint: '已存在的方案不会被修改，除非勾选“替换”。替换时会保留其作物、周期和农场。',
    importUnitNote: '施肥用量按 kg/ha 导入。如果某列使用其他单位，请之后在方案中修改。',
    importConfirm: '导入',
    importImporting: '正在导入...',
    importDone: '导入完成',
    importCreated: '个已创建',
    importReplaced: '个已替换',
    importSkipped: '个已跳过',
    importClose: '关闭',
  },

  activityCategories: {
    PREPARACION_TERRENO: '土地准备',
    SIEMBRA:             '播种与移植',
    FUMIGACION:          '喷药',
    FERTILIZACION:       '施肥',
    RIEGO:               '灌溉',
    COSECHA:             '收获',
    TRANSPORTE:          '运输',
    LABORES:             '一般劳作',
  },

  // ── 超级管理员（租户/公司管理）───────────────────────────────────
  superAdmin: {
    accessDenied:       '拒绝访问 — 仅超级管理员可用',
    tenantCreated:      '公司已创建',
    tenantCreateError:  '创建公司出错',
    tenantUpdated:      '公司已更新',
    tenantUpdateError:  '更新出错',
    editTenant:         '编辑公司',
    newTenant:          '新公司',
    fieldName:          '公司名称',
    fieldNamePlaceholder: '例如：我的公司',
    fieldCode:          '代码 / URL 别名',
    fieldCodeHint:      '例如：my-company → /my-company/login',
    fieldCodePlaceholder: 'my-company',
    fieldLogo:          'Logo URL',
    fieldLogoHint:      '图片的公开 URL（PNG/SVG）',
    fieldLogoPlaceholder: 'https://cdn.example.com/logo.png',
    cancel:             '取消',
    saveChanges:        '保存更改',
    createTenant:       '创建公司',
    title:              '公司',
    tenantsRegisteredSingular: '家已注册公司',
    tenantsRegisteredPlural: '家已注册公司',
    newTenantBtn:       '新公司',
    searchPlaceholder:  '搜索公司或代码...',
    loadingTenants:     '加载公司中...',
    noResults:          '无结果',
    noTenants:          '暂无已注册公司',
    colCompany:         '公司',
    colCode:            '代码',
    colStatus:          '状态',
    colCreated:         '创建于',
    colActions:         '操作',
    statusActive:       '活跃',
    statusInactive:     '未激活',
    login:              '登录',
    edit:               '编辑',
    deactivate:         '停用',
    activate:           '激活',
    loginError:         '登录出错',
    loginErrorBody:     '无法访问该企业。',
    tenantDeactivated:  '公司已停用',
    tenantActivated:    '公司已激活',
    statusChangeError:  '无法更改状态',
    activateNeedsCode:  '该企业没有代码（URL 标识）。请点击“编辑”设置代码后再激活。',
    deactivateTitle:    '停用公司',
    deactivateBodySuffix: '将变为未激活状态：其用户将无法登录，但其所有数据（地块、活动、财务）将被保留。您可以随时重新激活它。',
    // 安全和配置
    accessHoursTitle:   '访问时间窗口',
    accessHoursHint:    'UTC 时间访问限制。超级管理员豁免。',
    accessHoursStart:   '开始时间（HH:MM UTC）',
    accessHoursEnd:     '结束时间（HH:MM UTC）',
    passwordPolicyTitle: '密码策略',
    passwordPolicyHint: '新密码的最低要求。',
    passwordMinLength:  '最小长度',
    passwordMinLengthPlaceholder: '8',
    passwordMaxAgeDays: '过期（天数）',
    passwordMaxAgeDaysPlaceholder: '90（可选）',
    featureFlagsTitle: '启用的功能',
    featureFlagsHint: '控制此公司可以使用哪些模块/功能。',
    demoAccessTitle:    '演示访问',
    demoAccessEnabled:  '已启用 — 显示演示角色选择器',
    demoAccessDisabled: '已禁用',
    otpTitle:           '两步验证（OTP）',
    otpHint:            "登录时（Google 或密码）通过 Telegram 发送的验证码。选择“已绑定则需要”时，没有 Telegram 的人只需密码或 Google 即可登录。",
    otpModeRequired:    "必须 — 所有人都需要 Telegram 才能收到验证码",
    otpModeIfLinked:    "已绑定则需要 — 仅已绑定 Telegram 的人收到验证码",
    otpModeNever:       "从不 — 任何人都不需要验证码（适用于开发）",
    otpEnabled:         '已启用 — 需要通过 Telegram 验证 OTP',
    otpDisabled:        '已禁用 — 无需 OTP',
    enabled:            '已启用',
    disabled:           '已禁用',
    featureLabels: {
      dashboard:    '首页',
      field:        '田地与地块',
      tasks:        '任务',
      chat:         'AI对话',
      finance:      '财务',
      users:        '用户管理',
      settings:     '配置',
      irrigation:   '灌溉与施肥',
      harvest:      '收获与包装',
      inventory:    '库存与仓库',
      machinery:    '机械',
      spray:        '植保（病虫害）',
      merma:        '损耗',
      crop:         '作物与物候',
      payroll:      '工资',
      porcino:      '养猪模块',
      export:       '出口',
      transport:    '运输',
      traceability: '溯源',
      map:          'GIS地图',
      reports:      '报告',
      protocol:     '投入品方案',
      bulkImport:   '批量导入',
    },
    tenantDeleted: '公司已删除',
    tenantDeleteError: '无法删除公司',
    deleteTenantTitle: '删除公司',
    deleteTenantBody: '确定删除 {name} 吗？此操作会将公司从列表中隐藏。数据（用户、地块、作业）不会被删除，必要时可手动恢复。',
    changeBtn: '切换',
    currentBtn: '当前',
    currentTenantHint: '您已在此公司中',
    activateToLogin: '请先启用公司才能登录',
  },

  // ── 农场管理（农场/仓库 CRUD）──────────────────────────────────
  pageTitle: {
    login: '登录', demoLogin: '演示登录', acceptInvite: '完成注册', register: '注册',
    dashboard: '仪表盘', users: '用户', ranchos: '牧场', field: '田地', map: '地图',
    irrigation: '灌溉', inventory: '库存',
  },
  ranchosAdmin: {
    pageTitle:          '农场管理',
    noPermission:       '您没有权限查看此页面。',
    editRancho:         '编辑',
    newRancho:          '新农场',
    fieldName:          '名称 *',
    fieldNamePlaceholder: 'Rancho La Simiente',
    fieldLocation:      '位置',
    fieldLocationPlaceholder: 'Ébano, SLP',
    fieldHectareas:     '总公顷数',
    fieldHectareasPlaceholder: '150.5',
    fieldLatitude:      '纬度',
    fieldLongitude:     '经度',
    useMapBtn:          '在地图上查看',
    pickPointHint:      '点击地图以设置该点',
    confirmPoint:       '确认该点',
    useMyLocation: '使用我的位置',
    locLocating: '正在定位…',
    locDenied: '位置权限被拒绝。',
    locUnavailable: '无法获取您的位置。',
    locTimeout: '获取位置超时。',
    locUnsupported: '您的浏览器不支持地理定位。',
    usesFarmModulesSwitch: '按区域细分',
    usesFarmModulesHelp: '关闭后，该农场仅按地块级别管理。',
    nameRequired:       '名称为必填项',
    createError:        '创建农场出错',
    statusActive: '启用',
    updateError:        '更新农场出错',
    cancel:             '取消',
    saveChanges:        '保存更改',
    createRancho:       '创建农场',
    newWarehouse:       '新仓库',
    fieldWarehouseName: '名称 *',
    fieldWarehouseNamePlaceholder: '主仓库',
    fieldWarehouseLocationPlaceholder: 'Rancho Simiente — 2号仓',
    warehouseCreateError: '创建仓库出错',
    editWarehouse: '编辑仓库',
    saveWarehouse: '保存更改',
    warehouseUpdateError: '更新仓库出错',
    fieldWarehouseRancho: '范围',
    warehouseSharedOption: '共享（全公司）',
    warehouseSharedHint: '共享仓库会显示在所有牧场中；每次领用的成本计入消耗该地块的牧场。',
    colWarehouseScope: '范围',
    createWarehouse:    '创建仓库',
    ranchosTitle:       '农场',
    tabRanchos:         '农场',
    tabWarehouses:      '仓库',
    tabTelegramGroups:  'Telegram 群组',
    lotesManagedInField: '地块及其分区请在“田间”中管理',
    colName:            '名称',
    colLocation:        '位置',
    colHectareas:       '公顷数',
    colActions:         '操作',
    noRanchos:          '暂无已注册农场',
    warehousesTitle:    '仓库',
    colId:              'ID',
    noWarehouses:       '暂无已注册仓库',
    deleteRanchoTitle:  '删除农场',
    deleteRanchoConfirmPrefix: '删除 ',
    deleteRanchoConfirmSuffix: '？关联的地块不会被删除，但将变为无所属农场。',
    deleteWarehouseTitle: '删除仓库',
    deleteWarehouseConfirmPrefix: '删除 ',
    deleteWarehouseConfirmSuffix: '？此仓库中的现有库存不会被删除。',
    delete:             '删除',
  },

  // ── 关联到农场的 Telegram 群组 ──────────────────────────────────────────────
  telegramGroupsAdmin: {
    title:              'Telegram 群组',
    subtitle:           '已关联的群组决定该群组内所有消息的农场/地块，无论用户账户中保存的默认值是什么。',
    newGroup:            '+ 关联群组',
    colLabel:            '名称',
    colChatId:           'Chat ID',
    colRancho:           '农场',
    colLote:             '地块',
    colLinkedBy:         '关联人',
    colLinkedAt:         '日期',
    colStatus:           '状态',
    colActions:          '操作',
    noGroups:            '暂无已关联的群组',
    wholeRancho:         '整个农场',
    statusActive:        '已启用',
    statusInactive:      '已停用',
    deactivate:          '停用',
    activate:            '重新启用',
    colRole: '群组角色',
    roleAny: '不限（按发言人）',
    delete: '删除',
    deleteTitle: '删除群组',
    deleteBody: '要删除“{name}”的绑定吗？Telegram 群组本身不会被删除，只是不再关联牧场，机器人会告别。之后可以重新绑定。',
    deleteError: '无法删除群组',
    ranchoDeleted: '牧场已删除',
    fieldChatId:         'Chat ID *',
    fieldChatIdPlaceholder: '-4123456789',
    addBotHint:          '自动关联：将机器人 {bot} 添加到群组（或作为管理员添加到频道）后，机器人会私下询问要关联到哪个牧场；此表单仅作备用。\n\n关联之前：1）将机器人 {bot} 添加到 Telegram 群组；2）将其设为管理员（否则 Telegram 不会向其传递普通文本）；3）在群里发言的每位成员都必须在“用户”中关联自己的 Telegram。',
    fieldChatIdHint:     '手动备用：也可以在群组内执行 /vincular_grupo 通过机器人进行关联，或临时添加 @RawDataBot 查看其 Chat ID。',
    fieldRancho:         '农场 *',
    fieldRanchoPlaceholder: '选择一个农场',
    fieldLote:           '地块（可选）',
    fieldLotePlaceholder: '整个农场',
    fieldLabel:          '群组名称 *',
    fieldLabelPlaceholder: 'Rancho Simiente — General',
    fieldLanguage:       '通知语言',
    fieldLanguageHint:   '机器人在此群组发布通知所使用的语言。',
    colLanguage:         '语言',
    languageDefault:     '默认',
    chatIdRequired:      'Chat ID 为必填项',
    ranchoRequired:      '请选择一个农场',
    labelRequired:       '名称为必填项',
    createError:         '关联群组出错',
    updateError:         '更新群组出错',
    cancel:              '取消',
    save:                '关联',
    colPurpose:          '用途',
    colMode:             '模式',
    purposeCapture:      '录入',
    purposeSupervision:  '监督',
    modeAll:             '全部',
    modeMention:         '仅提及',
    configure:           '设置',
    purposeDialogTitle:  '群组用途 — {name}',
    purposeDialogBody:   '录入：机器人回复群内成员的消息。监督：群组只接收成员的副本；机器人不在此回复。',
    purposeLabel:        '用途',
    modeLabel:           '机器人回复方式',
    modeHint:            '全部：回复每条消息。仅提及：只在被提及时回复。',
    scopeLabel:          '副本范围',
    scopeRole:           '按角色',
    scopeRancho:         '按牧场',
    roleLabel:           '复制的角色',
    rolePlaceholder:     '选择角色',
    roleRequired:        '请选择一个角色',
    purposeSaveError:    '无法保存群组用途',
    inviteSupervisor:    '邀请监督人',
    inviteSent:          '邀请链接已发送到你的 Telegram 私聊。每位使用者需等待你批准。',
    inviteError:         '无法创建邀请链接',
    watchedToggle:       '被监控',
    watchedTitle:        '被监控人员与监督人 — {name}',
    watchedHint:         '被监控人员是其副本发送到此群组的用户。每人都有自己的话题，自动创建。',
    colTopic:            '话题',
    colLevel:            '级别',
    noWatched:           '此群组没有接收副本的用户。',
    topicOpen:           '开启',
    topicClosed:         '关闭',
    topicNone:           '暂无话题',
    supervisorsTitle:    '监督人',
    noSupervisors:       '暂无监督人名单。',
    verified:            '已验证',
    unverified:          '未验证',
    watchedLoadError:    '无法加载被监控名单',
  },

  // ── 批量导入 ────────────────────────────────────────────────────────────────
  bulkImport: {
    pageTitle:          '批量数据导入',
    sectionTitle:       '从 Excel 导入',
    sectionDesc:        '批量上传农场、地块、用户、产品、目录。',
    dropText:           '将 Excel 文件拖到此处或点击选择',
    selectFile:         '选择文件',
    processing:         '处理中...',
    formatsSupported:   '支持的格式：.xlsx、.xls、.csv',
    analyzing:          '正在分析文件...',
    validationErrorTitle: '验证错误',
    validationErrorsPrefix: '发现 ',
    validationErrorsSuffix: ' 个验证错误。请检查数据后重试。',
    readError:          '无法读取文件。请确保文件是包含 Ranchos、Lotes、Usuarios、Catalogo 工作表的有效 .xlsx/.csv 文件。',
    validationSuccess:  '验证成功 - 无错误',
    importSuccessPrefix: '导入成功完成。已创建 ',
    importSuccessSuffix: ' 条记录。',
    importError:        '导入数据出错。',
    summaryTitle:       '导入摘要',
    summaryRanchos:     '农场',
    summaryLotes:       '地块',
    summaryUsuarios:    '用户',
    summaryProductos:   '产品',
    previewTitle:       '预览（前 5 行）',
    previewShowingPrefix: '显示 ',
    previewShowingSuffix: ' 行中的 5 行',
    cancel:             '取消',
    confirmImport:      '确认并导入',
    importing:          '导入中...',
    templatesTitle:     '可用模板',
    templatesDesc:      '下载模板以正确填写：',
    templateRanchos:    '农场模板',
    templateLotes:      '地块模板',
    templateUsuarios:   '用户模板',
    templateProductos:  '产品模板',
    confirmDialogTitle: '确认导入',
    confirmDialogBody:  '您确定要导入这些数据吗？',
    confirmDialogNotePrefix: '此操作将在系统中创建 ',
    confirmDialogNoteSuffix: ' 条新记录。',
    confirm:            '确认',
    rowLabelPrefix:     '第 ',
    errorsMorePrefix:   '……还有 ',
    errorsMoreSuffix:   ' 个错误',
    filaLabel:          '行',
    filasLabel:         '行',
    downloadComplete:   '下载完整模板',
  },

  // ── 注册 ────────────────────────────────────────────────────────────────
  register: {
    title: '注册',
    titleFor: (tenantName: string) => `注册加入 ${tenantName}`,
    needInviteLink: '您需要农场的邀请链接才能注册。',
    loading: '加载中...',
    invalidCode: '注册码无效或已过期。',
    stepTelegram: '第 1 步（共 2 步）· 验证 Telegram',
    stepAccess: '第 2 步（共 2 步）· 选择登录方式',
    telegramIntro: '打开 Telegram 机器人，点击“开始”，并用按钮分享您的联系人。号码取自 Telegram，无需在此输入。',
    openTelegram: '打开 Telegram',
    waitingTelegram: '正在等待您在 Telegram 中分享联系人…',
    telegramLinkedWaitingPhone: 'Telegram 已绑定。还需要在与机器人的聊天中分享您的联系人。',
    telegramTimeout: '验证等待已超时。',
    retry: '生成新链接',
    phoneVerified: (masked: string) => `Telegram 已验证 ✓ ${masked}`,
    nameLabel: '名字',
    lastNameLabel: '姓氏',
    emailLabel: '邮箱',
    passwordLabel: '密码',
    confirmPasswordLabel: '确认密码',
    methodGoogle: '使用 Google 继续',
    methodOr: '或',
    methodPassword: '使用邮箱和密码',
    submit: '提交申请',
    nameRequired: '请输入您的名字。',
    emailInvalid: '请输入有效的邮箱。',
    passwordTooShort: '密码至少需要 8 个字符。',
    passwordMismatch: '密码不一致。',
    googleFailed: '无法验证您的 Google 账号，请重试。',
    tooManyAttempts: '尝试次数过多，请稍后再试。',
    genericError: '出现错误，请重试。',
    pendingTitle: '申请已提交',
    pendingMessage: '您的申请待处理。管理员审核后，我们会通过 Telegram 通知您。',
    webModeLink: '不使用 Telegram 注册',
    webTitle: '申请访问',
    phoneLabel: '电话（可选）',
    phoneInvalid: '电话无效。请使用国际格式，例如 +52 442 123 4567。',
    webPendingTitle: '申请审核中',
    webPendingMessage: '您的申请正在审核中。公司管理员审核通过后会为您开通访问。',
  },

  // ── 用户 ────────────────────────────────────────────────────────────────
  users: {
    inviteTitle:            '通过链接邀请',
    inviteTelegramLink:     '在 Telegram 中绑定',
    deleteError:            '无法删除该用户',
    roleWarnSuperAdmin:     '⚠️ 超级管理员拥有平台的完全控制权（包括其他公司），并允许自由查询数据库。仅限平台管理员使用。',
    roleWarnAdmin:          '⚠️ 管理员可管理用户、牧场、财务和设置。仅分配给需要管理的人员。',
    roleWarnMany:           '该用户的角色较多。田间工人只需「田间」角色：多余的角色会授予其不需要的功能权限。',
    ranchoFilterInfo:       '显示 {total} 位用户中有权访问 {rancho} 的 {n} 位（包含可访问所有牧场的用户）。',
    allRanchosBadge:        '可访问所有牧场',
    unlinkTelegramButton:   '解除绑定',
    unlinkTelegramTitle:    '解除 Telegram 绑定',
    unlinkTelegramBody:     '要解除 {name} 的 Telegram 绑定吗？在重新绑定之前，对方将无法使用该账户的机器人；之后该 Telegram 可绑定到其他账户。',
    unlinkTelegramConfirm:  '解除绑定',
    unlinkTelegramError:    '无法解除 Telegram 绑定',
    unlockResetButton: '解锁密码',
    resetLockedLabel: '找回已锁定',
    resetWaitingLabel: '找回等待中',
    unlockResetDone: '✓ 已解锁',
    unlockResetError: '无法解锁',
    mergeButton: '合并',
    mergeTitle: '合并用户',
    mergeBody: '此用户（{name}）将保留并吸收另一个用户：补上其缺少的手机号、Telegram、邮箱和密码，并合并对方的角色和访问权限。对方用户将被停用，且无法撤销。',
    mergePick: '要被吸收的用户',
    mergePickPlaceholder: '选择用户',
    mergeConfirm: '合并',
    mergeError: '无法合并',
    companyLinkTitle: "公司链接",
    companyLinkHint: "任何人都可以通过它自行申请访问。管理员在“申请”中批准之前保持待处理。",
    companyLinkGenerate: "生成公司链接",
    companyLinkRegenerate: "重新生成",
    companyLinkRegenerateHint: "重新生成会使之前的链接失效。",
    companyLinkError: "无法生成链接",
    accountButton: '账号',
    accountTitle: '账号与登录方式',
    accountHint: '手机号、邮箱、Telegram 和密码是此人的登录方式。',
    accessNotSet: '未设置',
    accessAdd: '添加',
    accessChange: '修改',
    accessRemove: '移除',
    accessSave: '保存',
    accessPhone: '手机号',
    accessEmail: '邮箱',
    accessTelegram: 'Telegram',
    accessPassword: '密码',
    accessTelegramLinked: '已绑定',
    accessTelegramLink: '通过链接绑定',
    accessPasswordSet: '已设置密码',
    accessPasswordNone: '未设置密码',
    accessPasswordPlaceholder: '新密码（至少8位）',
    accessPasswordNeedsEmail: '请先添加邮箱才能使用密码。',
    accessError: '无法保存更改',
    cannotDisableSelf:      '您不能停用自己的账户',
    cannotRemoveOwnAdminRole: '您不能移除自己的管理员角色',
    selfRolesSessionWarning: '保存角色更改后，您的会话将被关闭。请重新登录以查看更改。',
    confirmAndSave: '确认并保存',
    cannotDeleteSelf:       '您不能删除自己的账户',
    inviteTelegramHint:     '请在要使用的 Telegram 账号中打开，并分享联系人。如果该手机号已有账户，将直接绑定 Telegram。',
    addTitle:               '添加用户',
    editTitle:              '编辑用户',
    copyLabel:              '监督副本',
    copyNone:               '无副本',
    copyLevel1:             '级别 1：全部',
    copyLevel2:             '级别 2：筛选',
    copyLevel3:             '级别 3：仅提醒',
    mainGroupLabel:         '主要副本群组',
    mainGroupAuto:          '自动（按角色或牧场）',
    copyNeedsUserId:        '此用户没有副本所需的标识，无法在此更改。',
    deleteTitle:            '删除用户',
    phoneLabel:             '电话（WhatsApp / Telegram）*',
    phoneOnly:              '电话',
    languageLabel:          '语言',
    inviteButton:           '通过链接邀请',
    addButton:              '+ 添加用户',
    pendingRegistrationsTab:'申请',
    pendingEmpty: '没有待处理的申请',
    pendingLoading: '正在加载申请...',
    pendingMethodGoogle: 'Google',
    pendingMethodPassword: '邮箱和密码',
    pendingRanchosLabel: '允许的牧场',
    pendingRanchosPlaceholder: '所有牧场',
    pendingLotesLabel: '允许的地块',
    pendingLotesPlaceholder: '选择地块',
    pendingNeedLote: '田间工人至少需要一个地块；没有地块将无法访问任何地块。',
    grantExpensesApprove: '可以批准一般费用',
    pendingApproveError: '无法完成操作，请重试。',
    pendingAlreadyResolved: '该申请已被其他人处理。',
    pendingRejectTitle: '拒绝申请',
    pendingRejectBody: '拒绝 {name} 的申请？将通过 Telegram 通知对方。',
    activityTab: '活跃度',
    activityColName: '姓名',
    activityColPhone: '电话',
    activityColTelegram: 'Telegram',
    activityColLastSeen: '最后活动',
    activityNever: '从未',
    activityToday: '今天',
    activityDaysAgo: '{n} 天前',
    activityEmpty: '暂无活动数据',
    approve:                '批准',
    reject:                 '拒绝',
    confirm:                '确认',
    pageTitle: '用户',
    systemUsers: '系统用户',
    loadingUsers: '正在加载用户...',
    telegramLinked: '✓ 已绑定 Telegram',
    enable: '启用',
    disable: '停用',
    rolesLabel: '角色 *',
    emailOptional: '邮箱（选填）',
    emailPlaceholder: 'user@example.com',
    namePlaceholder: '例如：张伟',
    nameTooLong: '姓名不能超过 {max} 个字符',
    phoneRequired: '请填写手机号',
    rolesRequired: '请至少选择一个角色',
    createError: '创建用户失败',
    saveError: '保存更改失败',
    inviteError: '无法生成邀请',
    inviteShareMessage: '邀请你加入 Agrovid — 请使用此链接创建账号：',
    inviteEmailSubject: 'Agrovid 邀请',
    inviteShareHint: '分享此链接 — 7 天后过期，且只能使用一次：',
    copied: '✓ 已复制',
    copyLink: '复制链接',
    shareTelegram: '通过 Telegram 发送',
    shareWhatsapp: '通过 WhatsApp 发送',
    shareEmail: '通过邮件发送',
    shareEmailHint: '请先在上方填写邮箱以预填收件人',
    generateLink: '生成链接',
    deleteActiveWarning: '{name} 当前为启用状态。请先停用该用户才能删除 — 以免误删仍在工作的人员。',
    deleteConfirmBody: '确定删除 {name} 吗？此操作无法撤销。',
    ranchosPlaceholder: '选择牧场',
    accessAllRanchos: '✓ 可访问所有牧场（点击以限制）',
    accessRestrictRanchos: '仅限所选牧场（点击以授予全部权限）',
    defaultRanchoLabel: '默认活动牧场',
    defaultRanchoNone: '没有可用的牧场',
    defaultRanchoUnset: '未设置 — 使用“允许的牧场”中的第一个',
    defaultRanchoHint: '当用户未通过 /rancho 明确选择牧场时，Telegram 机器人将使用该牧场。',
    accessAllLotes: '✓ 可访问所有地块（点击以限制）',
    accessRestrictLotes: '仅限所选地块（点击以授予全部权限）',
    saveAccess: '保存权限',
  },

  // ── 词汇表 ───────────────────────────────────────────────────────────────
  vocabulary: {
    title:                  '企业词汇表',
    titleWithCompany:       '{company} 词汇表',
    description:            '机器人学习的术语和短语，用于纠正语音转录。',
    pendingTitle:           '等待审核的建议',
    activeTerms:            '活跃术语',
    aliasDetected:          '检测到别名',
    termSuggested:          '建议术语',
    origin:                 '来源',
    actions:                '操作',
    approve:                '批准',
    reject:                 '拒绝',
    addTerm:                '添加术语',
    term:                   '术语',
    aliases:                '别名',
    category:               '分类',
    scope:                  '范围',
    noTerms:                '未注册任何术语',
    delete:                 '删除',
    loading:                '加载中……',
    cancel:                 '取消',
    save:                   '保存',
    user:                   '用户',
    tenant:                 '企业',
    autoLearned:            '自动学习',
    manual:                 '手动',
    dialogTitle:            '添加词汇表术语',
    canonicalTerm:          '标准术语',
    placeCanonical:         '例如 阿巴美丁',
    aliasesComma:           '别名（逗号分隔）',
    placeAliases:           '例如 阿巴美丁、阿维美丁',
    placeCategory:          '例如 化学物品',
  },

  // ── 设备（F5） ──────────────────────────────────────────────────────────────
  drones: {
    title:       '无人机飞行',
    zoneFilter:  '地块或区域',
    subtitle:    '无人机喷施：每次飞行的面积、用量与回放',
    flights:     '飞行次数',
    hectares:    '处理公顷数',
    liters:      '施用升数',
    flightHours: '飞行小时',
    replay:      '飞行回放',
    play:        '播放',
    pause:       '暂停',
    restart:     '重新开始',
    speed:       '速度',
    elapsed:     '时间',
    sprayed:     '已处理',
    battery:     '电量',
    colDate:     '日期',
    colLote:     '地块',
    colHa:       '公顷',
    colLiters:   '升',
    colDuration: '时长',
    colBattery:  '电量',
    colAircraft: '机型',
    empty:       '暂无飞行记录。',
    simulated:   '按该次飞行的面积、用量与行距绘制的示意轨迹。',
    flightOf:    '飞行日期',
  },
  droneImport: {
    button:         '导入飞行记录（Excel）',
    title:          '导入无人机飞行记录',
    intro:          '上传一个每行一次飞行的 Excel 文件。系统会先校验并显示预览，确认前不会保存任何数据。',
    template:       '下载模板',
    chooseFile:     '选择 .xlsx 文件',
    validating:     '校验中…',
    summary:        '行有效',
    errorsTitle:    '请修正以下错误后重新上传文件',
    colRow:         '行',
    colField:       '字段',
    colMessage:     '问题',
    colDate:        '日期',
    colType:        '类型',
    colLote:        '地块',
    colValves:      '阀门',
    colHa:          '公顷',
    colLiters:      '升',
    confirm:        '导入',
    importing:      '导入中…',
    close:          '关闭',
    readError:      '无法读取文件。请使用模板，并确保包含 fecha 和 lote 列。',
    importError:    '导入失败。请检查文件后重试。',
    done:           '已创建飞行记录',
    skipped:        '已存在',
    typeAPPLICATION: '喷洒作业',
    typeVIDEO:       '视频',
    typeSCOUTING:    '巡查',
  },
  devices: {
    title:            '设备',
    subtitle:          '向平台上报数据的传感器、追踪器与无人机',
    empty:             '尚未注册设备。添加第一台以开始接收遥测数据。',
    add:               '新增设备',
    name:              '名称',
    type:              '类型',
    capabilities:      '功能',
    binding:           '绑定对象',
    unbound:           '未绑定',
    bindingIdPlaceholder: '货运/区域/地块 ID',
    bindingTargetPlaceholder: '选择关联目标',
    status:            '状态',
    lastSeen:          '最后上报',
    never:             '从未',
    apiKeyPrefix:      '密钥',
    rotateKey:         '轮换密钥',
    rotateConfirm:     '轮换后当前密钥将立即失效，设备需使用新密钥重新配置。是否继续？',
    keyShownOnce:      '请立即复制此密钥——它不会再次显示。若丢失，只能重新轮换。',
    keyCopied:         '密钥已复制',
    copy:              '复制',
    statusACTIVE:      '启用',
    statusINACTIVE:    '停用',
    statusREVOKED:     '已吊销',
    typeGPS_TRACKER:   'GPS 追踪器',
    typeTHERMOGRAPH:   '温度记录仪',
    typeSOIL_SENSOR:   '土壤传感器',
    typeWEATHER_STATION: '气象站',
    typeDRONE:         '无人机',
    typeCARRIER_API:   '承运商接口',
    typeRAIN_GAUGE:    '雨量计',
    save:              '保存',
    cancel:            '取消',
  },

  // ── 演示登录（演示角色选择器）─────────────────────────────────────────────────
  demoLogin: {
    unavailable: '此环境不提供演示。',
    goToLogin: '前往登录',
    roleLabels: {
      ADMIN:             '管理员',
      AGRONOMIST:        '农艺师',
      SUPERVISOR:        '田间主管',
      WAREHOUSE_MANAGER: '仓库管理员',
      FIELD_WORKER:      '田间工人',
      SUPER_ADMIN:       '超级管理员',
      AUDITOR:           '审计员',
      HR:           '人力资源',
      GUARD:           '保安',
      INVESTOR:          '投资人',
    },
    roleDesc: {
      ADMIN:             '完整的管理权限：用户、财务和设置',
      AGRONOMIST:        '种植方案、物候期和病虫害监测',
      SUPERVISOR:        '田间操作和任务管理',
      WAREHOUSE_MANAGER: '库存、存量和先进先出（FEFO）管理',
      FIELD_WORKER:      '基础权限，可登记操作和活动',
      SUPER_ADMIN:       '对整个平台的完全控制权限',
      AUDITOR:           '对整个系统的只读访问权限',
      HR:           '员工、人脸录入与考勤',
      GUARD:           '门岗：核对点名通行码并登记车辆',
      INVESTOR:          '投资人门户：生产、投资和物流的只读视图',
    },
    enterAs: '以此身份进入',
    subtitle: '选择您想用来体验演示的角色',
    connectError: '无法连接到演示服务器。',
  },

  // ── C 阶段 — 文档（跨域仓库） ──────────────────────────────────────────────
  documents: {
    title:    '文档',
    subtitle: '按来源分组的企业文档仓库。',
    subtitleWithCompany: '按来源分组的 {company} 文档仓库。',
    loading:  '正在加载文档...',
    noDocuments: '暂无文档。',
    folderSHIPMENT:       '装运',
    folderINVOICE:        '发票',
    folderMODULE:         '区域',
    folderPURCHASE_ORDER: '采购订单',
    folderOTHER:          '其他',
    docCountSuffix: '份文档',
    colType: '类型',
    colDate: '日期',
    colGeneratedBy: '生成者',
    download: '下载',
    back: '返回',
    kpiTotal: '文档总数',
    kpiTopFolder: '文档最多的文件夹',
    kpiLast30d: '最近30天上传',
    kpiDocTypes: '文档类型数',
    recentTitle: '最近文档',
  },

  // ── C 阶段 — 风险（异常 + 损耗聚合） ────────────────────────────────────────
  riesgos: {
    title:    '风险',
    subtitle: '未结风险的综合视图：生产异常和近期损耗事件。',
    anomaliesTitle: '未结异常',
    loadingAnomalies: '正在加载异常...',
    noAnomalies: '没有未结异常。',
    mermaTitle: '影响最大的近期损耗',
    loadingMerma: '正在加载损耗记录...',
    noMerma: '没有近期损耗记录。',
    complianceTitle: '合规（安全间隔期/再入期/最大残留限量）',
    complianceNote: '系统会实时拦截不合规操作（安全间隔期/再入期/最大残留限量），但目前还没有可查询的拦截历史记录——待该记录建成后将启用此部分。',
    responsible: '负责人',
    impact: '影响',
    kpiOpen: '未解决风险',
    kpiCritical: '严重',
    kpiHigh: '高',
    kpiMedium: '中',
    kpiLow: '低',
    kpiWithResponsible: '已指定负责人',
    kpiMermaImpact: '损耗影响（总计）',
    moduleRiskTitle: '区域风险地图',
    noActiveProject: '请选择一个活跃项目以查看区域风险地图。',
    noRisk: '无风险',
    categoryTitle: '按类别划分的风险',
    tableTitle: '风险与预警记录',
    colDate: '日期',
    colCategory: '类别',
    colDescription: '描述',
    colLevel: '级别',
    colResponsible: '负责人',
    colStatus: '状态',
    statusABIERTA: '未解决',
    statusEN_PROCESO: '处理中',
    statusRESUELTA: '已解决',
    statusDESCARTADA: '已忽略',
  },

  // ── 通用费用 — 审批队列（B2） ────────────────────────────────────────────────
  // ── Gastos generales — dashboard (B3) ───────────────────────────────────────
  calculators: {
    title: '计算器',
    subtitle: '用你的假设计算成本、产量和现金流。',
    calculate: '计算',
    result: '结果',
    fromSystem: 'del sistema',
    loadError: '无法加载目录。',
    runError: '无法计算。',
  },
  expensesDashboard: {
    title: '一般费用',
    subtitle: '以基准货币汇总已确认的费用。',
    from: '从',
    to: '到',
    rancho: '牧场',
    lote: '地块',
    all: '全部',
    kpiTotal: '期间总额',
    kpiCount: '已确认费用',
    kpiPending: '待审批',
    byCategory: '按类别',
    byCounterparty: '按供应商',
    byMonth: '按月份',
    empty: '该期间无数据。',
    loading: '加载中...',
    error: '无法加载汇总。',
    excluded: '已排除 {n} 笔使用其他基准货币的费用。',
    noCounterparty: '无供应商',
    colCategory: '类别',
    colCounterparty: '供应商',
    colMonth: '月份',
    colTotal: '总额',
    viewPending: '查看待审批',
  },

  expenseApprovals: {
    title: '待审批费用',
    subtitle: '未经直接审批登记的通用费用。请审批或驳回。',
    loading: '加载中...',
    empty: '没有待审批的费用。',
    colDate: '日期',
    colDescription: '说明',
    colCounterparty: '供应商',
    colAmount: '金额',
    colScope: '归属',
    colRegisteredBy: '登记人',
    scopeTenant: '公司',
    scopeRancho: '牧场',
    scopeLote: '地块',
    approve: '审批',
    reject: '驳回',
    rejectTitle: '驳回费用',
    rejectBody: '该费用将被驳回，不计入报表。原因可选填。',
    reasonLabel: '原因（可选）',
    cancel: '取消',
    conflict: '该费用已被他人处理，列表已刷新。',
    actionError: '操作未能完成，请重试。',
  },

  // ── D 阶段 — 成本（投资人门户） ──────────────────────────────────────────────
  costos: {
    title: '成本',
    subtitle: '成本控制、现金流与应收账款。',
    loading: '加载中...',
    noData: '暂无数据。',
    kpiPlanned: '预算总额',
    kpiExecuted: '已发生',
    kpiExecPct: '执行率',
    kpiRevenue: '预计收入',
    kpiMargin: '毛利润',
    kpiMarginPct: '毛利率',
    kpiGeneralExpenses: '一般费用',
    indirectsTitle: '按地块分摊的间接费用',
    colLote: '地块',
    colIndirect: '间接费用',
    colIndirectPerHa: '每公顷间接费用',
    excludedCurrencyNote: '部分一般费用使用其他基础币种，未计入这些合计：',
    compositionTitle: '成本构成（已发生）',
    compositionCenterLabel: '总成本',
    cashflowTitle: '预计现金流',
    budgetTitle: '预算与实际对比（按类别）',
    colCategory: '类别',
    colPlanned: '预算',
    colExecuted: '已花费',
    colVariance: '差异',
    colPct: '执行率',
    receivablesTitle: '应收账款',
    agingCurrent: '未到期',
    agingBucketPrefix: '逾期',
    agingTotal: '应收总额',
  },
  // Reglas PHI/REI/MRL de la empresa
  complianceRules: {
    title: '合规规则',
    subtitle: '按农药、作物和市场设置 PHI（采收前天数）、REI（重新进入前小时数）和 MRL 限量。没有规则时系统会阻止任何施药或采收。',
    newRule: '新建规则',
    importExcel: '导入 Excel',
    filterProduct: '产品',
    filterCrop: '作物',
    filterMarket: '市场',
    allProducts: '所有产品',
    allMarkets: '所有市场',
    colProduct: '产品',
    colCrop: '作物',
    colMarket: '市场',
    colPhi: 'PHI（天）',
    colRei: 'REI（小时）',
    colMrl: 'MRL',
    colScope: '来源',
    colActions: '操作',
    scopeTenant: '本公司',
    scopeGlobal: '通用（只读）',
    edit: '编辑',
    delete: '删除',
    deleteTitle: '删除规则',
    deleteConfirm: '删除 {product} 在 {crop}（{market}）上的规则？没有规则时，系统将阻止使用该产品的施药和采收。',
    cancel: '取消',
    save: '保存',
    saving: '保存中...',
    loading: '正在加载规则...',
    emptyTitle: '暂无合规规则',
    emptyHint: '请为每种农药、作物和市场添加规则，或导入 Excel 模板。没有规则时，出于安全考虑系统会阻止施药或采收。',
    emptyNoProducts: '请先在库存中添加农药（农药类别）。',
    noMatches: '没有符合筛选条件的规则。',
    dialogNewTitle: '新建合规规则',
    dialogEditTitle: '编辑合规规则',
    fieldProduct: '农药',
    fieldCrop: '作物',
    fieldCropHint: '从目录中选择或输入新作物。',
    fieldMarket: '市场',
    fieldPhi: 'PHI（采收前天数）',
    fieldRei: 'REI（重新进入前小时数）',
    fieldMrl: 'MRL（最大残留限量）',
    fieldMrlUnit: '单位',
    fieldNotes: '备注',
    requiredFields: '请填写产品、作物、市场、PHI、REI 和 MRL。',
    errorGeneric: '无法保存规则。',
    importTitle: '从 Excel 导入规则',
    importHelp: '下载模板，每条规则填写一行（Producto 可填写农药名称或 ID）后上传。确认前请检查预览。',
    downloadTemplate: '下载模板',
    chooseFile: '.xlsx 文件',
    preview: '预览',
    previewing: '检查中...',
    previewSummary: '共 {total} 行：{valid} 行有效（{toCreate} 条新增，{toUpdate} 条更新），{errors} 行有错误。',
    colRow: '行',
    colStatus: '状态',
    statusOk: '正常',
    actionCreate: '新增',
    actionUpdate: '更新',
    fixErrors: '请修正有错误的行并重新检查文件后再确认。',
    confirmImport: '确认导入',
    importing: '导入中...',
    imported: '导入完成：新增 {created} 条，更新 {updated} 条。',
    failedRows: '以下行未保存：{rows}。',
    close: '关闭',
  },
};
